import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Save, Loader2, Leaf, Utensils, Flame, TrendingUp, AlertTriangle, Lightbulb, SlidersHorizontal, Check, Clock, BookOpen, IndianRupee, Info, MapPin, Armchair, Footprints, X, Plus, Trash2, Lock, Calendar as CalendarIcon, Maximize2, Minimize2,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TimePicker } from '../../components/ui/time-picker';
import { confirm } from '../../components/ui/confirm-dialog';
import { useT } from '../../context/LanguageContext';
import apiClient from '../../services/api';
import { locationsService } from '../../services/locationsService';
import { floorPlanService } from '../../services/floorPlanService';
import BuffetFloorPlanner from './BuffetFloorPlanner';
import { getCalendar } from '../../services/insightsService';
import {
  CalendarPanel, CalendarHead, CalendarToolbar, CalendarMonths, CalendarEvents,
  CalendarEmpty, monthsBetween, isoOf,
} from '../../components/calendar/PlannerCalendar';

/** BuffetStudio: a step-by-step builder (name, menu, quantity, pricing, reports) storing new fields in Buffet.config. */

/* Serving times are quarter-hour slots built from the branch's hours, so an impossible time is simply absent. */
const SLOT_MINUTES = 15;

/* The most sittings one buffet can be split into — the same cap the server
   holds (apps/buffet/sittings.py), so a gap that would print four hundred
   rows onto every booking page is refused here rather than at save. */
const MAX_SITTINGS = 96;

/* Offered gaps and lengths. Minutes are what a kitchen thinks in for the gap
   between sittings, hours for how long guests get the table. */
const GAP_CHOICES = [15, 20, 30, 45, 60, 90, 120];
const LENGTH_CHOICES = [45, 60, 75, 90, 120, 150, 180, 240];

/* Money is typed into TEXT boxes: a native number field grows spinners and changes under a scroll wheel. */
const cleanMoney = (v) => {
  const s = String(v || '').replace(/[^\d.]/g, '');
  const i = s.indexOf('.');
  return i === -1 ? s : s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
};

/* One small hand-drawn line chart used for every curve here, with values printed rather than left to a slope. */
function MiniLine({ points, yLabel = '' }) {
  if (!points.length) return null;
  const W = 460, H = 150, PADL = 34, PADB = 24, PADT = 10, PADR = 14;
  const top = Math.max(...points.map((p) => p.y), 1);
  const x = (i) => PADL + (i * (W - PADL - PADR)) / Math.max(1, points.length - 1);
  const y = (v) => PADT + (1 - v / top) * (H - PADT - PADB);
  const path = points.map((p, i) => `${i ? 'L' : 'M'}${x(i)},${y(p.y)}`).join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Trend over the day">
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={PADL} x2={W - PADR} y1={y(top * f)} y2={y(top * f)}
            stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" />
          <text x={PADL - 6} y={y(top * f) + 3} textAnchor="end"
            className="fill-muted-foreground" style={{ fontSize: 10 }}>
            {Math.round(top * f)}
          </text>
        </g>
      ))}
      <path d={path} fill="none" stroke="#4f46e5" strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(p.y)} r="3.5" fill="#4f46e5" stroke="#fff" strokeWidth="1.5">
            <title>{`${p.x} — ${p.y}${yLabel ? ` ${yLabel}` : ''}${p.sub ? ` (${p.sub})` : ''}`}</title>
          </circle>
          <text x={x(i)} y={H - 6} textAnchor="middle"
            className="fill-muted-foreground" style={{ fontSize: 9.5 }}>{p.x}</text>
        </g>
      ))}
      <text x={x(points.length - 1)} y={y(points[points.length - 1].y) - 8}
        textAnchor="middle" className="fill-foreground"
        style={{ fontSize: 11, fontWeight: 700 }}>
        {points[points.length - 1].y}
      </text>
    </svg>
  );
}

const toMin = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return Number.isNaN(h) ? null : h * 60 + (m || 0);
};
const toHHMM = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
// "19:30" → "7:30 pm"
const to12h = (hhmm) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  const ap = h < 12 ? 'am' : 'pm';
  return `${h % 12 || 12}:${String(m || 0).padStart(2, '0')} ${ap}`;
};

/* Inclusive day count across a date range — "2026-01-01".."2026-01-03" is 3.
   Parsed as local dates rather than through Date(string), which reads a bare
   YYYY-MM-DD as UTC and can land a day out either side of the line. */
const parseDay = (s) => {
  const [y, m, d] = String(s || '').split('-').map(Number);
  return (y && m && d) ? new Date(y, m - 1, d) : null;
};
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const dayCount = (from, to) => {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
};

/* Python's weekday(): Monday 0 … Sunday 6. The server expands the repeat on
   these numbers, so the chips, the calendar and the backend all count the week
   from the same end — JS `getDay()` starts on Sunday and would put every tick
   one day out, which is the kind of bug that only shows up on a Sunday. */
const PY_DOW = (d) => (d.getDay() + 6) % 7;
const ALL_DOW = [0, 1, 2, 3, 4, 5, 6];
const WEEKDAY_DOW = [0, 1, 2, 3, 4];
const WEEKEND_DOW = [5, 6];
/* The name a generated pricing RULE carries. Deliberately not translated:
   a rule label is stored data, read back on the rules screen and printed
   against every cover sold under it, so it must not change meaning when
   somebody switches the interface to French. */
const DOW_LABEL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday',
  'Friday', 'Saturday', 'Sunday'];

/* EVERY date the buffet actually opens on: the span, narrowed to the ticked
   weekdays, minus the days struck out by hand.

   One rule, read three ways — the count under the fields, the days the
   calendar highlights, and what is posted to the server. Deriving each of
   those separately is how a planner ends up promising 116 days and opening
   80: the manager trusts the number on screen, and only the server's copy of
   the rule is real. */
const runDates = (from, to, dow, except) => {
  const a = parseDay(from);
  const b = parseDay(to);
  if (!a || !b || b < a) return [];
  const ticked = new Set(dow && dow.length ? dow : ALL_DOW);
  const struck = new Set(except || []);
  const out = [];
  for (const d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    const iso = isoOf(d);
    if (ticked.has(PY_DOW(d)) && !struck.has(iso)) out.push(iso);
  }
  return out;
};

// Range presets, in the units a season is actually decided in.
const RANGE_PRESETS = [
  { key: 'week', days: 7 },
  { key: 'fortnight', days: 14 },
  { key: 'month', months: 1 },
  { key: 'quarter', months: 3 },
  { key: 'halfYear', months: 6 },
];

/* The last date of a preset, counted INCLUSIVELY from the first: "one week"
   starting Monday ends the following Sunday, not the Monday after. */
const presetEnd = (fromIso, preset) => {
  const a = parseDay(fromIso);
  if (!a) return '';
  const d = new Date(a);
  if (preset.months) d.setMonth(d.getMonth() + preset.months);
  else d.setDate(d.getDate() + preset.days);
  d.setDate(d.getDate() - 1);
  return isoOf(d);
};

/* The widest window the branch is ever open across the week, since a Sunday brunch is set up on a Tuesday. */
function servingWindow(operatingHours) {
  if (!Array.isArray(operatingHours) || !operatingHours.length) return null;
  let open = null; let close = null; let varies = false;
  operatingHours.forEach((d) => {
    if (!d || d.closed || !d.open || !d.close) return;
    const o = toMin(d.open); const c = toMin(d.close);
    if (o == null || c == null || c <= o) return;
    if (open == null) { open = o; close = c; return; }
    if (o !== open || c !== close) varies = true;
    open = Math.min(open, o);
    close = Math.max(close, c);
  });
  if (open == null) return null;
  return { open: toHHMM(open), close: toHHMM(close), varies };
}

// Mirrors Buffet.BUFFET_TYPES: the type rides on the booking page and public site, so it is picked, not guessed.
const BUFFET_TYPES = [
  ['BREAKFAST', 'Breakfast'], ['BRUNCH', 'Brunch'], ['LUNCH', 'Lunch'],
  ['HIGH_TEA', 'High tea'], ['DINNER', 'Dinner'], ['ALL_DAY', 'All day'],
];

/* Retired: the kids-half-price policy that silently halved every child price; the kids price is now typed. */
const DEFAULT_POLICIES = {};
const DEFAULT_PLAN = { portion_g: 120, portion_pcs: 1, uptake_pct: 80 };
// A child eats about half an adult portion - the factor the prep math uses for items ticked for kids.
const KID_FACTOR = 0.5;

/* Counted things are whole things, rounded to the nearest piece but never rounding a real portion to nothing. */
const wholePcs = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return 0;
  return Math.max(1, Math.round(v));
};

/* Which things are counted rather than weighed; the guess only sets the default, and every row can switch. */
const PIECE_WORDS = /\bbottled?\b|\bcanned\b|\bnaan\b|\broti\b|\bparatha\b|\bkulcha\b|\bpuri\b|\bpoori\b|\bbhatura\b|\bchapati\b|\bpapad\b|\bidli\b|\bvada\b|\bsamosa\b|\bmomos?\b|\bkulfi\b|\bdosa\b/i;

/* Every ingredient a dish cooks with, deduped in first-seen order — the
   dish's OWN ticked ingredients first (the dish form's "Select ingredients"),
   then anything more its variant recipes name. What the "+ New counter"
   panel offers once its dish is picked. */
const dishRecipeIngredientIds = (dish) => {
  const seen = new Set();
  const out = [];
  const add = (raw) => {
    const id = Number(raw);
    if (id && !seen.has(id)) { seen.add(id); out.push(id); }
  };
  (dish?.ingredients || []).forEach((i) => add(i?.id));
  (dish?.variants || []).forEach((v) => (v?.recipe || []).forEach((r) => add(r?.ingredient_id)));
  return out;
};
const guessUnit = (name) => (PIECE_WORDS.test(String(name)) ? 'pcs' : 'g');

/* A number that is also recomputed from what was typed, so edits buffer locally and commit on blur or Enter. */
function QtyInput({ value, step, onCommit, className }) {
  const [draft, setDraft] = useState(null);
  return (
    <input
      type="number" min="0" step={step}
      value={draft ?? value}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { if (draft != null && draft !== '') onCommit(draft); setDraft(null); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className={className}
    />
  );
}

/* A typed figure with its unit beside it: a plain text box, digits only, capped as typed and committing every keystroke. */
function NumberBox({ label, value, min = 0, max, suffix, onCommit }) {
  const [draft, setDraft] = useState(null);
  const shown = draft ?? (Number(value) > 0 ? String(value) : '');
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="flex shrink-0 items-center gap-1.5">
        <Input
          type="text" inputMode="numeric" autoComplete="off"
          value={shown}
          placeholder={String(min)}
          onChange={(e) => {
            const digits = e.target.value.replace(/\D+/g, '');
            if (digits === '') { setDraft(''); onCommit(min); return; }
            const n = Math.min(max, Math.max(min, parseInt(digits, 10)));
            setDraft(String(n));
            onCommit(n);
          }}
          onBlur={() => setDraft(null)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          className="w-28 text-right tabular-nums"
          aria-label={typeof label === 'string' ? label : undefined}
        />
        <span className="w-5 text-sm text-muted-foreground">{suffix || ''}</span>
      </span>
    </div>
  );
}

const kg = (n) => `${(Math.round(n * 10) / 10).toFixed(1)} kg`;
// Mauritian rupees with western digit grouping - never the Indian rupee sign, since this suite prices in MUR.
const rupees = (n) => `Rs ${Math.round(n).toLocaleString('en-MU')}`;
// TimeFields come back as 'HH:MM' but a raw serializer can send seconds, which the time input rejects.
const hhmm = (v) => (v ? String(v).slice(0, 5) : '');
const prettyTime = (v) => {
  if (!v) return '';
  const [h, m] = hhmm(v).split(':').map(Number);
  if (Number.isNaN(h)) return v;
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m ? `${h12}:${String(m).padStart(2, '0')}${suffix}` : `${h12}${suffix}`;
};

// Chart palette — validated (dataviz): blue+green pass CVD/contrast light & dark.
const C_BLUE = '#2a78d6';
const C_GREEN = '#008300';
const C_GRID = '#e1e0d9';
const C_MUTED = '#898781';

/* ── small building blocks ─────────────────────────────────────────────── */

/* The step on screen is a Card whose emoji badge matches its step in the stepper strip above; n is the fallback when no emoji is given. */
const Section = ({ n, emoji, title, hint, aside, children }) => (
  <Card className="shadow-sm">
    <CardHeader className="flex-row items-start justify-between space-y-0 gap-3">
      <div>
        <CardTitle className="flex items-center gap-2.5 font-sans normal-case tracking-normal text-base">
          <span className={`h-6 w-6 rounded-md flex items-center justify-center shrink-0 ${
            emoji ? 'bg-primary/10 text-sm' : 'bg-primary text-primary-foreground text-xs font-bold'
          }`}>
            {emoji ? <span aria-hidden="true">{emoji}</span> : n}
          </span>
          {title}
        </CardTitle>
        {/* 24px badge + 10px gap — the hint lines up under the title text. */}
        {hint && <p className="mt-1.5 ml-[34px] text-sm text-muted-foreground">{hint}</p>}
      </div>
      {aside}
    </CardHeader>
    <CardContent className="p-5">{children}</CardContent>
  </Card>
);

/* Steps stay clickable when editing a saved buffet, but a new one is walked in order. */
const Stepper = ({ steps, index, done, onPick }) => (
  <div className="flex flex-wrap items-center gap-1 rounded-xl border border-border bg-card p-1.5">
    {steps.map((s, i) => {
      const on = i === index;
      const ok = done(s.key) && !on;
      return (
        <React.Fragment key={s.key}>
          {i > 0 && <span className="hidden sm:block h-px w-4 bg-border shrink-0" aria-hidden="true" />}
          <button
            type="button" onClick={() => onPick(i)} aria-current={on ? 'step' : undefined}
            aria-label={s.title}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition ${
              on ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {/* ONE notation for every step: emoji plus title, with only the badge colour changing and no trailing tick. */}
            <span className={`h-5 w-5 rounded-md flex items-center justify-center text-[13px] leading-none shrink-0 ${
              on ? 'bg-primary-foreground/25'
                : ok ? 'bg-emerald-500/25' : 'bg-muted grayscale opacity-70'
            }`}>
              <span aria-hidden="true">{s.emoji}</span>
            </span>
            <span className="hidden sm:inline">{s.title}</span>
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

const Chip = ({ on, onClick, icon: Icon, children }) => (
  <button type="button" onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-medium transition ${
      on ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:border-foreground/30'
    }`}>
    {Icon && <Icon size={14} className={on ? 'text-primary' : 'text-muted-foreground'} />}
    {children}
  </button>
);

/* One labelled line with its control on the right; MODULE LEVEL, or every keystroke remounts it and drops focus. */
const Row = ({ label, hint, divide, children }) => (
  <div className={`flex flex-wrap items-center justify-between gap-3 py-3 ${
    divide ? 'border-b border-border' : ''
  }`}>
    <div className="min-w-0">
      <div className="text-sm font-medium text-foreground">{label}</div>
      {hint && <div className="mt-0.5 max-w-md text-xs text-muted-foreground">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

/* Stat tiles sit on their own muted plate, so a row of four reads as a group. */
const Stat = ({ label, value, sub, subTone }) => (
  <div className="rounded-lg bg-muted/50 p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold text-foreground mt-0.5 tabular-nums">{value}</div>
    {sub && (
      <div className={`text-xs mt-0.5 ${
        subTone === 'bad' ? 'text-red-600' : subTone === 'good' ? 'text-blue-600' : 'text-muted-foreground'
      }`}>{sub}</div>
    )}
  </div>
);

// The same field styling as the rest of the buffet module, so inputs theme with the app.
const inputCls =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring';

/* ── main component ────────────────────────────────────────────────────── */

export default function BuffetStudio({ restaurantId, locationId, buffet, onBack, onSaved, onOpenRules }) {
  const { t } = useT();
  const cfg = (buffet?.config && typeof buffet.config === 'object') ? buffet.config : {};

  const [name, setName] = useState(buffet?.name || '');
  // Not edited here, but kept in state so saving from the Studio preserves the existing description.
  const [description] = useState(buffet?.description || '');

  /* ---- step 1: serving time ---- */
  const [startTime, setStartTime] = useState(hhmm(buffet?.start_time));
  const [endTime, setEndTime] = useState(hhmm(buffet?.end_time));
  /* WHEN a buffet runs is no longer held here: a template describes what it IS, and dates belong to scheduling. */

  /* ---- step 1: sittings ---------------------------------------------- */
  /* One window can be sold as many overlapping SITTINGS — 10:00–12:00, then
     10:15–12:15, and so on to closing. Three settings GENERATE that list (the
     same arithmetic the server runs in apps/buffet/sittings.py, so the table
     below is what will actually be bookable), and `sitPrices` holds only what
     somebody TYPED against a start time. Keyed by "HH:MM" rather than held as
     a list, so the price set for 10:15 stays with 10:15 when the window later
     opens an hour earlier — a list would shift every price up four rows. */
  /* The dates this buffet should be OPENED on. Deliberately not stored on the
     template: a buffet template says what the buffet IS, and the days it runs
     are Open Buffets' answer — holding the same fact in two places is what
     previously let them disagree and quietly take a buffet off every screen.
     This range is an INSTRUCTION, carried out at save by creating the
     openings, and then it is the openings that are the truth. */
  const [runFrom, setRunFrom] = useState('');
  const [runTo, setRunTo] = useState('');
  /* WHICH days inside that span. All seven until somebody says otherwise, so
     a manager who only wants "these dates" never meets this control at all. */
  const [runDow, setRunDow] = useState(ALL_DOW);
  // Individual dates struck out of the run — Christmas, a closure, a wedding.
  const [runExcept, setRunExcept] = useState([]);

  /* The buffet's id ONCE IT EXISTS — the prop for an existing buffet, and
     whatever the save came back with for a new one. Held here because saving a
     new buffet deliberately does not remount this screen: `buffet` stays
     undefined, so anything keyed off `buffet?.id` would go on calling a saved
     buffet unsaved. The run calendar is the visible casualty — it kept drawing
     every day as "planned" after the days had actually been opened. */
  const [savedId, setSavedId] = useState(buffet?.id || null);

  /* The days this buffet is ALREADY open on, read back from Open Buffets.
     The template stores no dates, so opening a saved buffet used to show an
     empty calendar however heavily it was scheduled — the range above is an
     instruction for opening MORE days, and could never double as a reading of
     the days already opened. These stay separate for that reason: they are a
     report, and nothing here writes them. `openedTick` re-reads after a save
     has created openings, so the calendar catches up without a reload. */
  const [openedDays, setOpenedDays] = useState([]);
  const [openedTick, setOpenedTick] = useState(0);
  useEffect(() => {
    if (!restaurantId || !savedId) { setOpenedDays([]); return undefined; }
    let alive = true;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/openings/`,
      { params: { buffet_id: savedId } })
      .then(({ data }) => {
        if (alive) setOpenedDays((data?.success && data.data?.days) || []);
      })
      /* A calendar that cannot say what is already open is the state this
         screen was in before; failing quietly puts it back there rather than
         blocking an edit that has nothing to do with dates. */
      .catch(() => { if (alive) setOpenedDays([]); });
    return () => { alive = false; };
  }, [restaurantId, savedId, openedTick]);

  /* Fill the date fields from the days this buffet is ALREADY open on.

     The calendar could show the run while the two inputs sat empty, which read
     as "no dates set" on a buffet running every day for months. The whole rule
     is reconstructed, not just the ends: the weekdays that actually appear
     among the open days become the ticked chips, and a day inside the span
     that matches those weekdays but is NOT open becomes a struck-out day. Save
     therefore re-sends the same rule it read, and the server's already-open
     skip makes that a no-op instead of widening a weekends-only buffet into a
     daily one.

     Clamped to today, because the server refuses to open a day that has been
     (a past `date` is a 400, not a skip). A run that started in the past
     prefills from today; its history stays visible on the calendar, which is
     the reading of what happened rather than an instruction to change it.

     Once only, and never over anything typed: the ref is what stops a later
     re-read of the openings from pulling a half-typed range back. */
  const datesPrefilled = useRef(false);
  useEffect(() => {
    if (datesPrefilled.current || !savedId || !openedDays.length) return;
    datesPrefilled.current = true;
    const today = todayISO();
    const iso = openedDays.map((o) => o?.date).filter(Boolean).sort();
    const ahead = iso.filter((d) => d >= today);
    // Everything already behind us: nothing left to re-open, so the fields
    // stay empty and mean what they say — "which days to open next".
    if (!ahead.length) return;
    const from = ahead[0];
    const to = ahead[ahead.length - 1];
    const open = new Set(ahead);
    const dow = [...new Set(ahead.map((d) => PY_DOW(parseDay(d))))].sort((a, b) => a - b);
    const except = [];
    for (const d = parseDay(from); d <= parseDay(to); d.setDate(d.getDate() + 1)) {
      const key = isoOf(d);
      if (dow.includes(PY_DOW(d)) && !open.has(key)) except.push(key);
    }
    setRunFrom(from);
    setRunTo(to);
    setRunDow(dow.length ? dow : ALL_DOW);
    setRunExcept(except);
  }, [savedId, openedDays]);

  /* The whole stretch this studio is deciding about: the range typed on step 1
     AND the days the buffet is already open on. The calendar has always drawn
     exactly this span; the holiday list and advance pricing now read the same
     one, because a saved template keeps no range of its own — reading only
     `runFrom` left both of them blank the moment a saved buffet was reopened,
     however many days it was actually running. */
  const openedIsos = useMemo(
    () => (openedDays || []).map((o) => o?.date).filter(Boolean).sort(),
    [openedDays],
  );
  const spanFrom = useMemo(
    () => [runFrom, openedIsos[0]].filter(Boolean).sort()[0] || '',
    [runFrom, openedIsos],
  );
  const spanTo = useMemo(
    () => [runTo, openedIsos[openedIsos.length - 1]].filter(Boolean).sort().pop() || '',
    [runTo, openedIsos],
  );

  const savedSit = (cfg.sittings && typeof cfg.sittings === 'object') ? cfg.sittings : {};
  /* Always on: the One service / Sittings choice is gone, so every buffet
     is cut into slots. Kept as a constant rather than ripped through every
     reader, and still written to config so the server contract is unchanged. */
  const sitOn = true;
  const [gapMin, setGapMin] = useState(Number(savedSit.gap_min) || 30);
  const [sitMin, setSitMin] = useState(Number(savedSit.duration_min) || 120);
  const [sitPrices, setSitPrices] = useState(
    () => (savedSit.prices && typeof savedSit.prices === 'object'
      ? { ...savedSit.prices } : {}),
  );

  /* Every start time this window offers. A sitting has to FINISH inside the
     window, so the last one starts a full length before the buffet closes —
     nobody is sold a seat at a counter that is already being cleared. */
  const sittingStarts = useMemo(() => {
    const open = toMin(startTime);
    const close = toMin(endTime);
    if (open == null || close == null || close <= open) return [];
    const last = close - sitMin;
    const out = [];
    for (let m = open; m <= last && out.length < MAX_SITTINGS; m += gapMin) {
      out.push(toHHMM(m));
    }
    return out;
  }, [startTime, endTime, gapMin, sitMin]);

  /* What was typed for one sitting, as a string the input can hold ('' = nothing set). */
  const sitVal = (start, field) => {
    const v = (sitPrices[start] || {})[field];
    return v === null || v === undefined ? '' : String(v);
  };
  const setSitVal = (start, field, value) => setSitPrices((p) => ({
    ...p,
    [start]: { ...(p[start] || {}), [field]: value === '' ? null : value },
  }));

  /* ---- step 5's per-dish history ---- */
  const [dishHistOpen, setDishHistOpen] = useState(null);   // dish name or null
  const [dishHist, setDishHist] = useState({});             // name → rows
  const [dishChart, setDishChart] = useState(null);         // {item,date,buffetId}
  const [dishChartSeries, setDishChartSeries] = useState(null);

  const openDishHistory = (dishName) => {
    const next = dishHistOpen === dishName ? null : dishName;
    setDishHistOpen(next);
    setDishChart(null);
    if (!next || dishHist[next]) return;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/dish-history/`,
      { params: { item: next } })
      .then(({ data }) => setDishHist((p) => ({
        ...p, [next]: data?.success ? (data.data.rows || []) : [],
      })))
      .catch(() => setDishHist((p) => ({ ...p, [next]: [] })));
  };

  useEffect(() => {
    if (!dishChart) { setDishChartSeries(null); return undefined; }
    let alive = true;
    setDishChartSeries(null);
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/dish-history/`,
      { params: { item: dishChart.item, date: dishChart.date, buffet_id: dishChart.buffetId } })
      .then(({ data }) => { if (alive) setDishChartSeries(data?.success ? (data.data.series || []) : []); })
      .catch(() => { if (alive) setDishChartSeries([]); });
    return () => { alive = false; };
  }, [restaurantId, dishChart]);
  /* The branch's opening hours, which bound both time fields; null until loaded or when none are configured. */
  const [hours, setHours] = useState(null);
  const [buffetType, setBuffetType] = useState(buffet?.buffet_type || 'LUNCH');
  const [spread, setSpread] = useState(cfg.spread_type || 'MIXED');
  /* Whether drinks are part of the per-head deal; old saves carry no flag, and no flag means no. */
  /* Live counters. A station is no longer just a name: it carries the
     ingredients it cooks through — several per counter — each with a quantity
     and a cost typed on the quantity step exactly as a dish is, plus the
     amount the counter is catered at. Old buffets stored bare name strings in
     live_stations; those load as counters with no ingredients, so nothing
     already saved changes meaning, and the names go on being mirrored into
     live_stations at save for anything still reading the old key. */
  const [counters, setCounters] = useState(() => {
    if (Array.isArray(cfg.live_counters) && cfg.live_counters.length) {
      return cfg.live_counters.map((c) => ({
        master_id: c?.master_id ?? null,
        name: String(c?.name || ''),
        total_amount: c?.total_amount ?? '',
        /* The station's OWN cost per kg/pc — it prices like a dish; absent on
           buffets saved while ingredient lines still did the pricing. */
        cost_per_unit: c?.cost_per_unit ?? '',
        /* The counter's OWN plan row — a station with a recipe preps like a
           dish, so it carries the same fields a dish plan does. Absent on
           buffets saved before recipes existed; the defaults below are the
           dish sheet's own. */
        unit: c?.unit === 'pcs' ? 'pcs' : 'g',
        portion_g: c?.portion_g ?? DEFAULT_PLAN.portion_g,
        portion_pcs: c?.portion_pcs ?? DEFAULT_PLAN.portion_pcs,
        kid_portion_g: c?.kid_portion_g ?? null,
        kid_portion_pcs: c?.kid_portion_pcs ?? null,
        uptake_pct: c?.uptake_pct ?? DEFAULT_PLAN.uptake_pct,
        ingredients: (Array.isArray(c?.ingredients) ? c.ingredients : []).map((r) => ({
          ingredient_id: r?.ingredient_id ?? null,
          name: String(r?.name || ''),
          qty: r?.qty ?? '',
          unit: ['kg', 'pcs'].includes(r?.unit) ? r.unit : 'kg',
          cost_per_unit: r?.cost_per_unit ?? '',
          /* The master's ratio, snapshotted at pick time: this much of the
             ingredient per 100 of the counter's output. > 0 = the row's
             quantity DERIVES from the counter's prep figure. */
          qty_per_100: Number(r?.qty_per_100) > 0 ? Number(r.qty_per_100) : null,
        })),
      }));
    }
    return (cfg.live_stations || [])
      .map((n) => ({
        name: String(n), total_amount: '', ingredients: [],
        unit: 'g', portion_g: DEFAULT_PLAN.portion_g, portion_pcs: DEFAULT_PLAN.portion_pcs,
        kid_portion_g: null, kid_portion_pcs: null, uptake_pct: DEFAULT_PLAN.uptake_pct,
      }));
  });
  /* The ingredient master, for the counter pickers. null = still loading (or
     failed) — existing rows carry their own snapshotted names, so the editor
     keeps rendering either way and only the "add" select goes quiet. */
  const [masterIngredients, setMasterIngredients] = useState(null);
  useEffect(() => {
    if (!restaurantId) return undefined;
    let alive = true;
    apiClient.get('/users/dish/masters/ingredient/', { params: { restaurant_id: restaurantId } })
      .then(({ data }) => { if (alive) setMasterIngredients(data?.success ? (data.data || []) : []); })
      .catch(() => { if (alive) setMasterIngredients([]); });
    return () => { alive = false; };
  }, [restaurantId]);
  /* Dish NAME → type bucket (dish / beverage / addon / combo / store_bought /
     bakery / liquor). The floor plan's dish list groups and colour-codes by
     it, with the same palette the menu catalogue's type badges use. Names,
     not ids, because the buffet spread is a name list all the way down. */
  const [dishTypes, setDishTypes] = useState({});
  useEffect(() => {
    if (!restaurantId) return undefined;
    let alive = true;
    apiClient.get(`/users/restaurant/${restaurantId}/getAllDishes/`)
      .then(({ data }) => {
        if (!alive) return;
        const map = {};
        (data?.data || []).forEach((d) => {
          /* kind wins for add-ons/combos; dish_type 'food' is the plain-dish
             default. Both combo shapes in the data land in one bucket. */
          const bucket = d.kind === 'addon' ? 'addon'
            : (d.kind === 'combo' || d.dish_type === 'combo') ? 'combo'
              : (d.dish_type && d.dish_type !== 'food') ? d.dish_type : 'dish';
          map[(d.name || '').trim().toLowerCase()] = bucket;
        });
        setDishTypes(map);
      })
      .catch(() => { if (alive) setDishTypes({}); });
    return () => { alive = false; };
  }, [restaurantId]);
  /* The Live Counter MASTER — counters defined once under Settings → Master
     Data, each already carrying its ingredients. The studio only ever picks
     from this list; what a counter is made of is not re-described per buffet. */
  const [masterCounters, setMasterCounters] = useState(null);
  const loadMasterCounters = () => apiClient
    .get('/users/live-counters/', { params: { restaurant_id: restaurantId } })
    .then(({ data }) => setMasterCounters(data?.success ? (data.data || []) : []))
    .catch(() => setMasterCounters([]));
  useEffect(() => {
    if (restaurantId) loadMasterCounters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);
  /* The inline "+" master-create dialog: null closed, else its draft. */
  const [counterDraft, setCounterDraft] = useState(null);
  /* The full dish list (variants + recipes) behind the draft's dish picker —
     a counter cooks ONE dish, and its ingredients are ticked from THAT
     dish's recipe. Fetched only when the "+" panel first opens. */
  const [dishList, setDishList] = useState(null);
  const loadDishList = () => apiClient
    .get(`/users/restaurant/${restaurantId}/dishes/`, { params: { page_size: 1000 } })
    .then(({ data }) => {
      const p = data?.success ? data.data : [];
      setDishList(Array.isArray(p) ? p : (p?.items || []));
    })
    .catch(() => setDishList([]));
  /* And the "+" inside ITS ingredient picker: an ingredient missing from the
     register is created on the spot (the same register Settings → Ingredients
     manages) and comes back already ticked into the draft. */
  const [ingDraft, setIngDraft] = useState(null);
  const saveIngDraft = async () => {
    if (!ingDraft || !ingDraft.name.trim()) return;
    setIngDraft((d) => d && { ...d, saving: true, error: '' });
    try {
      const { data } = await apiClient.post('/users/dish/masters/ingredient/', {
        restaurant_id: restaurantId, name: ingDraft.name.trim(),
      });
      if (!data?.success) throw new Error(data?.error || 'Could not create the ingredient');
      setMasterIngredients((v) => [...(v || []), data.data]);
      setCounterDraft((d) => d && { ...d, ingredientIds: [...d.ingredientIds, data.data.id] });
      setIngDraft(null);
    } catch (e) {
      setIngDraft((d) => d && {
        ...d, saving: false, error: e?.response?.data?.error || e.message,
      });
    }
  };

  const setCounter = (i, patch) => setCounters((v) => v.map(
    (c, k) => (k === i ? { ...c, ...patch } : c)));
  const removeCounter = (i) => setCounters((v) => v.filter((_, k) => k !== i));
  /* The ingredient's own stock unit seeds the row's unit, so "Paneer (kg)"
     starts in kilos without anyone re-picking it; a wrong guess is one click. */
  const counterUnitFor = (ing) => {
    // The live-counter endpoint says `unit`, the dish-master DTO `base_unit` — read either.
    const u = String(ing?.unit || ing?.base_unit || '').toLowerCase();
    if (u.startsWith('pc') || u.startsWith('unit') || u.startsWith('pi')) return 'pcs';
    return 'kg';
  };
  const counterSelected = (m) => counters.some((c) => (c.master_id
    ? String(c.master_id) === String(m.id)
    : c.name.trim().toLowerCase() === m.name.trim().toLowerCase()));
  /* Picking a master counter COPIES its ingredients onto the buffet — the same
     contract as picking a menu — so a later master edit never silently
     rewrites a saved buffet. Picking it off drops the copy, typed quantities
     included: the chip is a toggle, and half-kept copies are how two screens
     end up disagreeing about a spread. */
  const toggleMasterCounter = (m) => {
    setCounters((v) => {
      const idx = v.findIndex((c) => (c.master_id
        ? String(c.master_id) === String(m.id)
        : c.name.trim().toLowerCase() === m.name.trim().toLowerCase()));
      if (idx !== -1) return v.filter((_, k) => k !== idx);
      return [...v, {
        master_id: m.id,
        name: m.name,
        total_amount: '',
        cost_per_unit: '',
        /* The counter's own dish-like plan row starts from the sheet's defaults. */
        unit: 'g',
        portion_g: DEFAULT_PLAN.portion_g,
        portion_pcs: DEFAULT_PLAN.portion_pcs,
        kid_portion_g: null,
        kid_portion_pcs: null,
        uptake_pct: DEFAULT_PLAN.uptake_pct,
        ingredients: (m.ingredients || []).map((ing) => ({
          ingredient_id: ing.id,
          name: ing.name,
          qty: '',
          /* A ratio'd ingredient keeps the unit the recipe was written in. */
          unit: ['kg', 'pcs'].includes(ing.recipe_unit) ? ing.recipe_unit : counterUnitFor(ing),
          cost_per_unit: '',
          /* The master's ratio, copied at pick time like everything else, so
             a later register edit never rewrites a saved buffet. */
          qty_per_100: Number(ing.qty_per_100) > 0 ? Number(ing.qty_per_100) : null,
        })),
      }];
    });
  };
  /* Create a master counter without leaving the studio — the same one-tap the
     dish form gives its masters. It lands in the register (Settings → Master
     Data → Live Counters) and is selected onto this buffet in the same press. */
  const saveCounterDraft = async () => {
    if (!counterDraft || !counterDraft.name.trim()) return;
    /* Per-100 amounts are MANDATORY: a ticked ingredient without one cannot
       derive its quantity on any buffet, so the save says so instead. */
    const missing = counterDraft.ingredientIds
      .filter((id) => !(Number(counterDraft.recipe?.[id]?.qty) > 0));
    if (missing.length) {
      setCounterDraft((d) => d && {
        ...d,
        error: 'Give every ticked ingredient its per-100 amount — or untick what the counter doesn\'t use.',
      });
      return;
    }
    setCounterDraft((d) => d && { ...d, saving: true, error: '' });
    try {
      const { data } = await apiClient.post('/users/live-counters/', {
        restaurant_id: restaurantId,
        name: counterDraft.name.trim(),
        description: counterDraft.description || '',
        dish_id: counterDraft.dishId || null,
        ingredient_ids: counterDraft.ingredientIds,
        recipe: counterDraft.recipe || {},
      });
      if (!data?.success) throw new Error(data?.error || 'Could not create the counter');
      setMasterCounters((v) => [...(v || []), data.data]);
      toggleMasterCounter(data.data);
      setCounterDraft(null);
    } catch (e) {
      setCounterDraft((d) => d && {
        ...d, saving: false, error: e?.response?.data?.error || e.message,
      });
    }
  };
  const setCounterIng = (i, j, patch) => setCounters((v) => v.map((c, k) => (k === i
    ? { ...c, ingredients: c.ingredients.map((r, m) => (m === j ? { ...r, ...patch } : r)) }
    : c)));
  /* WHERE the buffet is served - several floor sections at once - and HOW it is eaten, seated or standing. */
  const [areaIds, setAreaIds] = useState((cfg.area_section_ids || []).map(Number));
  const [serviceStyle, setServiceStyle] = useState(cfg.service_style || 'SITTING');
  /* Where this buffet stands tables and dishes per area, saved on the BUFFET rather than the branch's plan. */
  const [floorLayout, setFloorLayout] = useState(() => {
    /* Older saves carry stations without counter numbers. They are numbered
       here once, in saved order — the same fallback rule the server prints
       them by — so the next save makes the numbers real and permanent. */
    const raw = cfg.floor_layout && typeof cfg.floor_layout === 'object' ? cfg.floor_layout : {};
    const used = new Set();
    Object.values(raw).forEach((l) => ((l && l.stations) || []).forEach((s) => {
      const n = Number(s && s.num);
      if (Number.isInteger(n) && n > 0) used.add(n);
    }));
    let next = 1;
    return Object.fromEntries(Object.entries(raw).map(([k, l]) => [k, {
      ...l,
      stations: ((l && l.stations) || []).map((s) => {
        const n = Number(s && s.num);
        if (Number.isInteger(n) && n > 0) return s;
        while (used.has(next)) next += 1;
        used.add(next);
        return { ...s, num: next };
      }),
    }]));
  });
  // The branch's floors → sections → tables, for the picker and the counts.
  const [floorPlan, setFloorPlan] = useState(null);
  // No editor for these any more, but merged with the saved config so a re-save keeps old policy values.
  const [policies] = useState({ ...DEFAULT_POLICIES, ...(cfg.policies || {}) });

  /* ---- step 4: the spread ---- */
  // The Buffet-type menu this serves from; picking one COPIES its spread, so later menu edits can't rewrite it.
  const [menuId, setMenuId] = useState(buffet?.menu_id ?? '');
  const [restMenus, setRestMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  // Course sections — the first BuffetMenu's sections; other menus preserved.
  const [sections, setSections] = useState(
    (buffet?.menus?.[0]?.sections || []).map((s) => ({
      name: s.name || '',
      items: (s.items || []).map((it) => (typeof it === 'string' ? it : it?.name || '')),
    })),
  );
  const [rotationNote, setRotationNote] = useState(
    cfg.rotation_note ?? "Mains rotate daily from a 15-dish pool so weekday regulars don't see repeats.",
  );

  /* ---- step 5: quantity ---- */
  /* The two headcounts are typed separately, the way a booking sheet reads, with kids counted by their portion. */
  const [kidsCovers, setKidsCovers] = useState(cfg.kids_covers ?? 0);
  const [adults, setAdults] = useState(
    Math.max(0, (buffet?.expected_covers ?? 120) - (cfg.kids_covers ?? 0)),
  );
  // Their total is still what saves as expected_covers, so nothing downstream had to learn about the split.
  const covers = (Number(adults) || 0) + (Number(kidsCovers) || 0);
  const [buffer, setBuffer] = useState(cfg.safety_buffer_pct ?? 10);
  // "Uptake" is trade vocabulary, so the i beside it opens the explanation rather than shipping a paragraph.
  const [showUptakeHelp, setShowUptakeHelp] = useState(false);
  const [plan, setPlan] = useState(cfg.dish_plan || {});
  /* What `plan` held before anybody typed. Its own ref because the register
     below writes to it after the form is already on screen, and that write
     has to reach the baseline too — see the dirty check further down. */
  const basePlan = useRef(cfg.dish_plan || {});
  /* The dish REGISTER (Buffet → Dish Quantities) is the source of portions:
     laid over this buffet's own snapshot, so a register edit reaches the
     buffet the next time it is saved — while every day already opened keeps
     the quantities it was opened with. */
  useEffect(() => {
    if (!restaurantId) return;
    apiClient.get('/users/buffet-dish-plans/', { params: { restaurant_id: restaurantId } })
      .then(({ data }) => {
        const reg = (data?.success && data.data) || {};
        if (Object.keys(reg).length) {
          setPlan((p) => ({ ...p, ...reg }));
          /* The register is a LOAD, not an edit. Folded into the baseline by
             exactly the same merge, so a form nobody has touched still reads
             as untouched — and a portion the manager DID retype in the
             meantime survives as a difference rather than being absorbed. */
          basePlan.current = { ...basePlan.current, ...reg };
        }
      })
      .catch(() => { /* no register yet — the snapshot and defaults stand */ });
  }, [restaurantId]);

  /* ---- step 6: pricing ---- */
  /* FIXED charges two typed prices and ignores rules; DYNAMIC lets the engine move a base price. Kept in config. */
  const [pricingMode, setPricingMode] = useState(
    String(cfg.pricing_mode || 'DYNAMIC').toUpperCase() === 'FIXED' ? 'FIXED' : 'DYNAMIC',
  );
  const [kidPrice, setKidPrice] = useState(buffet?.base_child_price ?? '');
  const [weekday, setWeekday] = useState(buffet?.base_adult_price ?? '');
  const [weekend, setWeekend] = useState(cfg.weekend_adult_price ?? '');
  /* Taxes that may ride on the ticket - the same list every bill charges from, with ids kept in config. */
  const [taxLabels, setTaxLabels] = useState([]);
  const [taxIds, setTaxIds] = useState(
    () => (Array.isArray(cfg.tax_label_ids) ? cfg.tax_label_ids : []),
  );
  /* Does the typed price already contain the tax? Either way the restaurant's takings are the NET. */
  const [taxInclusive, setTaxInclusive] = useState(!!cfg.tax_inclusive);
  useEffect(() => {
    if (!restaurantId) return;
    apiClient.get(`/users/restaurant/${restaurantId}/taxes/applicable/`)
      .then(({ data }) => setTaxLabels((data?.success && data.results) || []))
      .catch(() => setTaxLabels([]));
  }, [restaurantId]);

  const chosenTaxes = useMemo(
    () => taxLabels.filter((t) => taxIds.includes(t.id)),
    [taxLabels, taxIds],
  );
  /* Tax ON a net price, using the same arithmetic as the backend and every billing screen. */
  const addTax = (net) => {
    let total = Number(net) || 0;
    const parts = [];
    chosenTaxes.forEach((t) => {
      const amt = t.rate_type === 'FLAT'
        ? Number(t.rate)
        : ((t.is_compound ? total : (Number(net) || 0)) * Number(t.rate)) / 100;
      const r = Math.round(amt * 100) / 100;
      parts.push({ id: t.id, name: t.name, amount: r });
      total += r;
    });
    return { total: Math.round(total * 100) / 100, parts };
  };
  /* What a typed price means both ways round; addTax is affine, so two probes invert it exactly for any mix. */
  const taxOn = (typed) => {
    const p = Number(typed) || 0;
    if (!p || !chosenTaxes.length) return { net: p, gross: p, tax: 0, parts: [] };
    if (!taxInclusive) {
      const { total, parts } = addTax(p);
      return { net: p, gross: total, tax: Math.round((total - p) * 100) / 100, parts };
    }
    const b = addTax(0).total;
    const a = (addTax(1000).total - b) / 1000;
    const net = Math.max(0, a > 0 ? (p - b) / a : p - b);
    const { parts } = addTax(net);
    const tax = Math.round(parts.reduce((s, x) => s + x.amount, 0) * 100) / 100;
    /* The guest pays exactly what was typed, with the net absorbing the rounding and floored at zero. */
    return {
      net: Math.max(0, Math.round((p - tax) * 100) / 100), gross: p, tax, parts,
    };
  };
  const netOf = (typed) => taxOn(typed).net;

  /* ---- what each sitting actually charges ----------------------------
     A sitting with nothing typed against it sells at the buffet's own
     per-head price. That used to show as a grey placeholder, which reads as
     an empty row, and an empty row reads as free. The figure now sits IN the
     box instead, so every line of the table states the fixed price that
     sitting will charge — and re-reads it the moment the price above
     changes. Emptying a box hands the sitting back to the buffet price
     rather than pricing it at nothing, which is why `sitPrices` still keeps
     only what somebody typed. */
  const sitBase = (field) => (field === 'adult' ? weekday : kidPrice);
  const sitShown = (start, field) => {
    const typed = sitVal(start, field);
    if (typed !== '') return typed;
    const base = sitBase(field);
    return base === '' || base == null ? '' : String(base);
  };
  // Priced by hand, rather than following the buffet — only the badge cares.
  const sitIsOwn = (start) => sitVal(start, 'adult') !== '' || sitVal(start, 'child') !== '';
  /* Everything the service costs besides food, as one figure for the whole service rather than per head. */
  /* Kids ride at half the adult price until somebody types their own figure, which breaks the link. */
  const [kidsLinked, setKidsLinked] = useState(
    () => !buffet?.base_child_price
      || Number(buffet.base_child_price) === Math.round(Number(buffet.base_adult_price || 0) / 2),
  );
  // The food-cost margin being aimed for, which drives the suggested per-head price from the quantities.
  const [targetMargin, setTargetMargin] = useState(cfg.target_margin_pct ?? 65);

  const [stepIdx, setStepIdx] = useState(0);
  /* The wizard has TWO top-level phases. Setup is the buffet itself, walked
     through its four sub-steps; Advance settings is the optional depth on top
     of it, and stays shut until the buffet has actually been saved — there is
     nothing to configure advanced booking against until the thing exists. */
  const [phase, setPhase] = useState('setup');
  /* Which of the three advance areas is open. The phase has sub-steps exactly
     as setup does — one on screen at a time, picked from a stepper — rather
     than three cards stacked down the page, where the two that are still
     empty pushed the one that works out of sight. */
  const [advIdx, setAdvIdx] = useState(0);

  /* ---- phase 2: advance pricing uplifts -------------------------------
     What step 1 priced is the ordinary day; these are the days that are not.
     They are held as a plain map here and WRITTEN OUT AS PRICING RULES at
     save (a SEASONAL rule pinned to the holiday's date, a TIME rule on the
     weekday), because the rules engine is what the till, the booking page and
     every quote already read — a second private list of uplifts would look
     right on this screen and charge nothing at the counter.

     Keyed by the festival KEY rather than the date, so a buffet re-opened
     next year shows the tick against Divali wherever Divali has moved to.
     Weekends are keyed by weekday number (5 = Saturday, 6 = Sunday), the same
     convention BuffetPriceRule.days is stored in. */
  const advRules = useMemo(
    () => (buffet?.price_rules || []).filter((r) => r?.config?.advance_uplift),
    [buffet],
  );
  /* Two shapes an uplift can take: a % change on the normal price, or a new
     fixed price in its place. A rule saved by the older third choice — a flat
     Rs added on top — is read back as the price it came to, since that is
     the only way to show it in a control that no longer offers "add". */
  const upliftOf = (r) => {
    if (r.price_mode === 'SET') {
      return { mode: 'SET', value: r.adult_price == null ? '' : String(r.adult_price) };
    }
    if (r.price_mode === 'AMOUNT') {
      const b = Number(weekday);
      const v = Number(r.adjust_value);
      return b > 0 && Number.isFinite(v)
        ? { mode: 'SET', value: String(Math.round((b + v) * 100) / 100) }
        : { mode: 'PERCENT', value: '' };
    }
    return { mode: 'PERCENT', value: r.adjust_value == null ? '' : String(r.adjust_value) };
  };
  /* The kids price a NEW adult price implies, keeping step 1's adult:kids
     proportion — so "Rs 300 on Divali" does not quietly leave children at
     the ordinary rate. Null where either price is unset: the engine then
     keeps whatever the base child price is. */
  const childAt = (adult) => {
    const b = Number(weekday);
    const k = Number(kidPrice);
    return b > 0 && k > 0 && Number(adult) > 0
      ? Math.round((Number(adult) * k / b) * 100) / 100 : null;
  };
  const [holidayUplifts, setHolidayUplifts] = useState(() => Object.fromEntries(
    advRules
      .filter((r) => r.config.advance_uplift === 'HOLIDAY' && r.config.holiday_key)
      .map((r) => [r.config.holiday_key, upliftOf(r)]),
  ));
  const [weekendUplifts, setWeekendUplifts] = useState(() => Object.fromEntries(
    advRules
      .filter((r) => r.config.advance_uplift === 'WEEKEND' && (r.days || []).length)
      .map((r) => [String(r.days[0]), upliftOf(r)]),
  ));

  /* Full-screen floor plan. The planner is CONTROLLED (layout + onChange),
     so the expanded copy is the same editor rather than a second one to
     keep in sync — anything dragged or placed in it writes straight back
     to floorLayout and the inline plan shows it on close. */
  const [planExpanded, setPlanExpanded] = useState(false);
  /* The expanded editor works on its OWN copy. Save commits it, Close
     throws it away — so a plan can be tried out and abandoned, which a
     write-through editor never allows. */
  const [planDraft, setPlanDraft] = useState(null);
  /* Public holidays and festivals, so the run calendar can warn that a
     chosen day is one. Read from the shared calendar module — the same
     rows Settings → Calendar manages — rather than a second list that
     would drift from it. */
  const [holidays, setHolidays] = useState([]);
  /* Whether the calendar could not be READ, which is a different thing
     from a range with no holidays in it. Without this the two look
     identical — no markers — and a broken fetch reads as a quiet month. */
  const [holidaysFailed, setHolidaysFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /* ---- derived: dish plan table ---- */
  /* Dishes switched off FOR THIS BUFFET, held as names in its own config so the menu itself is untouched. */
  const [offDishes, setOffDishes] = useState(
    () => new Set(Array.isArray(cfg.off_dishes) ? cfg.off_dishes : []),
  );
  /* ── has anything actually been changed? ───────────────────────────────
     Every field that reaches the save payload, in one string. Anything left
     out of this list is a change that would be thrown away without asking,
     so it follows the payload rather than judgement about what matters.

     `plan` is compared against its own baseline (above); the rest cannot
     change except through a handler, so a snapshot of the first render is
     the honest "before". Key order shifting inside an object would read as a
     change that is not one — which only costs an extra confirm, never a
     silent discard. */
  const editPrint = JSON.stringify([
    name, buffetType, spread, startTime, endTime, runFrom, runTo, runDow, runExcept,
    gapMin, sitMin, sitPrices, serviceStyle, areaIds, floorLayout, planDraft,
    menuId, sections, [...offDishes], rotationNote, counters,
    adults, kidsCovers, buffer, targetMargin,
    pricingMode, weekday, weekend, kidPrice, kidsLinked, taxIds, taxInclusive,
    holidayUplifts, weekendUplifts,
  ]);
  const baseEdit = useRef(editPrint);
  const changed = editPrint !== baseEdit.current
    || JSON.stringify(plan) !== JSON.stringify(basePlan.current);

  const isOff = (d) => offDishes.has(d);
  const toggleDish = (d) => setOffDishes((prev) => {
    const next = new Set(prev);
    if (next.has(d)) next.delete(d); else next.add(d);
    return next;
  });

  /* Only what is actually going on the counter: everything downstream reads this, so a switched-off dish costs nothing. */
  const allDishes = useMemo(
    () => [...new Set(sections.flatMap((s) => s.items.filter(Boolean)))]
      .filter((d) => !offDishes.has(d)),
    [sections, offDishes],
  );
  const planFor = (dish) => ({ ...DEFAULT_PLAN, ...(plan[dish] || {}) });
  const setPlanField = (dish, f, v) =>
    setPlan((p) => ({ ...p, [dish]: { ...planFor(dish), [f]: v } }));
  // Weighed or counted: the saved choice wins, and a new dish starts from the name-based guess.
  const unitFor = (dish) => planFor(dish).unit || guessUnit(dish);
  /* The adult portion as the plan MEANS it, read whole for counted items so no plan cooks for half a piece. */
  const adultPortionFor = (dish) => {
    const p = planFor(dish);
    return unitFor(dish) === 'pcs'
      ? wholePcs(p.portion_pcs ?? 1)
      : (Number(p.portion_g) || 0);
  };
  // Guests actually eating this dish, buffer included - the multiplier both quantity directions share.
  /* What a child is served of this dish: half the adult portion unless edited, and stored only once it has been. */
  const kidPortionFor = (dish) => {
    const p = planFor(dish);
    // Old plans carry kids_ok:false for dishes children weren't served; read that as a kid portion of 0.
    if (p.kids_ok === false) return 0;
    if (unitFor(dish) === 'pcs') {
      const set = p.kid_portion_pcs;
      // Whole pieces either way, including on plans saved when halves were allowed, so an old buffet reads as plates.
      return set != null && set !== ''
        ? wholePcs(set)
        : wholePcs(adultPortionFor(dish) * KID_FACTOR);
    }
    const set = p.kid_portion_g;
    return set != null && set !== ''
      ? Number(set)
      : Math.round((Number(p.portion_g) || 0) * KID_FACTOR);
  };

  /* Adult-equivalent eaters, so suggestedQty stays one multiply and the quantity column can be divided back out. */
  const eatersFor = (dish, withBuffer = true) => {
    const p = planFor(dish);
    const kids = Number(kidsCovers) || 0;
    const grownUps = Number(adults) || 0;
    const adultPortion = adultPortionFor(dish);
    // With no adult portion to compare against, fall back to a flat half rather than dividing by zero.
    const ratio = adultPortion > 0 ? kidPortionFor(dish) / adultPortion : KID_FACTOR;
    return (grownUps + kids * ratio) * (Number(p.uptake_pct) / 100)
      * (withBuffer ? 1 + buffer / 100 : 1);
  };
  // What the covers model says to cook: kg for weighed items, whole pieces for counted ones.
  const suggestedQty = (dish) => {
    const p = planFor(dish);
    if (unitFor(dish) === 'pcs') {
      return Math.ceil(eatersFor(dish) * adultPortionFor(dish));
    }
    return (eatersFor(dish) * Number(p.portion_g)) / 1000;
  };
  /* The same demand WITHOUT the buffer, shown beside the prep figure so the gap between columns IS the buffer. */
  const neededQty = (dish) => {
    const p = planFor(dish);
    if (unitFor(dish) === 'pcs') {
      return Math.ceil(eatersFor(dish, false) * adultPortionFor(dish));
    }
    return (eatersFor(dish, false) * Number(p.portion_g)) / 1000;
  };
  // What the kitchen actually preps; the legacy qty_kg override is still honoured so an old plan doesn't move.
  const qtyFor = (dish) => {
    const { qty_kg } = planFor(dish);
    if (unitFor(dish) === 'g' && qty_kg != null && qty_kg !== '') return Number(qty_kg) || 0;
    return suggestedQty(dish);
  };
  /* The qty column edits the PORTION, not a detached override, so the covers figure keeps scaling it afterwards. */
  const applyQtyEdit = (dish, raw) => {
    const q = Number(raw);
    if (!Number.isFinite(q) || q < 0) return;
    const eaters = eatersFor(dish);
    if (!eaters) return;
    if (unitFor(dish) === 'pcs') {
      setPlan((p) => ({ ...p, [dish]: {
        ...planFor(dish),
        // A typed total divides back into a WHOLE portion per guest, since nothing countable comes in halves.
        portion_pcs: Math.max(1, wholePcs(q / eaters)),
        qty_kg: null,
      } }));
    } else {
      setPlan((p) => ({ ...p, [dish]: {
        ...planFor(dish),
        portion_g: Math.max(1, Math.round((q * 1000) / eaters)),
        qty_kg: null,
      } }));
    }
  };

  /* ---- derived: pricing math ---- */
  const wd = Number(weekday) || 0;
  /* The margin a price earns against what a cover costs to cook, measured on the NET, since tax is not earned. */
  const margin = (p) => {
    const net = netOf(p);
    return net > 0 && fc > 0 ? Math.round((1 - fc / net) * 100) : null;
  };

  /* What a cover costs from step 5: every costed item's qty times its cost, spread over the covers. */
  const costedDishes = allDishes.filter((d) => Number(planFor(d).cost_per_kg) > 0);
  const dishPrepCost = costedDishes.reduce(
    (n, d) => n + qtyFor(d) * Number(planFor(d).cost_per_kg), 0);
  /* ---- live counters as DISHES ----------------------------------------
     A counter whose master carries a recipe preps like a dish: the station's
     own output (the pasta, not the flour) takes a portion per guest, a kid
     portion and an uptake — the same arithmetic as the dish rows, kept as a
     mirror of eatersFor/suggestedQty on purpose — and its prep figure then
     scales each ingredient by the recipe's per-100 ratio. A counter with no
     recipe keeps the old hand-typed ingredient quantities, so nothing
     already saved changes meaning. */
  const counterAdultPortion = (c) => (c.unit === 'pcs'
    ? wholePcs(c.portion_pcs ?? 1)
    : (Number(c.portion_g) || 0));
  const counterKidPortion = (c) => {
    if (c.unit === 'pcs') {
      return c.kid_portion_pcs != null && c.kid_portion_pcs !== ''
        ? wholePcs(c.kid_portion_pcs)
        : wholePcs(counterAdultPortion(c) * KID_FACTOR);
    }
    return c.kid_portion_g != null && c.kid_portion_g !== ''
      ? Number(c.kid_portion_g)
      : Math.round((Number(c.portion_g) || 0) * KID_FACTOR);
  };
  const counterEaters = (c, withBuffer = true) => {
    const kids = Number(kidsCovers) || 0;
    const grownUps = Number(adults) || 0;
    const ap = counterAdultPortion(c);
    const ratio = ap > 0 ? counterKidPortion(c) / ap : KID_FACTOR;
    return (grownUps + kids * ratio) * (Number(c.uptake_pct ?? 80) / 100)
      * (withBuffer ? 1 + buffer / 100 : 1);
  };
  const counterQty = (c, withBuffer = true) => (c.unit === 'pcs'
    ? Math.ceil(counterEaters(c, withBuffer) * counterAdultPortion(c))
    : (counterEaters(c, withBuffer) * (Number(c.portion_g) || 0)) / 1000);
  /* Typing the station's qty re-derives its portion, exactly as applyQtyEdit
     does for a dish, so the covers figure keeps scaling it afterwards. */
  const applyCounterQtyEdit = (i, c, raw) => {
    const q = Number(raw);
    if (!Number.isFinite(q) || q < 0) return;
    const eaters = counterEaters(c);
    if (!eaters) return;
    if (c.unit === 'pcs') setCounter(i, { portion_pcs: Math.max(1, wholePcs(q / eaters)) });
    else setCounter(i, { portion_g: Math.max(1, Math.round((q * 1000) / eaters)) });
  };
  /* The recipe scaled to what the station preps: qty_per_100 is per 100 kg
     (or 100 pcs) of the counter's own output. */
  const counterIngDerived = (c, r, withBuffer = true) => {
    const per = Number(r.qty_per_100);
    if (!(per > 0)) return null;
    const q = (counterQty(c, withBuffer) * per) / 100;
    return r.unit === 'pcs' ? Math.ceil(q) : Math.round(q * 100) / 100;
  };
  /* The quantity a row actually stands for: derived from the recipe where
     there is one, else whatever was typed — one answer for cost, save and day. */
  const counterIngQtyEff = (c, r) => (Number(r.qty_per_100) > 0
    ? (counterIngDerived(c, r) || 0)
    : (Number(r.qty) || 0));
  /* What a live counter charges the plan: COSTED LIKE A DISH — its own cost
     per kg (or per pc) times what the station preps, or the Catered-at lump
     sum where one is typed (the hired kulfi cart), which wins. Ingredient
     rows carry quantities only; they never price the station. */
  const counterCost = (c) => {
    if (Number(c.total_amount) > 0) return Number(c.total_amount);
    const cpu = Number(c.cost_per_unit);
    return cpu > 0 ? counterQty(c) * cpu : 0;
  };
  const liveCounterCost = counters.reduce((n, c) => n + counterCost(c), 0);
  /* Counters count into the food cost like dishes do — same sheet, same
     arithmetic — so the margin, the suggested price and the profit line all
     already know about them. */
  const prepCost = dishPrepCost + liveCounterCost;
  const costPerCoverCalc = covers > 0 ? prepCost / covers : 0;
  const fc = costPerCoverCalc;

  const halfOf = (p) => String(Math.max(0, Math.round(Number(p) / 2)));

  // Adult price typed → the margin it earns follows, and kids with it.
  const applyAdultPrice = (v) => {
    setWeekday(v);
    const n = Number(v);
    if (kidsLinked && n > 0) setKidPrice(halfOf(n));
    const m = margin(n);
    if (m != null) setTargetMargin(Math.max(0, Math.min(95, m)));
  };
  // Kids typed by hand → their own figure from here on.
  const applyKidPrice = (v) => { setKidsLinked(false); setKidPrice(v); };

  /* ---- the branch's opening hours, which bound the serving window ---- */
  useEffect(() => {
    if (!restaurantId || !locationId) return;
    let alive = true;
    locationsService.get(restaurantId, locationId)
      .then((loc) => { if (alive) setHours(servingWindow(loc?.operating_hours)); })
      // No hours, or a failed call, leaves the fields free rather than refusing every time - better unbounded than locked.
      .catch(() => { if (alive) setHours(null); });
    // The same branch's floor plan feeds the areas picker; failure just hides it, since there are no areas to offer.
    floorPlanService.get(restaurantId, locationId)
      .then((plan) => { if (alive) setFloorPlan(plan || null); })
      .catch(() => { if (alive) setFloorPlan(null); });
    return () => { alive = false; };
  }, [restaurantId, locationId]);

  /* Sections flattened for the picker with their counts, then the seat arithmetic for the chosen areas. */
  const areaChoices = useMemo(() => (floorPlan?.floors || []).flatMap((f) =>
    (f.sections || f.rooms || []).map((sec) => ({
      id: Number(sec.id),
      name: sec.name,
      floor: f.name,
      tables: (sec.tables || []).length,
      seats: (sec.tables || []).reduce((n, t) => n + (Number(t.capacity) || 0), 0),
    }))), [floorPlan]);
  const pickedAreas = useMemo(
    () => (areaIds.length ? areaChoices.filter((a) => areaIds.includes(a.id)) : areaChoices),
    [areaChoices, areaIds],
  );
  const areaTables = pickedAreas.reduce((n, a) => n + a.tables, 0);
  const areaSeats = pickedAreas.reduce((n, a) => n + a.seats, 0);
  /* areaChoices carries counts for the chips; the planner needs the rooms themselves, with coordinates and fixtures. */
  const pickedSections = useMemo(() => {
    const want = new Set(pickedAreas.map((a) => Number(a.id)));
    return (floorPlan?.floors || []).flatMap((f) => (f.sections || f.rooms || []))
      .filter((sec) => want.has(Number(sec.id)));
  }, [floorPlan, pickedAreas]);

  /* ---- the branch's buffet menus (step 4's list) ---- */
  useEffect(() => {
    if (!restaurantId) return;
    setMenusLoading(true);
    apiClient.get(`/users/restaurant/${restaurantId}/getAllMenus/`)
      .then(({ data }) => setRestMenus((data?.success && data.data) || []))
      .catch(() => setRestMenus([]))
      .finally(() => setMenusLoading(false));
  }, [restaurantId]);

  /* Names already taken at this branch, so a clash is caught on step 1 rather than after the whole wizard. */
  const [takenNames, setTakenNames] = useState([]);
  useEffect(() => {
    if (!restaurantId) return;
    const params = { include_inactive: 'true' };
    if (locationId) params.location_id = locationId;
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/`, { params })
      .then(({ data }) => {
        const rows = (data?.success ? (data.results || data.data) : []) || [];
        setTakenNames(rows
          .filter((r) => String(r.id) !== String(buffet?.id || ''))
          .map((r) => String(r.name || '').trim().toLowerCase()));
      })
      .catch(() => setTakenNames([]));
  }, [restaurantId, locationId, buffet?.id]);

  const buffetMenus = useMemo(() => restMenus.filter((m) => (
    // Buffet-type menus served at this branch; a stale a-la-carte link stays visible so saving never drops it.
    (m.menu_type === 'BUFFET'
      && (!locationId || !(m.location_ids || []).length
        || (m.location_ids || []).map(String).includes(String(locationId))))
    || String(m.id) === String(menuId || '')
  )), [restMenus, locationId, menuId]);

  /* Pull a menu's sections + dishes in as this buffet's spread. */
  const pickMenu = async (m) => {
    const hasSpread = sections.some((s) => s.items.filter(Boolean).length);
    if (hasSpread && String(m.id) !== String(menuId)) {
      const ok = await confirm({
        title: `Load "${m.name}" into this buffet?`,
        description: 'The list below is replaced by that menu. '
          + 'Quantities are kept for dishes on both.',
        confirmText: 'Load menu',
      });
      if (!ok) return;
    }
    setImporting(true);
    setError('');
    try {
      const { data } = await apiClient.get(`/users/restaurant/${restaurantId}/menus/${m.id}/sections/`);
      if (!data?.success) throw new Error(data?.error || 'Could not read that menu');
      const secs = (data.data || []).map((s) => ({
        name: s.name || '',
        items: (s.items || []).map((it) => it.dish_name).filter(Boolean),
      }));
      /* Switching to a DIFFERENT menu starts the counter fresh, since dish names carried over would switch off the wrong dishes. */
      if (String(m.id) !== String(menuId)) setOffDishes(new Set());
      setMenuId(m.id);
      setSections(secs);
      if (!name.trim()) setName(m.name || '');
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not load that menu');
    } finally {
      setImporting(false);
    }
  };

  /* ---- reports data (existing buffets only) ---- */
  const [sales, setSales] = useState([]);
  const [waste, setWaste] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  useEffect(() => {
    if (!buffet?.id) return;
    setReportsLoading(true);
    Promise.all([
      apiClient.get(`/users/restaurant/${restaurantId}/buffets/${buffet.id}/covers/`).catch(() => null),
      apiClient.get(`/users/restaurant/${restaurantId}/buffets/${buffet.id}/waste/`).catch(() => null),
    ]).then(([c, w]) => {
      setSales(c?.data?.results || c?.data?.data || []);
      setWaste(w?.data?.results || w?.data?.data || []);
    }).finally(() => setReportsLoading(false));
  }, [restaurantId, buffet?.id]);

  const report = useMemo(() => {
    const now = Date.now();
    const weekAgo = now - 7 * 86400000;
    const at = (s) => new Date(s.created_at || s.date || 0).getTime();
    const thisWeek = sales.filter((s) => at(s) >= weekAgo);
    const coversWk = thisWeek.reduce((n, s) => n + (Number(s.adults) || 0) + (Number(s.children) || 0), 0);
    const revenueWk = thisWeek.reduce((n, s) => n + (Number(s.total) || 0), 0);
    const arpc = coversWk ? revenueWk / coversWk : 0;
    const wasteWk = waste.filter((s) => at(s) >= weekAgo);
    const wasteCost = wasteWk.reduce((n, s) => n + (Number(s.est_cost) || 0), 0);
    // Covers by day (last 7 days), split lunch (< 5 PM) vs dinner.
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(now - (6 - i) * 86400000);
      return { key: d.toDateString(), label: d.toLocaleDateString(undefined, { weekday: 'short' }), lunch: 0, dinner: 0 };
    });
    thisWeek.forEach((s) => {
      const d = new Date(s.created_at || s.date || 0);
      const row = days.find((x) => x.key === d.toDateString());
      if (!row) return;
      const n = (Number(s.adults) || 0) + (Number(s.children) || 0);
      if (d.getHours() < 17) row.lunch += n; else row.dinner += n;
    });
    // Waste kg per dish (kg rows only) for prepped-vs-consumed.
    const wasteKg = {};
    wasteWk.forEach((s) => {
      if ((s.unit || '').toLowerCase() === 'kg') {
        wasteKg[s.item] = (wasteKg[s.item] || 0) + (Number(s.qty) || 0);
      }
    });
    return { coversWk, revenueWk, arpc, wasteCost, days, wasteKg };
  }, [sales, waste]);

  const insights = useMemo(() => {
    const out = [];
    const wasteTop = Object.entries(report.wasteKg).sort((a, b) => b[1] - a[1])[0];
    if (wasteTop) {
      out.push({
        tone: 'warn', Icon: AlertTriangle,
        text: `${wasteTop[0]} shows the most left-over this week (${kg(wasteTop[1])}) — consider cutting its uptake assumption in the ⚖️ Quantity per item step.`,
      });
    }
    const busiest = [...report.days].sort((a, b) => (b.lunch + b.dinner) - (a.lunch + a.dinner))[0];
    if (busiest && busiest.lunch + busiest.dinner > 0) {
      out.push({
        tone: 'good', Icon: TrendingUp,
        text: `${busiest.label} is your busiest day (${busiest.lunch + busiest.dinner} covers) — check prep quantities cover it.`,
      });
    }
    if (report.arpc > 0 && fc > 0) {
      out.push({
        tone: 'info', Icon: Lightbulb,
        text: `Realised price per cover is ${rupees(report.arpc)} vs ${rupees(fc)} food cost — a ${Math.round((1 - fc / report.arpc) * 100)}% food margin.`,
      });
    }
    return out;
  }, [report, fc]);

  /* ---- steps ---- */
  const steps = useMemo(() => ([
    /* Step 1 answers WHAT this buffet is, WHEN it runs and HOW it is split:
       the name, the dates it should open on, and the sittings those dates
       will be sold in. The slot list is generated here because every later
       step is a per-slot decision — the menu it serves, the hall it fills,
       and the price each one charges. */
    { key: 'time', title: 'Name, dates & sittings', emoji: '⏰' },
    { key: 'menu', title: 'Buffet menu', emoji: '🍽️' },
    /* The hall AFTER the slots, not before: the map is read against the
       sittings it has to seat, so the slots have to exist first. */
    { key: 'seating', title: 'Sitting map', emoji: '🪑' },
    /* No Quantity step any more: portions live on the REGISTER (Buffet →
       Dish Quantities), loaded over this buffet's snapshot and re-snapshotted
       at save — the same define-once contract live counters follow. */
    { key: 'price', title: 'Pricing', emoji: '💰' },
    /* Reports only once a service is on record; a tab full of zeros reads as broken rather than pending. */
    ...(buffet?.id && (sales.length > 0 || waste.length > 0)
      ? [{ key: 'report', title: 'Reports', emoji: '📊' }] : []),
  ]), [buffet?.id, sales.length, waste.length]);
  const step = steps[Math.min(stepIdx, steps.length - 1)];

  // What still has to happen before a step counts as done - also what the footer refuses to walk past.
  /* ---- which days the run actually covers ---- */

  // The rule, resolved once. Everything below reads this and nothing re-derives it.
  const plannedDates = useMemo(
    () => runDates(runFrom, runTo, runDow, runExcept),
    [runFrom, runTo, runDow, runExcept],
  );
  const plannedSet = useMemo(() => new Set(plannedDates), [plannedDates]);

  /* Days ALREADY open that the rule now leaves out — Saturday unticked on a
     buffet that was opened every day for a season, or a date struck by hand
     that Open Buffets already has. Save closes these (the server refuses any
     that ran or sold covers, and says which), and the calendar strikes them
     out the moment the chip is pressed, instead of lighting them as if the
     rule had never been read. Future days only, and only PLANNED ones can
     go: the past is a record, and a running service is not un-scheduled
     from a form. `kept` is what the screen can already tell will be refused. */
  const struckOpen = useMemo(() => {
    const today = todayISO();
    const ticked = new Set(runDow);
    const struck = new Set(runExcept);
    const out = { closes: [], kept: [] };
    (openedDays || []).forEach((o) => {
      const d = parseDay(o?.date);
      if (!d || o.date < today) return;
      if (ticked.has(PY_DOW(d)) && !struck.has(o.date)) return;
      (o.status === 'PLANNED' ? out.closes : out.kept).push(o.date);
    });
    return out;
  }, [openedDays, runDow, runExcept]);
  const struckOpenSet = useMemo(
    () => new Set([...struckOpen.closes, ...struckOpen.kept]), [struckOpen],
  );

  /* Public holidays sitting inside the span. The buffet calendar already
     loads these to mark them, so offering to skip them costs nothing and
     saves a manager striking out Divali and the two Eids by hand. Only DAYS
     OFF: an observance like Père Laval is a day the island is out and moving,
     which is a day to open, not to close. */
  const holidaysInSpan = useMemo(() => {
    if (!runFrom || !runTo) return [];
    return (holidays || [])
      .filter((h) => h?.is_public_holiday && h.date >= runFrom && h.date <= runTo)
      .map((h) => h.date);
  }, [holidays, runFrom, runTo]);

  /* The same days as WHOLE ENTRIES, and observances included — what advance
     pricing prices against. The skip-toggle above wants days off; a price
     wants every day the island is out or on the move, which is exactly the
     set an observance belongs to. */
  const runHolidays = useMemo(() => {
    if (!spanFrom || !spanTo) return [];
    return (holidays || [])
      .filter((h) => h?.date && h.date >= spanFrom && h.date <= spanTo)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [holidays, spanFrom, spanTo]);

  /* Festival key → the dates an uplift rule for it should carry. The live
     calendar answers for every holiday inside the run; anything ticked on an
     EARLIER save that this range no longer covers falls back to what that
     save wrote. Without the fallback, opening a saved buffet (which keeps no
     run range, so no calendar is loaded) and pressing Save would quietly
     delete every uplift the manager had set. */
  const holidayRuleDates = useMemo(() => {
    const m = new Map();
    advRules
      .filter((r) => r.config.advance_uplift === 'HOLIDAY' && r.config.holiday_key)
      .forEach((r) => m.set(r.config.holiday_key, {
        name: r.label, date: r.date_from, end_date: r.date_to,
      }));
    runHolidays.forEach((h) => m.set(h.key, {
      name: h.name, date: h.date, end_date: h.end_date || h.date,
    }));
    return m;
  }, [advRules, runHolidays]);

  // Every holiday in the span is struck out — and there is at least one, or
  // the toggle would read "on" for a range that simply contains no holidays.
  const skipsHolidays = holidaysInSpan.length > 0
    && holidaysInSpan.every((d) => runExcept.includes(d));

  const toggleDow = (n) => setRunDow((prev) => (prev.includes(n)
    ? prev.filter((x) => x !== n)
    : [...prev, n].sort((a, b) => a - b)));

  /* Strike a single date out, or put it back. The calendar calls this, which
     is what makes "any day, for any reason" possible without a rule for it. */
  const toggleExcept = (iso) => setRunExcept((prev) => (prev.includes(iso)
    ? prev.filter((d) => d !== iso)
    : [...prev, iso].sort()));

  const toggleSkipHolidays = () => setRunExcept((prev) => (skipsHolidays
    ? prev.filter((d) => !holidaysInSpan.includes(d))
    : [...new Set([...prev, ...holidaysInSpan])].sort()));

  const stepIssue = (key) => {
    if (key === 'time') {
      if (!name.trim()) return 'Give the buffet a name first — it is the first field in this step.';
      // Two buffets with one name make the board, the till and every report a guess about which is which.
      if (takenNames.includes(name.trim().toLowerCase())) {
        return `This branch already has a buffet called “${name.trim()}”. `
          + 'Give this one a name that tells them apart.';
      }
      if (!startTime || !endTime) return 'Set the start and end time for this buffet before moving on.';
      // A window of zero or negative minutes is not a buffet, refused here so the walk and Save say the same thing.
      if (toMin(endTime) <= toMin(startTime)) {
        return `The buffet has to end after it starts — pick an end time later than ${to12h(startTime)}.`;
      }
    }
    /* Sittings switched on that produce nothing would leave a buffet nobody
       can book. Raised on step 1 now, which is where the hours, the gap and
       the length all sit — so the message can point at the very same step
       rather than sending anyone back a page. */
    if (key === 'time' && sitOn && sittingStarts.length === 0) {
      return `A ${sitMin}-minute sitting does not fit between `
        + `${to12h(startTime)} and ${to12h(endTime)} — shorten the sitting, `
        + 'or widen the serving window above.';
    }
    /* A date range that runs backwards would schedule nothing at all, so it
       is refused here rather than silently opening zero days on save. */
    if (key === 'time' && runFrom && runTo && runTo < runFrom) {
      return 'The last date has to be on or after the first — pick a range that runs forwards.';
    }
    /* A range whose every day has been ticked away or struck out opens
       nothing. Caught here, beside the controls that emptied it, and worded
       for whichever of the two did — the fix is a different one each way. */
    if (key === 'time' && runFrom && runTo && runTo >= runFrom
        && plannedDates.length === 0) {
      return runDow.length === 0
        ? 'No days of the week are ticked, so the buffet would never run — '
          + 'tick at least one day.'
        : 'Every day in that range is skipped, so nothing would open — '
          + 'put a date back, or tick another day of the week.';
    }
    /* The server refuses to open a buffet on a day that has been, so the same
       rule is said HERE, beside the field, rather than after a full save. */
    if (key === 'time' && runFrom && runFrom < todayISO()) {
      return 'A buffet can only be opened on today or later — move the first date forward.';
    }
    /* One date without the other opens nothing and reads as a half-finished
       thought, so it is named rather than silently ignored at save. */
    if (key === 'time' && !!runFrom !== !!runTo) {
      return 'Give both dates, or neither — one on its own opens nothing.';
    }
    /* The guest count is typed on step 1 now, so it is step 1 that refuses to
       be left without one — step 2 only splits a number that already exists,
       and every row on it is optional. */
    if (key === 'time' && covers < 1) {
      return 'Say how many guests you are prepping for — at least one adult or child.';
    }
    /* The seating questions ride on the merged Seating & sittings step. */
    if (key === 'guests' && !serviceStyle) {
      return 'Say how guests eat — sitting at tables, or standing.';
    }
    /* An empty pick used to mean "the whole branch" silently; where it is laid out decides capacity, so it is asked. */
    if (key === 'guests' && areaChoices.length > 0 && areaIds.length === 0) {
      return 'Pick at least one area the buffet is laid out in.';
    }
    /* Counter names key the day's prep rows, so a blank or doubled name would file quantities under nothing, or under each other. */
    if (key === 'menu' && counters.some((c) => !c.name.trim())) {
      return 'Every live counter needs a name — fill it in, or remove the blank one.';
    }
    if (key === 'menu') {
      const seen = new Set();
      for (const c of counters) {
        const k = c.name.trim().toLowerCase();
        if (seen.has(k)) {
          return `Two live counters are both called “${c.name.trim()}” — give them names that tell them apart.`;
        }
        seen.add(k);
      }
    }
    if (key === 'menu' && !menuId && allDishes.length === 0) {
      return 'Pick the buffet menu this spread serves from, or add a course with at least one dish.';
    }
    /* A menu is picked but every dish is switched off - the next steps would price and prep an empty counter. */
    if (key === 'menu' && menuId && allDishes.length === 0) {
      return 'Every dish is switched off — tick at least one to put on the counter.';
    }
    if (key === 'price' && (weekday === '' || Number(weekday) <= 0)) {
      return pricingMode === 'FIXED'
        ? 'The adult price is required — it is what every grown-up pays.'
        : 'The base adult price is required — it is the figure the pricing rules move.';
    }
    /* Blank is not an answer on a price; zero is, and is how "children eat free" is said explicitly. */
    if (key === 'price' && kidPrice === '') {
      return 'Set the kids price — put 0 if children eat free.';
    }
    return '';
  };
  const stepDone = (key) => {
    if (key === 'report') return false;
    return !stepIssue(key);
  };

  const goTo = (i) => {
    // A saved buffet may jump between steps; a new one is walked in order.
    if (!buffet?.id && i > stepIdx) {
      for (let k = stepIdx; k < i; k += 1) {
        const issue = stepIssue(steps[k].key);
        if (issue) { setError(issue); setStepIdx(k); return; }
      }
    }
    setError('');
    setStepIdx(i);
  };
  const next = () => {
    const issue = stepIssue(step.key);
    if (issue) { setError(issue); return; }
    setError('');
    setStepIdx((i) => Math.min(i + 1, steps.length - 1));
  };
  const back = () => { setError(''); setStepIdx((i) => Math.max(i - 1, 0)); };

  /* ---- save ---- */
  /* Save as draft parks it INACTIVE with only a name required; a full save walks every step's validation. */
  useEffect(() => {
    if (!restaurantId || !spanFrom) { return undefined; }
    let cancelled = false;
    // A range can straddle a year end (a Christmas-to-New-Year buffet), so
    // both years are asked for and merged.
    const years = [...new Set([spanFrom.slice(0, 4), (spanTo || spanFrom).slice(0, 4)])];
    Promise.all(years.map((y) => getCalendar(restaurantId, { year: Number(y) })))
      .then((results) => {
        if (cancelled) return;
        setHolidays(results.flatMap((r) => r?.entries || []));
        setHolidaysFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHolidays([]);
        setHolidaysFailed(true);
      });
    return () => { cancelled = true; };
  }, [restaurantId, spanFrom, spanTo]);

  const save = async (asDraft) => {
    const draft = asDraft === true;
    if (draft) {
      if (!name.trim()) {
        setError('A draft still needs a name — it is how you will find it in the list.');
        setStepIdx(steps.findIndex((s) => s.key === 'time'));
        return;
      }
    } else {
      for (const s of steps) {
        const issue = stepIssue(s.key);
        if (issue) {
          setError(issue);
          setStepIdx(steps.indexOf(s));
          return;
        }
      }
    }
    setSaving(true);
    setError('');
    try {
      /* Counted portions are STORED whole, so a plan saved when halves were allowed is repaired by the next save. */
      const dishPlan = Object.fromEntries(
        Object.entries(plan).map(([dish, p]) => {
          if (!p || typeof p !== 'object') return [dish, p];
          const out = { ...p };
          if (out.portion_pcs != null && out.portion_pcs !== '') {
            out.portion_pcs = wholePcs(out.portion_pcs);
          }
          if (out.kid_portion_pcs != null && out.kid_portion_pcs !== '') {
            out.kid_portion_pcs = wholePcs(out.kid_portion_pcs);
          }
          return [dish, out];
        }),
      );
      const config = {
        /* Spread the saved config first, then the fields this step owns. */
        ...cfg,
        /* Scheduling is gone from the studio, so saving CLEARS all three:
           runs_on() still gates on them, and a range or a weekday an older
           buffet was left with would go on hiding it with no field on any
           screen to clear. Empty means "every day it is opened on", and
           which days that is belongs to Open Buffets. */
        runs_from: '',
        runs_until: '',
        run_weekdays: [],
        area_section_ids: areaIds, service_style: serviceStyle,
        /* Only the picked areas are kept, so a dropped area doesn't leave stations behind to reappear months later. */
        floor_layout: Object.fromEntries(
          Object.entries(floorLayout)
            .filter(([id]) => pickedSections.some((sec) => String(sec.id) === String(id))),
        ),
        /* open_bar is deliberately NOT written here any more: the field is
           gone from the form, so this cannot know the answer. Whatever an
           existing buffet was saved with rides through on the `...cfg` spread
           above rather than being overwritten with a guess — a save must not
           silently turn a real open-bar deal off because the switch that set
           it no longer exists. */
        spread_type: spread,
        /* How the window is sold: one service, or a start time every so many
           minutes. Every typed price is kept — including any for a start time
           the window no longer offers, so narrowing the hours for a week does
           not throw away what a sitting was priced at. The server reads only
           the start times its own arithmetic produces, so nothing stale can
           reach a booking page. */
        sittings: {
          enabled: !!sitOn,
          gap_min: Number(gapMin) || 30,
          duration_min: Number(sitMin) || 120,
          prices: Object.fromEntries(
            Object.entries(sitPrices)
              .map(([start, v]) => [start, {
                adult: v?.adult === '' || v?.adult == null ? null : Number(v.adult),
                child: v?.child === '' || v?.child == null ? null : Number(v.child),
                covers: v?.covers === '' || v?.covers == null ? null : Number(v.covers),
                /* How many guests are EXPECTED in this sitting (step 2's
                   matrix) — a different fact from `covers`, which caps how
                   many may book it. Blank stays null: the sitting then draws
                   on the day's covers rather than planning for nobody. */
                adults: v?.adults === '' || v?.adults == null ? null : Number(v.adults),
                kids: v?.kids === '' || v?.kids == null ? null : Number(v.kids),
                off: !!v?.off,
              }])
              /* A row where nothing was typed is not a decision — dropped, so
                 the blob holds the prices somebody set and nothing else. */
              .filter(([, v]) => v.adult != null || v.child != null
                || v.covers != null || v.adults != null || v.kids != null || v.off),
          ),
        },
        /* The counters in full, and their names mirrored into live_stations so
           anything still reading the old key keeps its answer. Blank rows are
           tidied on the way out: an unnamed counter and a nameless ingredient
           are half-typed thoughts, not plans. */
        live_counters: counters
          .filter((c) => c.name.trim())
          .map((c) => ({
            // Which register entry it came from, so screens can tell a master
            // counter from one saved before the register existed.
            master_id: c.master_id ?? null,
            name: c.name.trim(),
            total_amount: Number(c.total_amount) > 0 ? Number(c.total_amount) : null,
            // The station's own per-kg/pc price — with total_amount, the two ways a counter is costed.
            cost_per_unit: Number(c.cost_per_unit) > 0 ? Number(c.cost_per_unit) : null,
            /* The station's own dish-like plan, saved so a reload derives the
               same figures the sheet showed. */
            unit: c.unit === 'pcs' ? 'pcs' : 'g',
            portion_g: Number(c.portion_g) || DEFAULT_PLAN.portion_g,
            portion_pcs: wholePcs(c.portion_pcs) || DEFAULT_PLAN.portion_pcs,
            kid_portion_g: c.kid_portion_g === '' || c.kid_portion_g == null ? null : Number(c.kid_portion_g),
            kid_portion_pcs: c.kid_portion_pcs === '' || c.kid_portion_pcs == null ? null : wholePcs(c.kid_portion_pcs),
            uptake_pct: Number(c.uptake_pct) >= 0 ? Number(c.uptake_pct) : DEFAULT_PLAN.uptake_pct,
            ingredients: c.ingredients
              .filter((r) => String(r.name || '').trim())
              .map((r) => ({
                ingredient_id: r.ingredient_id ?? null,
                name: String(r.name).trim(),
                /* The ABSOLUTE quantity — derived from the recipe where there
                   is one, else typed. Saved resolved so every day opened from
                   this template (service_plan.template_items) preps the same
                   figure the sheet showed, with no reader re-deriving. */
                qty: counterIngQtyEff(c, r) || 0,
                unit: ['kg', 'pcs'].includes(r.unit) ? r.unit : 'kg',
                cost_per_unit: Number(r.cost_per_unit) > 0 ? Number(r.cost_per_unit) : 0,
                // The ratio itself rides along, so a reload keeps deriving.
                qty_per_100: Number(r.qty_per_100) > 0 ? Number(r.qty_per_100) : null,
              })),
          })),
        live_stations: counters.map((c) => c.name.trim()).filter(Boolean),
        policies, rotation_note: rotationNote,
        // Dishes on the menu but off this buffet's counter.
        off_dishes: [...offDishes],
        safety_buffer_pct: buffer, dish_plan: dishPlan,
        kids_covers: Number(kidsCovers) || 0,
        target_margin_pct: targetMargin,
        pricing_mode: pricingMode,
        tax_label_ids: taxIds,
        // Whether the per-head prices already contain those taxes; false is how every buffet behaved before this switch.
        tax_inclusive: !!taxInclusive,
        weekend_adult_price: weekend === '' ? null : Number(weekend),
      };
      const menus = [
        {
          name: buffet?.menus?.[0]?.name || "Today's spread",
          description: buffet?.menus?.[0]?.description || '',
          days: buffet?.menus?.[0]?.days || [],
          sections: sections.filter((s) => s.name.trim()),
          is_active: true,
        },
        // Preserve any additional spreads untouched.
        ...(buffet?.menus || []).slice(1),
      ];
      /* The day-board matrix is gone: its tagged rules retire at save;
         hand-made rules (the rules screen's) ride along untouched. Advance
         settings owns its own rules and rebuilds them below, so they are
         dropped here rather than duplicated. */
      const manualRules = (buffet?.price_rules || [])
        .filter((r) => !r?.config?.day_pricing && !r?.config?.advance_uplift);

      /* Phase 2's uplifts, written as the pricing rules that actually charge
         them. A holiday is pinned to its own date (SEASONAL, from and to the
         same day) so next month's service is untouched; a weekend is a plain
         weekday rule. Both are PERCENT/AMOUNT modifiers, which the engine
         stacks on whatever step 1's base price resolved to — so "10% more
         than what was configured" stays true after the base price is edited.
         A blank or zero uplift is not a decision and writes no rule. */
      const upliftRule = (u, extra) => {
        const v = Number(u?.value);
        if (!upliftSet(u)) return null;
        /* A new price is a SET rule — the engine takes it outright, and it
           stays what was typed whatever step 1 later says. A % is a
           modifier on whatever the base resolves to. */
        if (u.mode === 'SET') {
          return {
            price_mode: 'SET',
            adult_price: v,
            child_price: childAt(v),
            adjust_value: null,
            is_active: true,
            ...extra,
          };
        }
        return {
          price_mode: 'PERCENT',
          adjust_value: v,
          is_active: true,
          ...extra,
        };
      };
      const advanceRules = [
        ...Object.entries(holidayUplifts).map(([key, u]) => {
          const h = holidayRuleDates.get(key);
          if (!h?.date) return null;
          return upliftRule(u, {
            rule_type: 'SEASONAL',
            label: h.name || key,
            days: [],
            date_from: h.date,
            date_to: h.end_date || h.date,
            // Above the weekend rule on purpose: a holiday that lands on a
            // Sunday is being priced as the holiday, and the engine stacks
            // modifiers in priority order.
            priority: 20,
            config: { advance_uplift: 'HOLIDAY', holiday_key: key },
          });
        }),
        ...Object.entries(weekendUplifts).map(([dow, u]) => upliftRule(u, {
          rule_type: 'TIME',
          label: DOW_LABEL[Number(dow)] || `Day ${dow}`,
          days: [Number(dow)],
          priority: 10,
          config: { advance_uplift: 'WEEKEND' },
        })),
      ].filter(Boolean);

      const payload = {
        name: name.trim(),
        description,
        buffet_type: buffetType,
        location_id: Number(locationId),
        menu_id: menuId === '' ? null : Number(menuId),
        price_rules: [...manualRules, ...advanceRules],
        base_adult_price: Number(weekday),
        // The kids price is TYPED in both modes; blank means kids pay the adult price, and nothing halves it silently.
        base_child_price: kidPrice === '' ? null : Number(kidPrice),
        child_age_limit: 12,
        start_time: startTime,
        end_time: endTime,
        // Derived from step 5 rather than typed, saved so the reports and break-even math have the figure they expect.
        est_food_cost_per_cover: fc > 0 ? Number(fc.toFixed(2)) : null,
        /* fixed_cost_per_day is no longer sent: the Additional costs box
           that set it has gone from this step, so a save cannot know the
           answer. Omitted rather than nulled — the server only writes
           what the payload names, so an existing figure survives instead
           of being wiped by a form that stopped asking. */
        expected_covers: covers,
        config,
        menus,
        /* A draft is parked inactive, invisible to guests and the till; a full save is also how a draft goes live. */
        is_active: !draft,
      };
      /* THE ID WE ALREADY HAVE: the prop, for a buffet opened from the
         board — or the one the CREATE below handed back on an EARLIER save
         of this same studio session. Without that second half, every press
         of Save on a new buffet POSTs another one, so the second press is
         refused for clashing with the buffet the first press just created
         ("This branch already has a buffet called ..."), and the manager is
         told to rename something they never duplicated. */
      const existingId = buffet?.id || savedId;
      const url = existingId
        ? `/users/restaurant/${restaurantId}/buffets/${existingId}/`
        : `/users/restaurant/${restaurantId}/buffets/`;
      const { data } = existingId
        ? await apiClient.put(url, payload)
        : await apiClient.post(url, payload);
      if (!data?.success) throw new Error(data?.error || 'Failed to save');

      /* The date range from step 1 is carried out HERE, once the buffet
         exists and has an id to open. A draft is skipped on purpose: parking
         something inactive must not put it on the counter for a fortnight.

         Openings are attempted one day at a time and failures are COUNTED,
         not thrown: a day that already has this buffet open is not an error
         worth losing the save over, and the summary tells the manager exactly
         how many days took. */
      const buffetId = data.data?.id || existingId;
      /* Set here, not after the openings call below: the buffet IS saved at
         this point, and the calendar must say so even when opening its dates
         fails — which is exactly the case that used to leave a saved buffet
         looking unsaved. */
      if (buffetId) setSavedId(buffetId);
      if (buffetId && !draft && runFrom && runTo) {
        /* ONE request for the whole range: the endpoint already expands a
           repeat server-side, enforces its own year cap and skips days the
           buffet is already open on. Looping a request per day would be up
           to 366 round trips and could half-succeed. */
        try {
          const res = await apiClient.post(
            `/users/restaurant/${restaurantId}/buffets/openings/`,
            {
              buffet_id: buffetId,
              date: runFrom,
              /* The same rule the count and the calendar were drawn from —
                 sent as the RULE, not as the dates it resolves to, so the
                 server applies its own year cap and its own already-open
                 skip rather than trusting a list this screen built. */
              repeat: {
                mode: runDow.length === ALL_DOW.length ? 'daily' : 'days',
                days: runDow,
                until: runTo,
                except: runExcept,
              },
              /* Every save re-sends the whole range, so by the second one the
                 days are already open. That is the state we asked for, not a
                 clash: `ensure` says so, and the endpoint stops calling it a
                 conflict. Without it, editing a saved buffet and pressing Save
                 reported "those dates could not be opened" every time. */
              ensure: true,
              /* And hold the days ALREADY open to the same rule: a weekday
                 unticked or a date struck out closes its open days, rather
                 than only shaping the ones this save adds. */
              close_struck: true,
            },
          );
          if (!res?.data?.success) throw new Error(res?.data?.error || 'Could not open those dates');
          /* A run can now half-succeed: days the template could not be built
             for are reported rather than taking the whole range down, and a
             database that refused a row stops the run where it stood. Both
             leave the manager on the studio with the count and the reason —
             the dates that DID open are open, and pressing Save again picks
             up where this left off, since a day already open is skipped. */
          const failed = res?.data?.failed || [];
          const stopped = res?.data?.stopped;
          if (failed.length || stopped) {
            const opened = (res?.data?.created || []).length;
            /* The manager stays on this screen, so the calendar has to show
               the days that DID open — otherwise it still reads as the range
               they typed and hides how far the run actually got. */
            setOpenedTick((n) => n + 1);
            setError(t('buffet.dates.partlyOpened', {
              opened,
              total: plannedDates.length,
              error: stopped || failed[0]?.error || '',
            }));
            setSaving(false);
            return;
          }
          /* Days the rule took OUT that were already open: closed now, and
             the calendar re-read so they go dark. Any the server kept — a
             service that ran, or sold covers — is named rather than folded
             into a success. */
          const closed = res?.data?.closed || [];
          const kept = res?.data?.kept || [];
          if (closed.length || kept.length) setOpenedTick((n) => n + 1);
          if (kept.length) {
            setError(t('buffet.dates.someKept', {
              closed: closed.length,
              kept: kept.length,
              error: kept[0]?.error || '',
            }));
            setSaving(false);
            return;
          }
        } catch (openErr) {
          /* The buffet IS saved at this point, so this must not read as a
             failed save. Say what did and did not happen, and leave the
             manager on the studio rather than bouncing them to a board that
             looks unchanged. */
          setOpenedTick((n) => n + 1);
          setError(t('buffet.dates.savedNotOpened', {
            error: openErr?.response?.data?.error || openErr.message,
          }));
          setSaving(false);
          return;
        }
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const toggleIn = (list, v) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

/* ---- charts ---- */
  const coversChart = () => {
    const max = Math.max(10, ...report.days.map((d) => Math.max(d.lunch, d.dinner)));
    const W = 640; const H = 180; const pad = 30;
    const bw = (W - pad * 2) / report.days.length;
    const y = (v) => H - 24 - (v / max) * (H - 40);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Covers by day, lunch vs dinner">
        {[0.25, 0.5, 0.75, 1].map((f) => (
          <line key={f} x1={pad} x2={W - pad} y1={y(max * f)} y2={y(max * f)} stroke={C_GRID} strokeWidth="1" />
        ))}
        {report.days.map((d, i) => {
          const x0 = pad + i * bw + bw * 0.18;
          const w = bw * 0.26;
          return (
            <g key={d.key}>
              <rect x={x0} y={y(d.lunch)} width={w} height={Math.max(0, H - 24 - y(d.lunch))} rx="3" fill={C_BLUE}>
                <title>{`${d.label} lunch: ${d.lunch} covers`}</title>
              </rect>
              <rect x={x0 + w + 2} y={y(d.dinner)} width={w} height={Math.max(0, H - 24 - y(d.dinner))} rx="3" fill={C_GREEN}>
                <title>{`${d.label} dinner: ${d.dinner} covers`}</title>
              </rect>
              <text x={pad + i * bw + bw / 2} y={H - 8} textAnchor="middle" fontSize="11" fill={C_MUTED}>{d.label}</text>
            </g>
          );
        })}
        <line x1={pad} x2={W - pad} y1={H - 24} y2={H - 24} stroke="#c3c2b7" strokeWidth="1" />
      </svg>
    );
  };

  const preppedChart = () => {
    // Kilogram bars: counted items don't belong on a weight axis, so only weighed dishes are charted.
    const rows = allDishes
      .filter((d) => unitFor(d) === 'g')
      .map((d) => {
        const prepped = qtyFor(d);
        const wasted = report.wasteKg[d] || 0;
        return { d, prepped, consumed: Math.max(0, prepped - wasted) };
      })
      .sort((a, b) => b.prepped - a.prepped)
      .slice(0, 6);
    if (!rows.length) return null;
    const max = Math.max(...rows.map((r) => r.prepped), 1);
    return (
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.d} className="flex items-center gap-3">
            <span className="w-32 shrink-0 truncate text-xs text-muted-foreground text-right">{r.d}</span>
            <div className="flex-1 space-y-1">
              <div className="h-3 rounded-r bg-muted">
                <div className="h-3 rounded-r" style={{ width: `${(r.prepped / max) * 100}%`, background: C_BLUE }}
                  title={`Prepped ${kg(r.prepped)}`} />
              </div>
              <div className="h-3 rounded-r bg-muted">
                <div className="h-3 rounded-r" style={{ width: `${(r.consumed / max) * 100}%`, background: C_MUTED }}
                  title={`Consumed ${kg(r.consumed)}`} />
              </div>
            </div>
            <span className="w-20 shrink-0 text-xs text-muted-foreground">{kg(r.prepped)} / {kg(r.consumed)}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ── steps ─────────────────────────────────────────────────────────── */

  const timeStep = () => {
    /* Ends cannot open before the quarter hour after Starts, so a buffet finishing before it begins can't be expressed. */
    const endFloor = hours && startTime
      ? toHHMM(toMin(startTime) + SLOT_MINUTES)
      : (hours ? hours.open : '');
    /* The same three time boxes used everywhere, bounded by the branch's hours; a legacy end before its start is never re-offered. */
    const timeField = (id, label, value, onPick, lo, hi, keep) => (
      <div>
        <Label htmlFor={id} className="text-sm text-muted-foreground">
          {label} <span className="text-red-500">*</span>
        </Label>
        <TimePicker
          id={id}
          className="mt-1"
          value={value || ''}
          onChange={onPick}
          min={hours ? lo : undefined}
          max={hours ? hi : undefined}
          step={SLOT_MINUTES}
          keepOutOfRange={!keep || !value || keep(value)}
        />
      </div>
    );

    return (
    <Section n={1} emoji="⏰" title="Name, dates & time">
      {/* Everything on this step is REQUIRED, said once at the top, in two columns so a wide card isn't half empty. */}
      <div className="grid grid-cols-1 gap-y-4">
        <div className="space-y-4">
          {/* The name first: it is what the guest sees on the booking page and what every screen lists the buffet by. */}
          <div>
            <Label htmlFor="buffet-name" className="text-sm text-muted-foreground">
              Buffet name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="buffet-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sunday Brunch Buffet"
              className="mt-1"
            />
          </div>

          {/* WHICH DAYS it runs. Optional: leave the pair blank and this stays
              a template to be opened later from Open Buffets, exactly as
              before. Fill it in and saving also OPENS the buffet on every day
              in the range — one trip instead of two screens. */}
          <div>
            <Label className="text-sm text-muted-foreground">
              {t('buffet.dates.label')}
            </Label>
            <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="block text-xs text-muted-foreground">{t('buffet.dates.from')}</span>
                <Input
                  type="date" value={runFrom} min={todayISO()} className="mt-1 w-full"
                  /* A native date input only opens its picker from the small
                     icon at the end of the field, which is easy to miss and was
                     being clipped when this column was narrow. Clicking or
                     focusing anywhere in the field opens it. */
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  onFocus={(e) => e.currentTarget.showPicker?.()}
                  onChange={(e) => {
                    setRunFrom(e.target.value);
                    // A start after the chosen end clears the end, the same
                    // way the time fields above behave.
                    if (e.target.value && runTo && runTo < e.target.value) setRunTo('');
                  }} />
              </div>
              <div>
                <span className="block text-xs text-muted-foreground">{t('buffet.dates.to')}</span>
                <Input
                  type="date" value={runTo} min={runFrom || todayISO()} className="mt-1 w-full"
                  onClick={(e) => e.currentTarget.showPicker?.()}
                  onFocus={(e) => e.currentTarget.showPicker?.()}
                  onChange={(e) => setRunTo(e.target.value)} />
              </div>
            </div>

            {/* A season is decided in weeks and months, not in end dates. The
                presets set the LAST date and are then forgotten — everything
                below still edits the range itself, so a preset is a shortcut
                and never a mode to get out of. */}
            {/* Always on screen, including before a date is picked — a
                length is the thing most managers actually know ("run it for a
                month"), and hiding the shortcut until they have typed the end
                date makes it a shortcut to nothing. With no first date yet,
                pressing one starts the run TODAY. */}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">{t('buffet.dates.runFor')}</span>
              {RANGE_PRESETS.map((preset) => {
                const from = runFrom || todayISO();
                const end = presetEnd(from, preset);
                const active = !!runFrom && runTo === end;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => { if (!runFrom) setRunFrom(from); setRunTo(end); }}
                    className={`rounded-full border px-2.5 py-1 text-xs transition ${active
                      ? 'border-primary bg-primary/10 font-medium text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted'}`}
                  >
                    {t(`buffet.dates.preset.${preset.key}`)}
                  </button>
                );
              })}
            </div>

            {/* WHICH days inside the span. Every day is ticked to begin with,
                so this only asks for attention from somebody who wants a
                weekends-only or a Sunday-brunch buffet. */}
            {/* Shown whether or not dates are filled in: these are the
                CONFIGURATION the range is then read through, and a manager who
                opens this screen to set up a weekends-only buffet has to be
                able to see that it is possible before typing anything. */}
            <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">
                    {t('buffet.dates.onDays')}
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button type="button" className="text-xs text-primary hover:underline"
                      onClick={() => setRunDow(ALL_DOW)}>
                      {t('buffet.dates.everyDay')}
                    </button>
                    <span className="text-xs text-muted-foreground">·</span>
                    <button type="button" className="text-xs text-primary hover:underline"
                      onClick={() => setRunDow(WEEKDAY_DOW)}>
                      {t('buffet.dates.noWeekends')}
                    </button>
                    <span className="text-xs text-muted-foreground">·</span>
                    <button type="button" className="text-xs text-primary hover:underline"
                      onClick={() => setRunDow(WEEKEND_DOW)}>
                      {t('buffet.dates.weekendsOnly')}
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {ALL_DOW.map((n) => {
                    const on = runDow.includes(n);
                    return (
                      <button
                        key={n}
                        type="button"
                        aria-pressed={on}
                        onClick={() => toggleDow(n)}
                        className={`h-8 min-w-[2.5rem] rounded-md border px-2 text-xs font-medium transition ${on
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'border-border bg-background text-muted-foreground/70 line-through'}`}
                      >
                        {t(`buffet.dates.dow.${n}`)}
                      </button>
                    );
                  })}
                </div>

                {/* What the rule does to days ALREADY open — said here, beside
                    the chip that causes it, before Save goes and does it. */}
                {struckOpen.closes.length > 0 || struckOpen.kept.length > 0 ? (
                  <p className="mt-3 text-xs text-amber-700">
                    {struckOpen.closes.length > 0
                      ? t('buffet.dates.willClose', { n: struckOpen.closes.length })
                      : null}
                    {struckOpen.kept.length > 0
                      ? ` ${t('buffet.dates.cantClose', { n: struckOpen.kept.length })}`
                      : null}
                  </p>
                ) : null}

                {/* Skipping days OFF is the one exclusion worth naming: on a
                    public holiday much of the island is shut, and a manager
                    should not have to strike out Divali and both Eids by hand. */}
                {holidaysInSpan.length > 0 ? (
                  <label className="mt-3 flex cursor-pointer items-center gap-2 text-xs text-foreground">
                    <input
                      type="checkbox"
                      checked={skipsHolidays}
                      onChange={toggleSkipHolidays}
                      className="h-3.5 w-3.5 accent-current"
                    />
                    {t('buffet.dates.skipHolidays', { n: holidaysInSpan.length })}
                  </label>
                ) : null}

                {/* Struck-out dates, each removable. The calendar is the fast
                    way in — this is what proves the click landed, and gives a
                    way back for a day now off-screen. */}
                {runExcept.length > 0 ? (
                  <div className="mt-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {t('buffet.dates.skipped', { n: runExcept.length })}
                      </span>
                      <button type="button" className="text-xs text-primary hover:underline"
                        onClick={() => setRunExcept([])}>
                        {t('buffet.dates.clearSkipped')}
                      </button>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {runExcept.map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => toggleExcept(d)}
                          title={t('buffet.dates.putBack')}
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                        >
                          {d}
                          <X size={11} />
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
                {/* Only once there is a range to click on — otherwise it
                    points at an empty calendar. */}
                {runFrom && runTo ? (
                  <p className="mt-2 text-[11px] text-muted-foreground">
                    {t('buffet.dates.clickHint')}
                  </p>
                ) : null}
              </div>

            <p className="mt-1.5 text-xs text-muted-foreground">
              {runFrom && runTo
                ? (plannedDates.length === 0
                  ? t('buffet.dates.noneLeft')
                  : t('buffet.dates.willOpenOf', {
                    n: plannedDates.length,
                    span: dayCount(runFrom, runTo),
                  }))
                : t('buffet.dates.hint')}
            </p>
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {/* Moving the start past the chosen end clears the end - a fresh pick beats keeping an impossible window. */}
            {timeField('buffet-start', 'Starts', startTime, (v) => {
              setStartTime(v);
              if (v && endTime && toMin(endTime) <= toMin(v)) setEndTime('');
            }, hours ? hours.open : '', hours ? hours.close : '')}
            {timeField('buffet-end', 'Ends', endTime, setEndTime,
              endFloor, hours ? hours.close : '',
              (t) => !startTime || toMin(t) > toMin(startTime))}
          </div>
          <div>
            <Label htmlFor="buffet-type" className="text-sm text-muted-foreground">
              Session <span className="text-red-500">*</span>
            </Label>
            <select id="buffet-type" className={`${inputCls} mt-1`} value={buffetType}
              onChange={(e) => setBuffetType(e.target.value)}>
              {BUFFET_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        <div className="space-y-4">
          {/* What KIND of spread: not a customization, but the first thing a guest asks. */}
          {/* SITTINGS, asked here with the hours: the slots are cut from this
              very window, so one service vs a start-every-N-minutes is part
              of saying WHEN the buffet runs. Step 2 only splits the guests
              across whatever this decides. */}
          {/* Every buffet is sold in sittings — there is no One service / Sittings
              choice any more. The two numbers that CUT the window into slots are
              therefore always here and always answered, rather than hiding behind
              a switch that had to be found before they could be set. */}
          <div>
            <div className="text-sm text-muted-foreground mb-1.5">
              {t('buffet.sit.title')} <span className="text-red-500">*</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-muted-foreground">{t('buffet.sit.gap')}</span>
                <select className={`${inputCls} mt-1`} value={gapMin}
                  onChange={(e) => setGapMin(Number(e.target.value))}>
                  {GAP_CHOICES.map((m) => (
                    <option key={m} value={m}>{t('buffet.sit.min', { n: m })}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs text-muted-foreground">{t('buffet.sit.length')}</span>
                <select className={`${inputCls} mt-1`} value={sitMin}
                  onChange={(e) => setSitMin(Number(e.target.value))}>
                  {LENGTH_CHOICES.map((m) => (
                    <option key={m} value={m}>
                      {m % 60 === 0 ? t('buffet.sit.hr', { n: m / 60 })
                        : t('buffet.sit.min', { n: m })}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {sittingStarts.length > 0 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                {t('buffet.sit.count', {
                  n: sittingStarts.length,
                  first: to12h(sittingStarts[0]),
                  last: to12h(sittingStarts[sittingStarts.length - 1]),
                })}
              </p>
            )}
          </div>

          <div>
            <div className="text-sm text-muted-foreground mb-1.5">
              Type <span className="text-red-500">*</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Chip icon={Leaf} on={spread === 'VEG'} onClick={() => setSpread('VEG')}>Veg</Chip>
              <Chip icon={Utensils} on={spread === 'MIXED'} onClick={() => setSpread('MIXED')}>Non-veg</Chip>
            </div>
          </div>

          {/* HOW MANY guests, asked here and nowhere else. The day's totals
              belong beside the hours because they are the same decision —
              how big a service this is. Step 2 then only splits these across
              the sittings, so there is one place to change a number and one
              place to read it. */}
          <div>
            <div className="text-sm text-muted-foreground mb-1.5">
              {t('buffet.guests.title')} <span className="text-red-500">*</span>
            </div>
            <div className="space-y-1.5 max-w-sm">
              <NumberBox label={t('buffet.guests.adults')} value={adults}
                min={0} max={9999} onCommit={setAdults} />
              <NumberBox label={t('buffet.guests.children')} value={kidsCovers}
                min={0} max={9999} onCommit={setKidsCovers} />
              <div className="flex items-center justify-between gap-4 border-t border-border pt-1.5">
                <span className="text-sm text-muted-foreground">{t('buffet.guests.covers')}</span>
                <span className="text-sm font-semibold tabular-nums text-foreground">{covers}</span>
              </div>
            </div>
          </div>

          {/* WHEN a buffet runs is not asked here at all — neither a date
              range nor weekdays. A template says what the buffet IS; which
              days it runs on is answered by opening it onto them in Open
              Buffets, where one repeat covers up to a year. Two places to
              say it only ever disagreed, and the losing one took the buffet
              off every screen with nothing on this page to explain why. */}
        </div>
      </div>
    </Section>
    );
  };


  /* ── step 3: Seating — sitting or standing, and where ──────────────── */
  const seatingStep = () => (
    <Section n={3} emoji="🪑" title="Sitting map — the hall">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-6">
        <div>
          <div className="text-sm text-muted-foreground mb-1.5">
            How guests eat <span className="text-red-500">*</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* One word each: sitting and standing need no chip to explain them. */}
            <Chip icon={Armchair} on={serviceStyle === 'SITTING'}
              onClick={() => setServiceStyle('SITTING')}>
              Sitting
            </Chip>
            <Chip icon={Footprints} on={serviceStyle === 'STANDING'}
              onClick={() => setServiceStyle('STANDING')}>
              Standing
            </Chip>
          </div>
        </div>

        <div>
          {areaChoices.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No floor plan for this branch yet — draw one under Tables.
            </p>
          ) : (
            <>
              <div className="text-sm text-muted-foreground mb-1.5">
                Location <span className="text-red-500">*</span> <span className="text-muted-foreground/70">— pick one or more areas</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {areaChoices.map((a) => (
                  <Chip key={a.id} icon={MapPin} on={areaIds.includes(a.id)}
                    onClick={() => setAreaIds((v) => toggleIn(v, a.id))}>
                    {a.name}
                    <span className={areaIds.includes(a.id) ? 'opacity-80' : 'text-muted-foreground'}>
                      {' '}· {a.floor} · {a.tables}T
                    </span>
                  </Chip>
                ))}
              </div>
              {/* The seat count only matters, and only prints, when guests actually take a chair. */}
              {serviceStyle === 'SITTING' && (
                <p className="mt-3 text-sm text-muted-foreground">
                  {areaIds.length === 0
                    ? <>Across the whole branch: <strong className="text-foreground">{areaTables} tables
                        · {areaSeats} chairs</strong>.</>
                    : (() => {
                      /* A CHAIR IS NOT A COVER. Seats are what the room holds
                         at ONE sitting, and the room turns over: six sittings
                         of 49 chairs seat 294 people in a day. Comparing the
                         chair count straight against the whole service's
                         planned covers called a room too small whenever
                         sittings were on — which is always — and left a
                         manager hunting for a bigger hall they did not need.
                         So the line says both figures and judges on the one
                         that decides it: the day's capacity. */
                      const sittings = sittingStarts.length || 1;
                      const dayCapacity = areaSeats * sittings;
                      const enough = dayCapacity >= covers;
                      return (
                        <>
                          The picked location{pickedAreas.length === 1 ? ' holds' : 's hold'}
                          {' '}<strong className="text-foreground">{areaTables} tables · {areaSeats} chairs</strong>
                          {' — '}
                          <strong className="text-foreground">
                            {t('buffet.seatmap.perSitting', { seats: areaSeats })}
                          </strong>
                          {sittings > 1
                            ? <>, {t('buffet.seatmap.acrossSittings', { total: dayCapacity, n: sittings })}</>
                            : null}
                          {covers > 0
                            ? (
                              <span className={enough
                                ? 'text-emerald-700 font-medium'
                                : 'text-amber-600 font-medium'}>
                                {' — '}
                                {enough
                                  ? t('buffet.seatmap.enough', { covers })
                                  : t('buffet.seatmap.short', { covers })}
                              </span>
                            )
                            : '.'}
                        </>
                      );
                    })()}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* The floor plan draws ONLY the areas picked under Location above.
          With nothing picked, the seat summary may speak for the whole
          branch, but drawing every room would invite stations into areas the
          buffet never picks — and those are dropped at save — so the plan
          waits for the pick instead of guessing at it. */}
      {areaChoices.length > 0 && (
        <div className="mt-6">
          <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm text-muted-foreground">Floor plan</span>
            <span className="text-xs text-muted-foreground/80">
              — {t('buffet.plan.previewHint')}
            </span>
            {/* Placing stations is fiddly in a column this narrow, so the same
                editor opens full screen. */}
            {areaIds.length > 0 && pickedSections.length > 0 && (
              <button
                type="button"
                onClick={() => { setPlanDraft(floorLayout); setPlanExpanded(true); }}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
              >
                <Maximize2 size={13} />
                {t('buffet.plan.expand')}
              </button>
            )}
          </div>
          {areaIds.length === 0 || pickedSections.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
              Pick one or more areas under <strong className="text-foreground">Location</strong> above
              — the floor plan is drawn for the picked areas only.
            </p>
          ) : (
            /* PREVIEW only. Dragging a table around a column this narrow
               produced moves nobody intended, so the small plan shows the
               arrangement and Expand is where it is changed. */
            <BuffetFloorPlanner
              areas={pickedSections}
              dishes={allDishes}
              dishTypes={dishTypes}
              layout={floorLayout}
              onChange={setFloorLayout}
              readOnly
            />
          )}
        </div>
      )}
    </Section>
  );

  /* Just the pickable list of Buffet-type menus; picking one still imports its spread behind the scenes. */
  const menuStep = () => (
    <Section n={2} emoji="🍽️" title="Buffet menu">
      {menusLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 size={15} className="animate-spin" /> Loading menus…
        </div>
      ) : buffetMenus.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-5 text-sm text-muted-foreground">
          No buffet menus at this branch yet. Create a menu in <strong className="text-foreground">Menus</strong> and
          set its type to <strong className="text-foreground">Buffet</strong> — it will show up here.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {buffetMenus.map((m) => {
            const on = String(m.id) === String(menuId);
            return (
              <button key={m.id} type="button" disabled={importing} onClick={() => pickMenu(m)}
                className={`text-left rounded-xl border p-4 transition disabled:opacity-60 ${
                  on ? 'border-primary bg-primary/5 shadow-sm' : 'border-border bg-card hover:border-primary/50'
                }`}>
                <div className="flex items-center gap-2 font-semibold text-foreground">
                  <BookOpen size={15} className={on ? 'text-primary' : 'text-muted-foreground'} />
                  <span className="truncate">{m.name}</span>
                  {on && <Check size={14} className="ml-auto shrink-0 text-primary" strokeWidth={3} />}
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {m.section_count || 0} sections · {m.item_count || 0} dishes
                  {m.is_active === false && ' · inactive'}
                </p>
              </button>
            );
          })}
        </div>
      )}

      {importing && (
        <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={15} className="animate-spin" /> Loading that menu's dishes…
        </div>
      )}

      {/* What is actually going on the counter: unticking drops a dish from this buffet only, never from the menu. */}
      {!importing && sections.some((s) => s.items.filter(Boolean).length > 0) && (
        <div className="mt-5 border-t border-border pt-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <div className="text-sm font-semibold text-foreground">
              On the counter
              <span className="ml-2 font-normal text-muted-foreground">
                {allDishes.length} of {new Set(sections.flatMap((s) => s.items.filter(Boolean))).size} dishes
              </span>
            </div>
            {offDishes.size > 0 && (
              <button
                type="button"
                onClick={() => setOffDishes(new Set())}
                className="text-xs font-medium text-primary hover:underline"
              >
                Put all {offDishes.size} back on
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sections.filter((s) => s.items.filter(Boolean).length).map((s, si) => {
              const items = s.items.filter(Boolean);
              const on = items.filter((d) => !isOff(d)).length;
              return (
                <div key={`${s.name}-${si}`} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <span className="text-sm font-semibold text-foreground truncate">{s.name}</span>
                    <span className={`text-xs shrink-0 ${on === 0 ? 'text-amber-700' : 'text-muted-foreground'}`}>
                      {on === 0 ? 'none on' : `${on} of ${items.length}`}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {items.map((d) => (
                      <label
                        key={d}
                        className="flex items-start gap-2 rounded px-1.5 py-1 cursor-pointer hover:bg-accent/50"
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4 mt-0.5 shrink-0"
                          checked={!isOff(d)}
                          onChange={() => toggleDish(d)}
                        />
                        <span className={`text-sm min-w-0 ${
                          isOff(d) ? 'text-muted-foreground line-through' : 'text-foreground'
                        }`}>
                          {d}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {allDishes.length === 0 && (
            <p className="mt-3 text-sm text-amber-700">
              Every dish is switched off — there is nothing on the counter, so no quantities
              or costs can be worked out in the next steps.
            </p>
          )}
        </div>
      )}

      {/* The stations cooking in the hall belong with the menu - they are part of what is served. */}
      <div className="mt-5 pt-4 border-t border-border">
        <div className="text-sm text-muted-foreground mb-2">Live counters</div>
        <p className="mb-2 text-xs text-muted-foreground">
          Counters are <strong className="text-foreground">master data</strong> — defined once under
          Buffet → Live Counters: each cooks a <strong className="text-foreground">dish</strong>, its
          ingredients ticked from that dish&apos;s recipe with per-100 quantities — and offered here
          as chips. Pick one and everything comes with it; the ⚖️ Quantity per item step then plans
          the station <strong className="text-foreground">like a dish</strong> — portion and uptake
          give the station&apos;s own quantity, and the recipe derives each ingredient&apos;s from it.
        </p>
        {/* The register, as chips. Tapping picks the counter (its ingredients ride along); tapping again puts it back. */}
        <div className="flex flex-wrap gap-2">
          {masterCounters === null ? (
            <span className="text-xs text-muted-foreground">Reading the counter register…</span>
          ) : (
            <>
              {masterCounters.map((m) => (
                <Chip key={m.id} icon={Flame} on={counterSelected(m)}
                  onClick={() => toggleMasterCounter(m)}>
                  {m.name}
                </Chip>
              ))}
              <button type="button"
                onClick={() => {
                  setIngDraft(null);
                  if (dishList === null) loadDishList();
                  setCounterDraft({
                    name: '', description: '', dishId: null, ingredientIds: [], recipe: {}, saving: false, error: '',
                  });
                }}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-3 py-1 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground"
                title="Define a new live counter — it lands in the master and is picked onto this buffet">
                <Plus size={13} /> New counter
              </button>
            </>
          )}
        </div>

        {/* The one-tap master create, inline — name the counter and tick the
            ingredients it cooks through. It is a REGISTER entry: the same
            counter then appears under Settings → Master Data and on every
            other buffet's chips. */}
        {counterDraft && (
          <div className="mt-3 rounded-lg border border-primary/40 bg-primary/[0.03] p-3">
            {/* The DISH comes first — picking it names the counter and brings
                the dish's own ingredients in, every one already ticked. */}
            {dishList === null ? (
              <p className="text-xs text-muted-foreground">Reading the dish list…</p>
            ) : (
              <select value={counterDraft.dishId ?? ''}
                onChange={(e) => {
                  const dishId = e.target.value ? Number(e.target.value) : null;
                  const next = (dishList || []).find((x) => x.id === dishId);
                  const offered = dishRecipeIngredientIds(next);
                  setCounterDraft((d) => {
                    if (!d) return d;
                    const recipe = {};
                    offered.forEach((x) => { if (d.recipe?.[x]) recipe[x] = d.recipe[x]; });
                    /* The counter is NAMED by its dish; a hand-typed name is
                       left alone, blank or previous-dish names follow along. */
                    const prev = (dishList || []).find((x) => x.id === d.dishId);
                    const untouched = !d.name.trim() || (prev && d.name.trim() === prev.name);
                    return {
                      ...d, dishId,
                      name: next && untouched ? next.name : d.name,
                      ingredientIds: [...offered], recipe,
                    };
                  });
                }}
                className="block h-8 w-full max-w-[340px] rounded-md border border-border bg-background px-2 text-sm">
                <option value="">— pick the dish this counter cooks —</option>
                {[...dishList]
                  .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')))
                  .map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input value={counterDraft.name} placeholder="Name — same as the dish, filled on pick"
                onChange={(e) => setCounterDraft((d) => d && { ...d, name: e.target.value })}
                className="h-8 max-w-[240px] text-sm font-medium" />
              <Input value={counterDraft.description} placeholder="Description (optional)"
                onChange={(e) => setCounterDraft((d) => d && { ...d, description: e.target.value })}
                className="h-8 max-w-[280px] text-sm" />
            </div>
            {masterIngredients === null ? (
              <p className="mt-2 text-xs text-muted-foreground">Reading the ingredient list…</p>
            ) : (
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {/* Offered = the picked dish's recipe ingredients, plus any
                    already ticked (a fresh "+ New ingredient" lands here). */}
                {(() => {
                  const dish = (dishList || []).find((x) => x.id === counterDraft.dishId);
                  const base = dishRecipeIngredientIds(dish);
                  const have = new Set(base);
                  counterDraft.ingredientIds.forEach((x) => {
                    if (!have.has(x)) { have.add(x); base.push(x); }
                  });
                  if (base.length === 0) {
                    return (
                      <span className="text-xs text-muted-foreground">
                        {counterDraft.dishId
                          ? 'This dish has no ingredients ticked yet — add them on the dish’s Ingredients tab, or create one here.'
                          : 'Pick the dish first — its ingredients appear here, already ticked.'}
                      </span>
                    );
                  }
                  return base.map((id) => {
                    /* The register resolves the name; a row the register list
                       misses still shows via the dish's own copy. */
                    const ing = (masterIngredients || []).find((x) => x.id === id)
                      || (dish?.ingredients || []).find((x) => x.id === id);
                    if (!ing) return null;
                    const on = counterDraft.ingredientIds.includes(id);
                    return (
                      <button key={id} type="button" aria-pressed={on}
                        onClick={() => setCounterDraft((d) => d && {
                          ...d,
                          ingredientIds: on
                            ? d.ingredientIds.filter((x) => x !== id)
                            : [...d.ingredientIds, id],
                        })}
                        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${
                          on ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background text-muted-foreground hover:bg-muted'}`}>
                        {ing.name}
                      </button>
                    );
                  });
                })()}
                {/* An ingredient missing from the register is created without
                    leaving the panel — the same one-tap the dish form gives —
                    and lands in the row already ticked. */}
                {ingDraft ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Input value={ingDraft.name} placeholder="Ingredient name"
                      autoFocus
                      onChange={(e) => setIngDraft((d) => d && { ...d, name: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveIngDraft(); }
                        if (e.key === 'Escape') setIngDraft(null);
                      }}
                      className="h-7 w-44 text-xs" />
                    <Button type="button" size="sm" className="h-7 px-2 text-xs"
                      onClick={saveIngDraft}
                      disabled={ingDraft.saving || !ingDraft.name.trim()}>
                      {ingDraft.saving ? 'Adding…' : 'Add'}
                    </Button>
                    <button type="button" onClick={() => setIngDraft(null)}
                      className="text-muted-foreground hover:text-foreground" aria-label="Cancel new ingredient">
                      <X size={14} />
                    </button>
                  </span>
                ) : (
                  <button type="button"
                    onClick={() => setIngDraft({ name: '', saving: false, error: '' })}
                    className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-foreground"
                    title="Create a new ingredient in the register (Settings → Ingredients) and tick it here">
                    <Plus size={12} /> New ingredient
                  </button>
                )}
              </div>
            )}
            {ingDraft?.error && (
              <p className="mt-1.5 text-xs text-red-600">{ingDraft.error}</p>
            )}
            {/* The RECIPE, typed with the counter: what 100 kg (or 100 pcs) of its
                output takes of each ticked ingredient. The quantity step scales it
                by the station's own prep figure, so nothing is retyped per buffet. */}
            {counterDraft.ingredientIds.length > 0 && (
              <div className="mt-3 rounded-md border border-border bg-background/60 p-2">
                <div className="mb-1.5 text-xs font-medium text-foreground">
                  Recipe — per 100 kg of {counterDraft.name.trim() || 'this counter'} *
                </div>
                <div className="space-y-1.5">
                  {counterDraft.ingredientIds.map((id) => {
                    const ing = (masterIngredients || []).find((x) => x.id === id)
                      || (((dishList || []).find((x) => x.id === counterDraft.dishId)
                        ?.ingredients) || []).find((x) => x.id === id);
                    if (!ing) return null;
                    const spec = counterDraft.recipe?.[id] || {};
                    return (
                      <div key={id} className="flex items-center gap-2 text-sm">
                        <span className="w-40 truncate">{ing.name}</span>
                        <input type="number" min="0" step="0.5" value={spec.qty ?? ''}
                          placeholder="—"
                          onChange={(e) => setCounterDraft((d) => d && ({
                            ...d,
                            recipe: {
                              ...d.recipe,
                              [id]: { unit: spec.unit || 'kg', ...d.recipe?.[id],
                                qty: e.target.value === '' ? '' : Number(e.target.value) },
                            },
                          }))}
                          className="h-8 w-24 rounded border border-border bg-background px-2 text-right text-sm tabular-nums" />
                        <select value={spec.unit || 'kg'}
                          onChange={(e) => setCounterDraft((d) => d && ({
                            ...d,
                            recipe: { ...d.recipe, [id]: { ...d.recipe?.[id], unit: e.target.value } },
                          }))}
                          className="h-8 rounded border border-border bg-background px-1.5 text-xs text-muted-foreground">
                          {/* Weighed or counted only — no litres on a counter's recipe. */}
                          <option value="kg">kg</option>
                          <option value="pcs">pcs</option>
                        </select>
                        <span className="text-xs text-muted-foreground">per 100</span>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Example: 100 kg of Pasta takes 15 kg raw pasta and 20 kg white sauce — a
                  buffet prepping 40 kg derives 6 kg and 8 kg on its own.
                  <strong className="text-foreground"> Every ticked ingredient needs its
                  amount</strong> — the counter won&apos;t save without them.
                </p>
              </div>
            )}
            {counterDraft.error && (
              <p className="mt-2 text-xs text-red-600">{counterDraft.error}</p>
            )}
            <div className="mt-3 flex items-center gap-2">
              <Button type="button" size="sm" onClick={saveCounterDraft}
                disabled={counterDraft.saving || !counterDraft.name.trim()}>
                {counterDraft.saving ? 'Creating…' : 'Create & pick'}
              </Button>
              <Button type="button" variant="outline" size="sm"
                onClick={() => setCounterDraft(null)} disabled={counterDraft.saving}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* The picked counters, each showing ALL its ingredients straight away
            — copied from the master at pick time, so a later master edit
            never rewrites a saved buffet. A counter is put back whole; which
            ingredients it cooks is the register's decision, not a day's. */}
        {counters.length > 0 && (
          <div className="mt-3 space-y-3">
            {counters.map((c, i) => (
              <div key={`${c.name}-${i}`} className="rounded-lg border border-border bg-secondary/30 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{c.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {c.ingredients.length
                      ? `${c.ingredients.length} ingredient${c.ingredients.length === 1 ? '' : 's'}`
                      : 'no ingredients in the master'}
                  </span>
                  {!c.master_id && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700"
                      title="Saved before counters became master data — it keeps working; recreate it in the register to share it across buffets">
                      not in the register
                    </span>
                  )}
                  <button type="button" onClick={() => removeCounter(i)}
                    className="ml-auto inline-flex items-center gap-1 p-1 text-xs text-red-500 hover:text-red-700"
                    aria-label={`Take ${c.name || 'this counter'} off this buffet`}>
                    <Trash2 size={14} /> Remove
                  </button>
                </div>
                {c.ingredients.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {c.ingredients.map((r, j) => (
                      <span key={`${r.name}-${j}`}
                        className="rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground">
                        {r.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );

  /* Live counters, planned like dishes. A station with a RECIPE in the
     register is a dish in its own right: Pasta takes a portion per guest, a
     kid portion and an uptake exactly like the rows above, and the quantity
     that gives is what scales the recipe — 100 kg of pasta at "15 kg raw
     pasta, 20 kg white sauce per 100" derives its ingredient quantities
     instead of having them retyped. A counter with no recipe keeps the old
     hand-typed rows. WHICH ingredients a counter cooks — and the ratio —
     is the register's decision; this step decides how much of the station. */
  /* RETIRED from the stepper: portions live on the register now (Buffet →
     Dish Quantities). Kept only as reference while the register beds in. */
  // eslint-disable-next-line no-unused-vars
  const qtyStep = () => (
    <Section n={5} emoji="⚖️" title="Quantity per item">
      {/* The party this sheet is prepped FOR, recapped where the quantities are typed, with the buffered total beside it. */}
      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <span className="text-sm text-muted-foreground">Prepping for</span>
        <span className="flex items-baseline gap-2">
          <strong className="text-2xl font-bold leading-none tabular-nums text-primary">
            {covers.toLocaleString('en-MU')}
          </strong>
          <span className="text-sm font-medium text-foreground">covers</span>
        </span>
        <span className="text-sm text-muted-foreground tabular-nums">
          {adults.toLocaleString('en-MU')} adults · {kidsCovers.toLocaleString('en-MU')} kids
          {/* The counts live on step 1 now, so this jumps there rather than to
              step 2, which only splits them. */}
          <button type="button"
            onClick={() => goTo(steps.findIndex((s) => s.key === 'time'))}
            className="ml-2 text-primary hover:underline">change in ⏰ Name &amp; time</button>
        </span>
        {buffer > 0 && (
          <span className="text-sm text-muted-foreground">
            + {buffer}% safety buffer → the sheet cooks for{' '}
            <strong className="tabular-nums text-foreground">
              {Math.ceil(covers * (1 + buffer / 100)).toLocaleString('en-MU')}
            </strong>
          </span>
        )}
      </div>
      <div className="space-y-3 mb-2 max-w-md">
        <NumberBox label="Safety buffer" value={buffer} min={0} max={100} suffix="%"
          onCommit={setBuffer} />
      </div>
      {allDishes.length === 0 && counters.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No items yet — pick a buffet menu, or list courses by hand.
        </p>
      ) : (() => {
        /* Rows in the order the counter is laid out, course by course, since the chef preps by course. */
        const seen = new Set();
        const groups = sections
          .map((s, i) => ({
            name: (s.name || '').trim() || `Course ${i + 1}`,
            items: (s.items || []).filter(Boolean).filter((d) => {
              if (offDishes.has(d) || seen.has(d)) return false;
              seen.add(d);
              return true;
            }),
          }))
          .filter((g) => g.items.length);

        /* Weighed and counted items total separately, each showing what guests
           need and what the kitchen preps. Live counters count their OWN
           output in — their ingredients don't, being already inside it. */
        const totalKg = allDishes
          .filter((d) => unitFor(d) === 'g')
          .reduce((n, d) => n + qtyFor(d), 0)
          + counters.filter((c) => c.unit !== 'pcs').reduce((n, c) => n + counterQty(c), 0);
        const totalPcs = allDishes
          .filter((d) => unitFor(d) === 'pcs')
          .reduce((n, d) => n + qtyFor(d), 0)
          + counters.filter((c) => c.unit === 'pcs').reduce((n, c) => n + counterQty(c), 0);
        const neededKg = allDishes
          .filter((d) => unitFor(d) === 'g')
          .reduce((n, d) => n + neededQty(d), 0)
          + counters.filter((c) => c.unit !== 'pcs').reduce((n, c) => n + counterQty(c, false), 0);
        const neededPcs = allDishes
          .filter((d) => unitFor(d) === 'pcs')
          .reduce((n, d) => n + neededQty(d), 0)
          + counters.filter((c) => c.unit === 'pcs').reduce((n, c) => n + counterQty(c, false), 0);

        const row = (d) => {
          const p = planFor(d);
          const u = unitFor(d);
          const qty = qtyFor(d);
          const needed = neededQty(d);
          return (
            <React.Fragment key={d}>
            <tr className="border-b border-border">
              <td className="py-2 text-foreground">
                <span className="inline-flex items-center gap-2">
                  {d}
                  {/* What past services did with this dish - planned vs eaten, day by day and buffet by buffet. */}
                  <button type="button"
                    onClick={() => openDishHistory(d)}
                    aria-expanded={dishHistOpen === d}
                    title={`How much ${d} past services planned, and how much actually went`}
                    className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium transition ${
                      dishHistOpen === d
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/40'}`}>
                    <TrendingUp size={11} /> past
                  </button>
                </span>
              </td>
              <td className="py-2 whitespace-nowrap">
                {u === 'g' ? (
                  <input type="number" min="10" step="10" value={p.portion_g}
                    onChange={(e) => setPlanField(d, 'portion_g', Number(e.target.value) || 0)}
                    className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                ) : (
                  <input type="number" min="1" step="1" value={adultPortionFor(d)}
                    onChange={(e) => setPlanField(d, 'portion_pcs', wholePcs(e.target.value))}
                    className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                )}
                {/* Weighed or counted — per dish, saved with the plan. */}
                <select value={u} title="How this item is measured"
                  onChange={(e) => setPlanField(d, 'unit', e.target.value)}
                  className="ml-1.5 rounded border border-border bg-background px-1.5 py-1 text-xs text-muted-foreground">
                  <option value="g">g</option>
                  <option value="pcs">pcs</option>
                </select>
              </td>
              {/* Kid portion, where ZERO says "children don't touch this" - one number carrying both amount and exclusion. */}
              <td className="py-2 whitespace-nowrap">
                <input
                  type="number"
                  min="0"
                  step={u === 'g' ? 10 : 1}
                  value={kidPortionFor(d)}
                  title={`What a child is served. Defaults to ${
                    u === 'g'
                      ? `half the adult portion (${Math.round((Number(p.portion_g) || 0) * KID_FACTOR)} g)`
                      /* Counted items round to the nearest whole piece, never below one, so 0 stays the deliberate "not served". */
                      : `the nearest whole piece (${wholePcs(adultPortionFor(d) * KID_FACTOR)} pcs)`
                  }; type over it to set your own, or 0 if kids are not served this.`}
                  onChange={(e) => setPlanField(
                    d,
                    u === 'g' ? 'kid_portion_g' : 'kid_portion_pcs',
                    e.target.value === '' ? null
                      : (u === 'g' ? Number(e.target.value) : wholePcs(e.target.value)),
                  )}
                  className="w-20 rounded border border-border bg-background px-2 py-1 text-sm"
                />
                <span className="ml-1 text-xs text-muted-foreground">{u}</span>
              </td>
              {/* The % rides ON the same line as its number, or it wraps and a stray "%" reads as a broken row. */}
              <td className="py-2 whitespace-nowrap">
                <input type="number" min="0" max="100" step="5" value={p.uptake_pct}
                  onChange={(e) => setPlanField(d, 'uptake_pct', Number(e.target.value) || 0)}
                  className="w-16 rounded border border-border bg-background px-2 py-1 text-sm" />
                <span className="ml-1 text-xs text-muted-foreground">%</span>
              </td>
              <td className="py-2 whitespace-nowrap">
                <input type="number" min="0" step="10" value={p.cost_per_kg ?? ''}
                  placeholder="—"
                  onChange={(e) => setPlanField(d, 'cost_per_kg', e.target.value === '' ? null : Number(e.target.value))}
                  className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                <span className="ml-1 text-xs text-muted-foreground">per {u === 'g' ? 'kg' : 'pc'}</span>
              </td>
              {/* The guests' own demand with the buffer off, read-only, since the editable number is the prep figure beside it. */}
              <td className="py-2 text-right text-sm tabular-nums whitespace-nowrap text-muted-foreground">
                {u === 'pcs' ? needed : Math.round(needed * 10) / 10}
                <span className="ml-1 text-xs">{u === 'pcs' ? 'pcs' : 'kg'}</span>
              </td>
              <td className="py-2">
                <div className="flex items-center justify-end gap-1.5">
                  {/* Editable both ways: typing a quantity re-derives the portion, so the covers figure keeps scaling it. */}
                  <QtyInput
                    value={u === 'pcs' ? qty : Math.round(qty * 10) / 10}
                    step={u === 'pcs' ? 1 : 0.5}
                    onCommit={(v) => applyQtyEdit(d, v)}
                    className="w-24 rounded border border-border px-2 py-1 text-sm text-right font-semibold tabular-nums bg-background text-foreground"
                  />
                  <span className="text-xs text-muted-foreground w-6">{u === 'pcs' ? 'pcs' : 'kg'}</span>
                </div>
              </td>
              {/* What that quantity costs - the Cost column times the qty, repricing as a portion or uptake edit lands. */}
              <td className="py-2 text-right text-sm tabular-nums whitespace-nowrap text-muted-foreground"
                title={Number(p.cost_per_kg) > 0 ? undefined : 'Fill in the Cost column to price this row'}>
                {Number(p.cost_per_kg) > 0 ? rupees(qty * Number(p.cost_per_kg)) : '—'}
              </td>
            </tr>
            {dishHistOpen === d && (
              <tr className="border-b border-border bg-muted/30">
                <td colSpan={8} className="py-3 px-2">
                  {!dishHist[d] ? (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 size={14} className="animate-spin" /> Reading past services…
                    </p>
                  ) : dishHist[d].length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {d} has not been on a counter before — no history yet.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      <table className="text-sm">
                        <thead>
                          <tr className="text-left text-xs text-muted-foreground">
                            <th className="pr-6 py-1 font-medium">Day</th>
                            <th className="pr-6 py-1 font-medium">Buffet</th>
                            <th className="pr-6 py-1 font-medium text-right">Planned</th>
                            <th className="pr-6 py-1 font-medium text-right">Eaten</th>
                            <th className="py-1" />
                          </tr>
                        </thead>
                        <tbody>
                          {dishHist[d].map((r) => (
                            <tr key={`${r.date}-${r.buffet_id}`}>
                              <td className="pr-6 py-1 whitespace-nowrap">{r.date}</td>
                              <td className="pr-6 py-1">{r.buffet_name}</td>
                              <td className="pr-6 py-1 text-right tabular-nums">
                                {r.planned_qty != null ? `${r.planned_qty} ${r.unit}` : '—'}
                              </td>
                              {/* Eaten above plan reads amber: the pan ran past what was prepped, the one row worth spotting at a glance. */}
                              <td className={`pr-6 py-1 text-right tabular-nums ${
                                r.planned_qty != null && r.consumed_qty > r.planned_qty
                                  ? 'text-amber-600 font-medium' : ''}`}>
                                {r.consumed_qty} {r.unit}
                              </td>
                              <td className="py-1">
                                <button type="button"
                                  onClick={() => setDishChart(
                                    dishChart && dishChart.date === r.date && dishChart.buffetId === r.buffet_id
                                      ? null
                                      : { item: d, date: r.date, buffetId: r.buffet_id })}
                                  className="text-xs text-primary hover:underline">
                                  {dishChart && dishChart.date === r.date && dishChart.buffetId === r.buffet_id
                                    ? 'hide' : 'more info'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {dishChart && dishChart.item === d && (
                        <div className="max-w-xl rounded-lg border border-border bg-card p-3">
                          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            {d} on {dishChart.date} — how it went down
                          </div>
                          {dishChartSeries === null ? (
                            <p className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Loader2 size={14} className="animate-spin" /> Drawing…
                            </p>
                          ) : dishChartSeries.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No readings were logged that day — the counter was not counted.
                            </p>
                          ) : (
                            <MiniLine
                              points={dishChartSeries.map((pt) => ({
                                x: pt.label, y: pt.total, sub: `+${pt.qty} ${pt.unit} this reading`,
                              }))}
                              yLabel={dishChartSeries[0].unit}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            )}
            </React.Fragment>
          );
        };

        return (
          <div className="overflow-x-auto">
            {/* Full width: the prep sheet is the whole point of this step, so it uses the whole screen. */}
            <table className="w-full text-sm
              [&>thead>tr>th]:pr-6 [&>tbody>tr>td]:pr-6 [&>tfoot>tr>td]:pr-6
              [&>thead>tr>th:last-child]:pr-0 [&>tbody>tr>td:last-child]:pr-0
              [&>tfoot>tr>td:last-child]:pr-0">
              <thead>
                <tr className="text-left text-muted-foreground border-b border-border">
                  <th className="py-2 font-medium">Item</th>
                  <th className="py-2 font-medium whitespace-nowrap">Portion / adult</th>
                  <th className="py-2 font-medium whitespace-nowrap">Portion / kid</th>
                  <th className="py-2 font-medium">
                    {/* The explanation floats over the table on hover rather than unfolding a row; a click pins it for touch. */}
                    <span className="group relative inline-flex items-center gap-1.5">
                      Uptake
                      <button
                        type="button"
                        onClick={() => setShowUptakeHelp((v) => !v)}
                        aria-expanded={showUptakeHelp}
                        aria-controls="uptake-help"
                        title="What does uptake mean?"
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                          showUptakeHelp
                            ? 'border-primary bg-primary/10 text-primary'
                            : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        <Info size={12} />
                        <span className="sr-only">What does uptake mean?</span>
                      </button>
                      <span
                        id="uptake-help"
                        role="tooltip"
                        className={`absolute left-1/2 top-full z-20 mt-2 w-[26rem] max-w-[80vw] -translate-x-1/2
                          rounded-xl border border-border bg-card p-4 text-left text-sm font-normal
                          normal-case tracking-normal text-foreground shadow-lg ${
                          showUptakeHelp
                            ? 'block'
                            : 'hidden group-hover:block group-focus-within:block'
                        }`}
                      >
                        <span className="block font-semibold">
                          Uptake is the share of your guests who actually take this item.
                        </span>
                        <span className="mt-1.5 block text-muted-foreground">
                          Nobody at a buffet eats everything. At{' '}
                          <strong className="text-foreground">{covers} covers</strong>, an uptake of{' '}
                          <strong className="text-foreground">80%</strong> plans for about{' '}
                          <strong className="text-foreground">{Math.round(covers * 0.8)} guests</strong>{' '}
                          putting it on their plate — so it is what stops you cooking a full pan of
                          every dish on the counter.
                        </span>
                        <span className="mt-2 block rounded-lg bg-secondary/60 px-3 py-2 font-mono text-xs text-muted-foreground">
                          covers × uptake × portion, + {buffer}% safety buffer = qty to prep
                        </span>
                        <span className="mt-2 block text-muted-foreground">
                          A staple everyone takes (rice, naan, the salad) sits near{' '}
                          <strong className="text-foreground">90–100%</strong>; one of several competing
                          mains around <strong className="text-foreground">40–60%</strong>; a niche
                          dessert lower still. Watch what the day's{' '}
                          <strong className="text-foreground">consumption readings</strong> say was really
                          eaten and correct these next service.
                        </span>
                        <span className="mt-2 block text-xs text-muted-foreground">
                          Changing uptake moves the suggested <em>Qty to prep</em> — and typing over
                          that quantity yourself always wins.
                        </span>
                      </span>
                    </span>
                  </th>
                  <th className="py-2 font-medium whitespace-nowrap">Cost (Rs)</th>
                  <th className="py-2 font-medium text-right whitespace-nowrap"
                    title="covers × uptake × portion — the guests' own demand, before the safety buffer">
                    Qty needed
                  </th>
                  <th className="py-2 font-medium text-right whitespace-nowrap"
                    title={`Qty needed + ${buffer}% safety buffer — counted items round UP to whole pieces. This is what the kitchen cooks; type over it and your figure wins.`}>
                    Qty to prep{buffer > 0 ? ` (+${buffer}%)` : ''}
                  </th>
                  <th className="py-2 font-medium text-right whitespace-nowrap"
                    title="Qty to prep × its cost — per kg for weighed items, per piece for counted ones">
                    Cost to prep
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <React.Fragment key={g.name}>
                    <tr className="bg-secondary/50">
                      <td colSpan={8} className="py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {g.name}
                        <span className="ml-2 font-normal normal-case tracking-normal">· {g.items.length} item{g.items.length === 1 ? '' : 's'}</span>
                      </td>
                    </tr>
                    {g.items.map(row)}
                  </React.Fragment>
                ))}
                {/* Live counters ride IN the sheet, each heading its own group:
                    the station row plans and prices exactly like a dish (its
                    own cost per kg/pc, or the Catered-at lump sum on the
                    header), and its ingredient rows carry QUANTITIES ONLY —
                    derived from the per-100 recipe, or typed where none is. */}
                {counters.map((c, i) => (
                  <React.Fragment key={`lc-${c.name}-${i}`}>
                    <tr className="bg-secondary/50">
                      <td colSpan={8} className="py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <span className="flex w-full items-center gap-2">
                          {c.name || 'Unnamed counter'}
                          <span className="font-normal normal-case tracking-normal">
                            · live counter{c.ingredients.length > 0 && <> · {c.ingredients.length} ingredient{c.ingredients.length === 1 ? '' : 's'}</>}
                          </span>
                          {/* A lump sum for the whole station — the hired kulfi cart. Typed, it beats the per-kg cost. */}
                          <label className="ml-auto inline-flex items-center gap-1.5 font-normal normal-case tracking-normal">
                            Catered at Rs
                            <input type="text" inputMode="decimal" value={c.total_amount}
                              placeholder="—"
                              title="One amount for the whole station — it wins over the row's per-kg cost"
                              onChange={(e) => setCounter(i, { total_amount: cleanMoney(e.target.value) })}
                              className="w-24 rounded border border-border bg-background px-2 py-1 text-sm text-right tabular-nums" />
                          </label>
                        </span>
                      </td>
                    </tr>
                    <tr className="border-b border-border">
                      <td className="py-2 font-semibold text-foreground whitespace-nowrap">
                        {c.name || 'Unnamed counter'}
                        <span className="ml-2 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">station</span>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        {c.unit === 'pcs' ? (
                          <input type="number" min="1" step="1" value={counterAdultPortion(c)}
                            onChange={(e) => setCounter(i, { portion_pcs: wholePcs(e.target.value) })}
                            className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                        ) : (
                          <input type="number" min="10" step="10" value={c.portion_g}
                            onChange={(e) => setCounter(i, { portion_g: Number(e.target.value) || 0 })}
                            className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                        )}
                        <select value={c.unit} title="How the station's output is measured"
                          onChange={(e) => setCounter(i, { unit: e.target.value })}
                          className="ml-1.5 rounded border border-border bg-background px-1.5 py-1 text-xs text-muted-foreground">
                          <option value="g">g</option>
                          <option value="pcs">pcs</option>
                        </select>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <input type="number" min="0" step={c.unit === 'pcs' ? 1 : 10}
                          value={counterKidPortion(c)}
                          title="What a child is served of the station's output. Defaults to half the adult portion; 0 if kids are not served this."
                          onChange={(e) => setCounter(i, c.unit === 'pcs'
                            ? { kid_portion_pcs: e.target.value === '' ? null : wholePcs(e.target.value) }
                            : { kid_portion_g: e.target.value === '' ? null : Number(e.target.value) })}
                          className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                        <span className="ml-1 text-xs text-muted-foreground">{c.unit}</span>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <input type="number" min="0" max="100" step="5" value={c.uptake_pct}
                          onChange={(e) => setCounter(i, { uptake_pct: Number(e.target.value) || 0 })}
                          className="w-16 rounded border border-border bg-background px-2 py-1 text-sm" />
                        <span className="ml-1 text-xs text-muted-foreground">%</span>
                      </td>
                      <td className="py-2 whitespace-nowrap">
                        <input type="number" min="0" step="10" value={c.cost_per_unit}
                          placeholder="—"
                          title="What a kg (or a piece) of the station's output costs — typed here like any dish, never added up from its ingredients"
                          onChange={(e) => setCounter(i, { cost_per_unit: e.target.value === '' ? '' : Number(e.target.value) })}
                          className="w-20 rounded border border-border bg-background px-2 py-1 text-sm" />
                        <span className="ml-1 text-xs text-muted-foreground">per {c.unit === 'pcs' ? 'pc' : 'kg'}</span>
                      </td>
                      <td className="py-2 text-right text-sm tabular-nums whitespace-nowrap text-muted-foreground">
                        {c.unit === 'pcs' ? counterQty(c, false) : Math.round(counterQty(c, false) * 10) / 10}
                        <span className="ml-1 text-xs">{c.unit === 'pcs' ? 'pcs' : 'kg'}</span>
                      </td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1.5">
                          <QtyInput
                            value={c.unit === 'pcs' ? counterQty(c) : Math.round(counterQty(c) * 10) / 10}
                            step={c.unit === 'pcs' ? 1 : 0.5}
                            onCommit={(v) => applyCounterQtyEdit(i, c, v)}
                            className="w-24 rounded border border-border px-2 py-1 text-sm text-right font-semibold tabular-nums bg-background text-foreground" />
                          <span className="text-xs text-muted-foreground w-6">{c.unit === 'pcs' ? 'pcs' : 'kg'}</span>
                        </div>
                      </td>
                      <td className="py-2 text-right text-sm tabular-nums whitespace-nowrap text-muted-foreground"
                        title={counterCost(c) > 0 ? undefined : 'Type the station’s cost per kg, or a Catered-at amount on its header'}>
                        {counterCost(c) > 0 ? rupees(counterCost(c)) : '—'}
                      </td>
                    </tr>
                    {c.ingredients.map((r, j) => {
                      const hasRatio = Number(r.qty_per_100) > 0;
                      const ingNeeded = hasRatio ? counterIngDerived(c, r, false) : null;
                      const ingPrep = hasRatio ? counterIngDerived(c, r) : null;
                      return (
                        <tr key={`${r.name}-${j}`} className="border-b border-border/60">
                          <td className="py-1.5 pl-4 text-muted-foreground whitespace-nowrap">· {r.name}</td>
                          <td className="py-1.5 text-xs tabular-nums text-muted-foreground whitespace-nowrap"
                            title={hasRatio
                              ? `The register's recipe: ${r.qty_per_100} ${r.unit} of ${r.name} per 100 ${c.unit === 'pcs' ? 'pcs' : 'kg'} of ${c.name}`
                              : 'No ratio in the register for this ingredient — its quantity is typed by hand'}>
                            {hasRatio ? <>{r.qty_per_100} {r.unit} <span className="opacity-70">/ 100</span></> : '—'}
                          </td>
                          <td className="py-1.5 text-xs text-muted-foreground">—</td>
                          <td className="py-1.5 text-xs text-muted-foreground">—</td>
                          <td className="py-1.5 text-xs text-muted-foreground"
                            title="Ingredient rows carry quantities only — the station is costed on its own row">—</td>
                          <td className="py-1.5 text-right text-sm tabular-nums whitespace-nowrap text-muted-foreground">
                            {hasRatio ? <>{ingNeeded}<span className="ml-1 text-xs">{r.unit}</span></> : '—'}
                          </td>
                          <td className="py-1.5 text-right whitespace-nowrap">
                            {hasRatio ? (
                              /* DERIVED, deliberately not typable: the ratio is the register's
                                 answer and the station's qty is the knob — change either there. */
                              <span className="text-sm font-semibold tabular-nums text-foreground"
                                title={`${c.name} at ${c.unit === 'pcs' ? counterQty(c) : Math.round(counterQty(c) * 10) / 10} ${c.unit === 'pcs' ? 'pcs' : 'kg'} × ${r.qty_per_100} ${r.unit} per 100`}>
                                {ingPrep}<span className="ml-1 text-xs font-normal text-muted-foreground">{r.unit}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5">
                                <QtyInput value={r.qty} step={r.unit === 'pcs' ? 1 : 0.5}
                                  onCommit={(v) => setCounterIng(i, j, { qty: Number(v) > 0 ? Number(v) : '' })}
                                  className="w-20 rounded border border-border px-2 py-1 text-sm text-right font-semibold tabular-nums bg-background text-foreground" />
                                <select value={r.unit} title="How this ingredient is measured"
                                  onChange={(e) => setCounterIng(i, j, { unit: e.target.value })}
                                  className="rounded border border-border bg-background px-1.5 py-1 text-xs text-muted-foreground">
                                  {/* Weighed or counted only — no litres on a counter row. */}
                                  <option value="kg">kg</option>
                                  <option value="pcs">pcs</option>
                                </select>
                              </span>
                            )}
                          </td>
                          <td className="py-1.5 text-right text-xs text-muted-foreground">—</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td className="py-2 text-sm font-semibold text-foreground" colSpan={5}>Total</td>
                  {/* Needed vs prepped, footed like the rows above, with the difference between the cells being the buffer. */}
                  <td className="py-2 text-right text-sm font-semibold text-muted-foreground tabular-nums whitespace-nowrap">
                    {[neededKg > 0 ? kg(neededKg) : null,
                      neededPcs > 0 ? `${Math.ceil(neededPcs)} pcs` : null]
                      .filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="py-2 text-right text-sm font-bold text-foreground tabular-nums whitespace-nowrap">
                    {[totalKg > 0 ? kg(totalKg) : null,
                      totalPcs > 0 ? `${Math.ceil(totalPcs)} pcs` : null]
                      .filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="py-2 text-right text-sm font-bold text-foreground tabular-nums whitespace-nowrap"
                    title={costedDishes.length < allDishes.length
                      ? `Only ${costedDishes.length} of ${allDishes.length} items carry a cost — the real figure is higher`
                      : 'Every item costed'}>
                    {/* Dishes AND live counters — the whole sheet's figure, the same prepCost the price step reads. */}
                    {prepCost > 0 ? rupees(prepCost) : '—'}
                    {prepCost > 0 && costedDishes.length < allDishes.length && (
                      <span className="ml-1 align-middle text-[11px] font-normal text-amber-700">
                        ({costedDishes.length}/{allDishes.length} costed)
                      </span>
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })()}
    </Section>
  );

  const priceStep = () => {
    /* The prices themselves. What a cover COSTS and what the service earns
       are no longer asked here — this step is what you charge. */
    // The ticked taxes as one label, so each sitting row can name what its figure carries.
    const taxNames = chosenTaxes.map((x) => x.name).join(' + ');


    return (
      <Section n={4} emoji="💰" title="Pricing">
        {/* Two columns on a wide screen, with reading order preserved: costs and margin left, prices and totals right. */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-start [&>*]:min-w-0">

          {/* 3 - what you actually charge, with each price beside the margin it earns rather than in a stat row below. */}
          <div className="rounded-xl border border-border p-4">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Your prices
            </div>
            {/* How the price behaves, chosen before the numbers: fixed is the whole story, dynamic lets the engine move it. */}
            <div className="mb-3 flex gap-2">
              {[
                ['FIXED', 'Fixed price', 'One adult and one kids price, charged at all times — pricing rules are ignored.'],
                ['DYNAMIC', 'Dynamic pricing', 'A base price the pricing rules may move — weekends, happy hours, occupancy…'],
              ].map(([key, label, tip]) => (
                <button
                  key={key} type="button" title={tip}
                  onClick={() => setPricingMode(key)}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition-colors ${
                    pricingMode === key
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-secondary/60'
                  }`}
                >
                  {pricingMode === key && <Check size={13} className="mr-1 inline" />}
                  {label}
                </button>
              ))}
            </div>
            {/* The two per-head prices, typed in BOTH modes: fixed charges
                them as they stand; dynamic hands them to the rules engine as
                the base its rules move. The rules themselves live on the
                Dynamic pricing rules screen — no price matrix here. */}
            <Row
              divide
              label={<>{pricingMode === 'DYNAMIC' ? 'Base adult price' : 'Adult price'} <span className="text-red-500">*</span></>}
              hint={margin(wd) != null && fc ? `Margin ${margin(wd)}% at ${rupees(fc)} food cost` : null}
            >
              <Input
                id="buffet-weekday" type="text" inputMode="decimal" value={weekday}
                onChange={(e) => applyAdultPrice(cleanMoney(e.target.value))}
                className="w-36 text-right tabular-nums"
              />
            </Row>
            <Row
              divide
              label={<>{pricingMode === 'DYNAMIC' ? 'Base kids price' : 'Kids price'} <span className="text-red-500">*</span></>}
            >
              <Input
                id="buffet-kids-price" type="text" inputMode="decimal" value={kidPrice}
                onChange={(e) => applyKidPrice(cleanMoney(e.target.value))}
                className="w-36 text-right tabular-nums"
              />
            </Row>
            {pricingMode === 'FIXED' && (buffet?.price_rules?.length || 0) > 0 && (
              <p className="mb-2 mt-1 text-xs text-amber-700">
                {buffet.price_rules.length} pricing rule{buffet.price_rules.length === 1 ? ' stays' : 's stay'} saved
                but {buffet.price_rules.length === 1 ? 'is' : 'are'} <strong>not applied</strong> while
                fixed pricing is on — switch back to dynamic and they take over again.
              </p>
            )}
            {pricingMode === 'DYNAMIC' && (
              <p className="mb-2 mt-1 text-xs text-muted-foreground">
                These are the base prices. Dynamic pricing is shaped on the rules screen —
                weekends, happy hours, occupancy — and its rules move them.
              </p>
            )}
            {/* Tax on the ticket, shared by both modes; the backend computes
                the inclusive figure for every screen. Asked ALWAYS, including
                when the restaurant has no tax rates yet: the block used to
                vanish entirely in that case, so "is Rs 210 with or without
                VAT?" simply went unanswered and unasked, and the first
                anybody heard of it was a bill. With no rates to tick it says
                so, and points at the screen that makes them. */}
            <div className="border-t border-border/60 pt-3 pb-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t('buffet.tax.title')}
              </div>

              {/* What the typed price MEANS, asked before the taxes, since it changes what every number under it says. */}
              <div className="mb-2 mt-1.5 inline-flex rounded-lg border border-border bg-background p-0.5">
                {[[false, t('buffet.tax.excl')], [true, t('buffet.tax.incl')]].map(([v, label]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setTaxInclusive(v)}
                    aria-pressed={taxInclusive === v}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                      taxInclusive === v
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-muted'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                {taxInclusive ? t('buffet.tax.inclNote') : t('buffet.tax.exclNote')}
              </p>

              {taxLabels.length === 0 ? (
                <p className="text-xs text-amber-700">
                  {t('buffet.tax.noRates')}{' '}
                  <Link
                    to="/settings/tax-rates"
                    className="font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {t('buffet.tax.setUp')}
                  </Link>
                </p>
              ) : (
                <>
                  {/* WHICH tax — the same question either way round, but not
                      the same sentence: one names what is already inside the
                      price, the other what gets added to it. */}
                  <div className="mb-1.5 text-xs font-medium text-foreground">
                    {taxInclusive ? t('buffet.tax.pickIncl') : t('buffet.tax.pickExcl')}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1">
                    {taxLabels.map((tl) => (
                      <label key={tl.id} className="flex cursor-pointer items-center gap-1.5 text-sm text-foreground">
                        <input
                          type="checkbox"
                          checked={taxIds.includes(tl.id)}
                          onChange={() => setTaxIds((prev) => (
                            prev.includes(tl.id) ? prev.filter((x) => x !== tl.id) : [...prev, tl.id]))}
                        />
                        {tl.name} · {tl.rate_type === 'FLAT' ? rupees(tl.rate) : `${tl.rate}%`}
                        {tl.is_compound ? ` ${t('buffet.tax.compound')}` : ''}
                      </label>
                    ))}
                  </div>

                  {/* Which tax to pick is only a real question once it changes a number, so the answer prints as one is ticked. */}
                  {taxIds.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700">
                      {t('buffet.tax.noneTicked')}
                    </p>
                  )}
                  {taxIds.length > 0 && Number(weekday) > 0 && (
                    <div className="mt-2 rounded-lg border border-border bg-background p-2.5">
                      {[['adult', weekday], ['kid', kidPrice]]
                        .filter(([, v]) => v !== '' && Number(v) > 0)
                        .map(([who, v]) => {
                          const bd = taxOn(v);
                          return (
                            <div key={who} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-0.5">
                              <span className="text-sm text-muted-foreground">
                                {who === 'adult' ? t('buffet.tax.adultPays') : t('buffet.tax.kidPays')}
                              </span>
                              <strong className="tabular-nums text-base text-foreground">
                                {rupees(bd.gross)}
                              </strong>
                              <span className="text-xs text-muted-foreground">
                                {t('buffet.tax.breakdown', { net: rupees(bd.net), tax: rupees(bd.tax) })}
                                {bd.parts.length > 1 && ` (${bd.parts.map((x) => x.name).join(' + ')})`}
                              </span>
                            </div>
                          );
                        })}
                      {pricingMode === 'DYNAMIC' && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t('buffet.tax.rulesCarry')}
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

          </div>

          {onOpenRules && pricingMode !== 'FIXED' && (
            <button
              type="button"
              onClick={onOpenRules}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:opacity-80"
            >
              <SlidersHorizontal size={14} />
              Dynamic pricing rules{buffet?.price_rules?.length ? ` · ${buffet.price_rules.length} active` : ''} →
            </button>
          )}
        </div>

        {/* ---- What each SITTING charges -------------------------------
            The slot list is step 1's; this step only puts a price against
            each one. Every row carries a REAL price — the buffet's own
            per-head figure until somebody types over it — so the table reads
            as the fixed price each sitting charges rather than a grid of
            blanks a manager has to translate, and the right-hand column says
            what the guest actually hands over once tax is on it. */}
        {sitOn && (
          <div className="mt-8 border-t border-border pt-6">
            <h3 className="text-sm font-semibold text-foreground">
              {pricingMode === 'FIXED'
                ? t('buffet.slotPrice.titleFixed')
                : t('buffet.slotPrice.title')}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('buffet.slotPrice.hint')}
            </p>

            {sittingStarts.length === 0 ? (
              <p className="mt-3 text-sm text-muted-foreground">
                {t('buffet.slotPrice.noSlots')}
              </p>
            ) : (
              <div className="mt-3 max-h-96 overflow-auto rounded-md border border-border">
                <table className="w-full text-sm min-w-[30rem]">
                  <thead className="sticky top-0 bg-muted/60 backdrop-blur">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-3 py-2 font-medium">{t('buffet.sit.colTime')}</th>
                      <th className="px-3 py-2 font-medium">{t('buffet.sit.colAdult')}</th>
                      <th className="px-3 py-2 font-medium">{t('buffet.sit.colChild')}</th>
                      <th className="px-3 py-2 font-medium text-right">
                        {t('buffet.slotPrice.colCharges')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sittingStarts.map((s) => {
                      const v = sitPrices[s] || {};
                      const off = !!v.off;
                      const end = toHHMM(toMin(s) + sitMin);
                      /* What this sitting charges, run through the same tax
                         arithmetic the bill uses — so the column is the
                         figure the guest hands over, not the figure typed. */
                      const paysAdult = taxOn(sitShown(s, 'adult'));
                      const paysChild = taxOn(sitShown(s, 'child'));
                      return (
                        <tr key={s} className={`border-t border-border ${off ? 'opacity-50' : ''}`}>
                          <td className="px-3 py-1.5 whitespace-nowrap text-foreground">
                            {to12h(s)} – {to12h(end)}
                            {off && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {t('buffet.slotPrice.slotOff')}
                              </span>
                            )}
                            {/* Which rows were touched, since every row now
                                shows a price and they would otherwise look
                                alike whether the manager set it or not. */}
                            {!off && sitIsOwn(s) && (
                              <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
                                {t('buffet.slotPrice.ownTag')}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5">
                            <Input value={sitShown(s, 'adult')} disabled={off}
                              onChange={(e) => setSitVal(s, 'adult', cleanMoney(e.target.value))}
                              placeholder="—"
                              className="h-8 w-24 text-right tabular-nums" inputMode="decimal" />
                          </td>
                          <td className="px-3 py-1.5">
                            <Input value={sitShown(s, 'child')} disabled={off}
                              onChange={(e) => setSitVal(s, 'child', cleanMoney(e.target.value))}
                              placeholder="—"
                              className="h-8 w-24 text-right tabular-nums" inputMode="decimal" />
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            {off ? (
                              <span className="text-xs text-muted-foreground">—</span>
                            ) : (
                              <>
                                <div className="tabular-nums text-sm font-medium text-foreground">
                                  {paysAdult.gross ? rupees(paysAdult.gross) : '—'}
                                  {paysChild.gross > 0 && (
                                    <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                                      {t('buffet.slotPrice.kidsShort', { price: rupees(paysChild.gross) })}
                                    </span>
                                  )}
                                </div>
                                {taxNames && (
                                  <div className="text-[11px] text-muted-foreground">
                                    {taxInclusive
                                      ? t('buffet.slotPrice.taxIncl', { names: taxNames })
                                      : t('buffet.slotPrice.taxPlus', { names: taxNames })}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* A fixed figure per sitting is the whole table under Fixed
                price; under Dynamic the rules still get the last word, and
                saying so once beats repeating it on every row. */}
            {pricingMode === 'DYNAMIC' && sittingStarts.length > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                {t('buffet.slotPrice.dynamicNote')}
              </p>
            )}
          </div>
        )}
      </Section>
    );
  };

  const reportStep = () => (
    <Section n={5} emoji="📊" title="Reports">
      {reportsLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 size={15} className="animate-spin" /> Loading data…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Covers this week" value={report.coversWk} />
            <Stat label="Buffet revenue" value={rupees(report.revenueWk)} />
            <Stat label="Food cost"
              value={report.arpc && fc ? `${Math.round((fc / report.arpc) * 100)}%` : '—'}
              sub={fc ? 'target 30%' : null} />
            <Stat label="Wastage"
              value={report.revenueWk ? `${(report.wasteCost / report.revenueWk * 100).toFixed(1)}%` : '—'}
              sub={report.wasteCost ? rupees(report.wasteCost) : null}
              subTone={report.revenueWk && report.wasteCost / report.revenueWk > 0.06 ? 'bad' : undefined} />
          </div>

          <div className="mb-1 text-sm font-semibold text-foreground">Covers by day</div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_BLUE }} /> Lunch
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_GREEN }} /> Dinner
            </span>
          </div>
          {coversChart()}

          <div className="mt-6 mb-1 text-sm font-semibold text-foreground">Prepped vs consumed · this week</div>
          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_BLUE }} /> Prepped kg
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C_MUTED }} /> Consumed kg
            </span>
          </div>
          {preppedChart() || <p className="text-sm text-muted-foreground">Add items to the spread to see prep planning here.</p>}

          <div className="mt-6 space-y-2">
            {insights.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Run services and sell covers to unlock insights.
              </p>
            ) : insights.map((ins, i) => (
              <div key={i} className={`flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 text-sm ${
                ins.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : ins.tone === 'good' ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                    : 'border-blue-200 bg-blue-50 text-blue-900'
              }`}>
                <ins.Icon size={15} className="mt-0.5 shrink-0" />
                <span>{ins.text}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );

  const STEP_BODY = {
    /* Name, dates and the sittings those dates are sold in. The slot list is
       what every later step is keyed by, so it is settled first. */
    time: timeStep,
    menu: menuStep,
    seating: seatingStep,
    price: priceStep,
    report: reportStep,
  };

  /* ── render ────────────────────────────────────────────────────────── */
  const isLast = stepIdx >= steps.length - 1;
  const nextStep = steps[stepIdx + 1];
  /* "Save as draft" is only offered while the buffet isn't live, since on an active one it would take it off the counter. */
  const savedDraft = !!buffet?.id && buffet?.is_active === false;
  const canDraft = !buffet?.id || savedDraft;

  /* Every exit that abandons the studio funnels through here — the back link and Cancel ask the same question. */
  const confirmLeave = async () => {
    /* Nothing was changed, so there is nothing to discard and nothing worth
       parking as a draft: a warning here is a question about work that does
       not exist. Just leave. */
    if (!changed) { onBack(); return; }
    const choice = await confirm({
      title: buffet?.id ? 'Discard your changes?' : 'Discard this buffet?',
      description: buffet?.id
        ? 'Everything you changed since opening the studio is lost; the saved buffet stays as it was.'
        : 'Nothing has been saved — this buffet will simply not exist. Keep it as a draft and it waits, inactive, until you finish it.',
      confirmText: 'Discard',
      cancelText: 'Keep editing',
      /* The way out the message offers, as a button - NEW buffets only, since a draft is parked inactive. */
      altText: buffet?.id ? undefined : 'Save as draft',
      tone: 'destructive',
    });
    if (choice === 'alt') { save(true); return; }
    if (choice === true) onBack();
  };

  return (
    // Full width like every other screen; this page used to cap at max-w-5xl and leave half a desktop empty.
    <div className="p-6 w-full space-y-5">
      <button onClick={confirmLeave} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back to templates
      </button>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        {/* The title REPORTS the name rather than collecting it: a required value belongs in step 1 with
            the others. A NEW buffet has no name yet, so the title says what this screen is doing instead;
            the moment a name is typed, the name takes over. */}
        <h1 className="flex-1 min-w-[260px] font-display text-3xl font-semibold tracking-tight leading-none">
          {name.trim() || (!buffet?.id && 'Create New Buffet Template')}
          {/* The same grey DRAFT flag the board uses, so the buffet still offering "Keep as draft" says why. */}
          {savedDraft && (
            <span className="ml-3 align-middle rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500"
              title="Not live — invisible to guests and the till until it is saved as active">
              DRAFT
            </span>
          )}
        </h1>
        {/* Every way OUT of the studio lives here, rather than split between the top bar and the footer. */}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={saving}
            className="text-muted-foreground hover:text-red-600"
            onClick={confirmLeave}
          >
            <X size={16} className="mr-1.5" /> Cancel
          </Button>
          {/* The one door out of the all-mandatory rule: a draft needs only a name and is invisible until finished. */}
          {canDraft && (
            <Button type="button" variant="outline" disabled={saving}
              onClick={() => save(true)}
              title={savedDraft
                ? 'Save your changes and leave it a draft — off the counter until you take it live'
                : 'Keep what is filled in so far, inactive, and finish it later'}>
              {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              {savedDraft ? 'Keep as draft' : 'Save as draft'}
            </Button>
          )}
          <Button onClick={save} disabled={saving}
            title={savedDraft ? 'Save everything and take this buffet live' : undefined}>
            {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
            Save buffet
          </Button>
        </div>
      </div>

      {/* A one-line recap of the steps already answered, so the choices driving this one stay on screen. */}
      {(startTime || menuId) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          {startTime && endTime && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={13} /> {prettyTime(startTime)} – {prettyTime(endTime)}
            </span>
          )}
          {allDishes.length > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <BookOpen size={13} /> {sections.filter((s) => s.items.filter(Boolean).length).length} courses ·
              {' '}{allDishes.length} items
            </span>
          )}
          {wd > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <IndianRupee size={13} /> {rupees(wd)} per head
            </span>
          )}
        </div>
      )}

      {/* Full-screen floor plan. Rendered at the ROOT of the studio, not
          inside the step: from within the step it would be clipped by the
          card and confined to the narrow column, which is the very problem it
          exists to solve. The planner is controlled, so this is the same
          editor — closing it needs no save. */}
      {planExpanded && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-foreground">
                {t('buffet.plan.fullTitle')}
              </h2>
              <p className="text-xs text-muted-foreground">{t('buffet.plan.fullHint')}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* Discard is the plain button and Save the loud one, because the
                  draft is thrown away here rather than merely hidden. */}
              <button
                type="button"
                onClick={async () => {
                  const dirty = JSON.stringify(planDraft) !== JSON.stringify(floorLayout);
                  if (dirty) {
                    const ok = await confirm({
                      title: t('buffet.plan.discardTitle'),
                      description: t('buffet.plan.discardBody'),
                      confirmText: t('buffet.plan.discardConfirm'),
                      cancelText: t('buffet.plan.keepEditing'),
                      tone: 'destructive',
                    });
                    if (!ok) return;
                  }
                  setPlanDraft(null);
                  setPlanExpanded(false);
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/60 hover:text-foreground"
              >
                <Minimize2 size={14} />
                {t('buffet.plan.collapse')}
              </button>
              <Button
                type="button"
                onClick={() => {
                  setFloorLayout(planDraft);
                  setPlanDraft(null);
                  setPlanExpanded(false);
                }}
              >
                <Save size={15} className="mr-2" />
                {t('buffet.plan.apply')}
              </Button>
            </div>
          </div>
          {/* Tight to the editor: the planner draws its own ground now, so a
              wide outer padding only adds to the empty border around it. */}
          <div className="min-h-0 flex-1 overflow-auto p-2">
            <BuffetFloorPlanner
              areas={pickedSections}
              dishes={allDishes}
              dishTypes={dishTypes}
              layout={planDraft || floorLayout}
              onChange={setPlanDraft}
              fitHeight
            />
          </div>
        </div>
      )}

      <PhaseBar phase={phase} onPick={setPhase} saved={!!savedId} t={t} />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</div>
      )}

      {/* ONE layout, ONE calendar, both phases. The advance phase used to
          draw its own copy at 30/70 with the months stacked one per row, so
          the wall planner a manager had just been reading turned into a
          different, narrower object the moment they stepped forward — same
          dates, same buffet, unrecognisable. The calendar is the fixed half
          of this screen; only the panel beside it changes. */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,65fr)_minmax(0,35fr)] gap-5 items-start">
        <RunCalendar from={runFrom} to={runTo} saved={!!savedId}
                     opened={openedDays} planned={plannedSet} struck={struckOpenSet}
                     /* Read-only on advance: the run dates are step 1's
                        decision, and striking a day out from a pricing screen
                        would silently change what the phase before it set. */
                     onToggleDate={phase === 'advance' ? null : toggleExcept}
                     holidays={holidays} holidaysFailed={holidaysFailed} t={t} />

        <div className="min-w-0 space-y-5">
          {phase === 'advance' ? (
            <AdvanceSettings
              t={t}
              index={advIdx}
              onPick={setAdvIdx}
              pricing={{
                restaurantId,
                locationId,
                /* The stretch being priced: what step 1 typed, widened to the
                   days already open. `to` falls back to `from` so a one-day
                   range still asks. */
                from: spanFrom,
                to: spanTo || spanFrom,
                base: weekday,
                kids: kidPrice,
                holidayUplifts,
                setHolidayUplifts,
                weekendUplifts,
                setWeekendUplifts,
                pricingMode,
                /* Uplifts are pricing RULES, and fixed pricing ignores every
                   rule — so the warning on the card can put it right rather
                   than sending the manager back a phase to find the switch. */
                onUseDynamic: () => setPricingMode('DYNAMIC'),
              }}
            />
          ) : (
            <>
              <Stepper steps={steps} index={stepIdx} done={stepDone} onPick={goTo} />

              {(STEP_BODY[step.key] || timeStep)()}

              {/* The footer is the wizard's own navigation and nothing else; leaving and saving stay in the top bar. */}
              <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
                <Button type="button" variant="outline" onClick={back} disabled={stepIdx === 0}>
                  <ArrowLeft size={16} className="mr-2" /> Back
                </Button>
                <span className="mx-auto text-xs text-muted-foreground">
                  {t('buffet.wiz.subStepOf', { n: stepIdx + 1, total: steps.length })}
                </span>
                {!isLast ? (
                  <Button type="button" onClick={next}>
                    Next: {nextStep.title} <ArrowRight size={16} className="ml-2" />
                  </Button>
                ) : (
                  /* End of setup. Advance settings need a saved buffet, so the
                     button says which of the two it is doing rather than
                     silently doing nothing on an unsaved one. */
                  <Button type="button" onClick={() => (buffet?.id ? setPhase('advance') : save(false))}>
                    {buffet?.id ? t('buffet.wiz.toAdvance') : t('buffet.wiz.saveToContinue')}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* The two top-level phases. Advance settings is disabled — with the reason on
   the button itself — until the buffet has been saved. */
function PhaseBar({ phase, onPick, saved, t }) {
  const items = [
    { key: 'setup', label: t('buffet.wiz.phase1'), n: 1 },
    { key: 'advance', label: t('buffet.wiz.phase2'), n: 2 },
  ];

  const Phase = ({ it }) => {
    const locked = it.key === 'advance' && !saved;
    const active = phase === it.key;
    return (
      <button
        type="button"
        disabled={locked}
        onClick={() => onPick(it.key)}
        aria-current={active ? 'step' : undefined}
        title={locked ? t('buffet.wiz.lockedHint') : undefined}
        className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition
          ${active ? 'bg-primary text-primary-foreground'
            : locked ? 'text-muted-foreground/60 cursor-not-allowed'
              : 'text-muted-foreground hover:bg-muted/60'}`}
      >
        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px]
          ${active ? 'bg-primary-foreground/20' : 'bg-muted'}`}>
          {it.n}
        </span>
        {it.label}
        {locked && <Lock size={13} className="ml-0.5" />}
      </button>
    );
  };

  return (
    <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-2">
      <Phase it={items[0]} />

      {/* The two phases sit at opposite ends with the track between them, so
          the bar reads as one journey rather than two buttons huddled in a
          corner. The track FILLS once the buffet is saved — that is the moment
          the second phase becomes reachable, so the line showing it joined up
          is the same fact as the padlock coming off. */}
      <div className="relative mx-1 hidden h-px flex-1 bg-border sm:block" aria-hidden="true">
        <span
          className={`absolute inset-y-0 left-0 bg-primary transition-all duration-500 ${
            saved ? 'w-full' : 'w-0'}`}
        />
      </div>
      {/* Narrow screens get a plain separator rather than a stretched track. */}
      <span className="mx-auto sm:hidden" aria-hidden="true" />

      <Phase it={items[1]} />
    </div>
  );
}

/* The dates this buffet will run on, drawn as a wall planner. Before a range
   is set it is deliberately an empty frame rather than today's month: an empty
   calendar says "nothing scheduled yet", where a populated one would imply
   otherwise. The grid itself is the shared planner (components/calendar) — the
   same green for a day that is on, the same weekend band and the same holiday
   marks the rest of RestoBOXX draws. */
function RunCalendar({
  from, to, saved, opened = [], planned = null, onToggleDate = null,
  struck = null, holidays = [], holidaysFailed = false, single = false, t,
}) {
  /* Date -> the opening already on it. Two different facts share this grid:
     `from`/`to` are days this save WILL open, `opened` is days Open Buffets
     has already put the buffet on. The calendar drew only the first, which is
     why editing a saved buffet showed an empty planner however heavily it was
     booked — a template keeps no dates, so its own range is blank every time
     it is reopened. */
  const openedByDate = useMemo(() => {
    const m = new Map();
    (opened || []).forEach((o) => { if (o?.date) m.set(o.date, o); });
    return m;
  }, [opened]);

  const openedDates = useMemo(
    () => [...openedByDate.keys()].sort(), [openedByDate],
  );

  /* The months to draw: wide enough for both facts. A range typed for next
     March on a buffet already running this month has to show both, or half of
     what the manager is deciding about is off-screen. */
  const spanFrom = useMemo(
    () => [from, openedDates[0]].filter(Boolean).sort()[0] || '',
    [from, openedDates],
  );
  const spanTo = useMemo(
    () => [to, openedDates[openedDates.length - 1]].filter(Boolean).sort().pop() || '',
    [to, openedDates],
  );
  const months = useMemo(() => monthsBetween(spanFrom, spanTo), [spanFrom, spanTo]);

  /* Inside the typed span — which is NOT the same as "the buffet runs that
     day" any more. Kept for the days that are inside it but ticked or struck
     out, because those are the ones the grid has to show as skipped rather
     than simply not draw. */
  const inSpan = (d) => {
    const a = parseDay(from);
    const b = parseDay(to);
    return !!(a && b && d >= a && d <= b);
  };
  /* The rule, as resolved by the form. Falls back to the whole span so this
     component still draws correctly for any caller that has no rule to give. */
  const isPlanned = (d) => (planned ? planned.has(isoOf(d)) : inSpan(d));

  // Date → the holidays on it. A date can carry more than one (New Year's Day
  // and its holiday, or a festival landing on a public holiday), so the value
  // is a list rather than the first one found.
  const holidayByDate = useMemo(() => {
    const m = new Map();
    (holidays || []).forEach((h) => {
      if (!h?.date) return;
      const list = m.get(h.date) || [];
      list.push(h);
      m.set(h.date, list);
    });
    return m;
  }, [holidays]);

  /* The ones actually inside the chosen range — this is what the list under
     the calendar names, so it reads the holidays this buffet will run through
     rather than every holiday in the year. */
  const hitHolidays = useMemo(() => {
    const a = parseDay(spanFrom);
    const b = parseDay(spanTo);
    if (!a || !b) return [];
    return (holidays || [])
      .filter((h) => {
        const d = parseDay(h.date);
        return d && d >= a && d <= b;
      })
      .sort((x, y) => x.date.localeCompare(y.date));
  }, [holidays, spanFrom, spanTo]);

  // Days the buffet will run on, not days in the span — the two now differ
  // the moment a weekday is unticked or a date struck out.
  const days = planned
    ? [...planned].filter((iso) => iso >= from && iso <= to).length
    : dayCount(from, to);
  // How many of those days are a Saturday or a Sunday: the fact a manager
  // checks when a spread is priced for weekend trade. Counted over everything
  // drawn, so an already-open weekend counts as much as a planned one.
  const weekendDays = useMemo(() => {
    const a = parseDay(spanFrom);
    const b = parseDay(spanTo);
    if (!a || !b) return 0;
    let n = 0;
    for (const d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
      if ((d.getDay() === 0 || d.getDay() === 6)
          && (isPlanned(d) || openedByDate.has(isoOf(d)))) n += 1;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spanFrom, spanTo, from, to, openedByDate]);
  const publicHolidays = hitHolidays.filter((h) => h.is_public_holiday).length;
  const todayIso = todayISO();

  const fmtDay = (isoDate) => {
    const d = parseDay(isoDate);
    return d ? d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : isoDate;
  };

  return (
    <CalendarPanel>
      <CalendarHead
        title={t('buffet.cal.title')}
        subtitle={months.length
          ? t('buffet.cal.range', { from: fmtDay(spanFrom), to: fmtDay(spanTo) })
          : t('buffet.cal.empty')}
        counts={months.length ? [
          /* Two separate figures on purpose: days already being served and
             days this save would add answer different questions, and one
             total would hide whichever the manager came to check. */
          ...(openedDates.length
            ? [{ label: t('buffet.cal.countOpened'), value: openedDates.length, tone: 'sea' }]
            : []),
          ...(days ? [{ label: t('buffet.cal.countOpen'), value: days, tone: 'sea' }] : []),
          { label: t('buffet.cal.countWeekends'), value: weekendDays },
          { label: t('buffet.cal.countHolidays'), value: publicHolidays, tone: 'flame' },
        ] : []}
      />

      {months.length === 0 ? (
        <CalendarEmpty icon={<CalendarIcon size={26} className="opacity-40" />}>
          {/* A saved buffet with no openings is a real state, and it is not
              the same as one nobody has filled in yet — it says the template
              exists but has never been put on a day. */}
          {saved ? t('buffet.cal.emptyHintSaved') : t('buffet.cal.emptyHint')}
        </CalendarEmpty>
      ) : (
        <>
          <CalendarToolbar
            legend={[
              ...(openedDates.length
                ? [{ key: 'dot', label: t('buffet.cal.legendOpened') }] : []),
              ...(openedDates.some((d) => openedByDate.get(d)?.status === 'RUNNING')
                ? [{ key: 'live', label: t('buffet.cal.legendRunning') }] : []),
              ...(days
                ? [{ key: 'open', label: saved ? t('buffet.cal.legendSet') : t('buffet.cal.legendPlanned') }]
                : []),
              { key: 'weekend', label: t('buffet.cal.legendWeekend') },
              ...(publicHolidays > 0
                ? [{ key: 'holiday', label: t('buffet.cal.legendHoliday') }] : []),
              ...(hitHolidays.length > publicHolidays
                ? [{ key: 'festival', label: t('buffet.cal.legendObservance') }] : []),
              { key: 'today', label: t('buffet.cal.legendToday') },
            ]}
          />

          {/* Months across, not down: a season reads as a wall planner. */}
          <CalendarMonths
            months={months}
            /* One month per row where the calendar is a narrow rail; three
               across where it has the wide half of the screen. */
            single={single}
            todayIso={todayIso}
            /* Only days inside the typed span respond: clicking outside it
               would silently widen a range the manager set with the fields,
               which is not what a strike-out gesture means. */
            onDayClick={onToggleDate
              ? (key, d) => { if (inSpan(d)) onToggleDate(key); }
              : undefined}
            dayInfo={(key, d) => {
              const hols = holidayByDate.get(key) || [];
              /* A day OFF and a day the country is OUT are opposite facts for
                 a kitchen: on a public holiday much of the island is shut, on
                 Père Laval or Holi it is on the move. */
              const isDayOff = hols.some((h) => h.is_public_holiday);
              const willRun = isPlanned(d);
              /* Inside the range but NOT running: unticked weekday, or struck
                 out by hand. Drawn deliberately rather than left blank — a day
                 the manager took out and a day that was never in the range
                 look identical otherwise, and only one of them can be undone
                 by clicking it. */
              const skipped = !willRun && inSpan(d);
              const open = openedByDate.get(key) || null;
              /* Open today, but the rule no longer covers it: struck out on
                 the grid now, closed by Save. Lit as if planned, it read as
                 the opposite of what the chip had just said. */
              const closes = !!open && !!struck && struck.has(key);
              return {
                on: (willRun || !!open) && !closes,
                skipped: skipped || closes,
                /* The dot separates the two: tint alone says "this day has the
                   buffet on it", the dot says it is already open — which is
                   the difference between a day you can still change your mind
                   about and one guests can already book. */
                dot: !!open,
                live: open?.status === 'RUNNING',
                holiday: isDayOff,
                festival: hols.length > 0 && !isDayOff,
                title: [
                  open ? (open.status === 'RUNNING'
                    ? t('buffet.cal.dayRunning')
                    : t('buffet.cal.dayOpened')) : null,
                  willRun ? (saved ? t('buffet.cal.daySet') : t('buffet.cal.dayPlanned')) : null,
                  skipped ? t('buffet.cal.daySkipped') : null,
                  closes ? t('buffet.cal.dayCloses') : null,
                  ...hols.map((h) => [
                    h.icon ? `${h.icon} ` : '',
                    h.name,
                    h.is_public_holiday ? ` (${t('buffet.cal.publicHoliday')})` : '',
                    h.is_approximate ? ` ${t('buffet.cal.approxMark')}` : '',
                  ].join('')),
                ].filter(Boolean).join(' · ') || undefined,
                /* The hover bubble, on the days that have a name worth
                   reading. A violet rule says something is on the 8th; this
                   says it is Divali, without a trip to the list below. Only
                   holidays get one — a bubble over every square of a wall
                   planner being clicked through is in the way. */
                tip: hols.length ? [
                  ...(open ? [open.status === 'RUNNING'
                    ? t('buffet.cal.dayRunning') : t('buffet.cal.dayOpened')] : []),
                  ...(willRun ? [saved
                    ? t('buffet.cal.daySet') : t('buffet.cal.dayPlanned')] : []),
                  ...(skipped ? [t('buffet.cal.daySkipped')] : []),
                  ...(closes ? [t('buffet.cal.dayCloses')] : []),
                  ...hols.map((h) => ({
                    icon: h.icon,
                    name: h.name,
                    tag: h.is_public_holiday
                      ? t('buffet.cal.publicHoliday')
                      : (h.is_approximate
                        ? t('buffet.cal.approxMark') : t('buffet.cal.observance')),
                  })),
                ] : undefined,
              };
            }}
          />

          {/* Named, not just marked. A manager choosing dates needs to know it
              is Divali on the 8th — a coloured rule alone says "something is
              on this day" and makes them go and look it up. */}
          <CalendarEvents
            title={hitHolidays.length > 0
              ? t('buffet.cal.holidaysInRange', { n: hitHolidays.length }) : undefined}
            items={hitHolidays.map((h) => ({
              date: h.date,
              day: fmtDay(h.date),
              name: h.name,
              icon: h.icon,
              soft: !h.is_public_holiday,
              tag: h.is_public_holiday
                ? t('buffet.cal.publicHoliday')
                : (h.is_approximate ? t('buffet.cal.approxMark') : t('buffet.cal.observance')),
            }))}
            note={hitHolidays.length === 0
              ? (holidaysFailed ? t('buffet.cal.holidaysUnavailable') : t('buffet.cal.noHolidays'))
              : (!saved ? t('buffet.cal.notYetOpened') : undefined)}
          />
        </>
      )}
    </CalendarPanel>
  );
}

/* ── phase 2: advance settings ─────────────────────────────────────────── */

/* How far back the footfall tables look. Three years is enough to show a
   pattern and recent enough to still be this restaurant — a fourth year is
   usually a different chef, a different room and a different price. */
const YEARS_BACK = 3;

const UPLIFT_ZERO = { mode: 'PERCENT', value: '' };

// Typed, and not zero — a blank box is a question nobody answered, not "no".
const upliftSet = (u) => {
  const v = Number(u?.value);
  if (!Number.isFinite(v)) return false;
  // A new price of nothing is not a price; a change of nothing is not a change.
  return u?.mode === 'SET' ? v > 0 : v !== 0;
};

/* What the day will actually cost. The SAME sum the pricing engine does on
   the day (apps/buffet/controller.effective_price), done here so the decision
   is made against a price a guest would pay rather than a percentage. */
const applyUplift = (base, u) => {
  const b = Number(base);
  const v = Number(u?.value);
  if (!upliftSet(u)) return null;
  // A new price needs no base to be read; a % change is a % of one.
  if (u.mode === 'SET') return v;
  if (!Number.isFinite(b) || b <= 0) return null;
  // To the cent, so 210 × 1.10 reads 231 and never 231.00000000000003.
  return Math.round(Math.max(0, u.mode === 'AMOUNT' ? b + v : b * (1 + v / 100)) * 100) / 100;
};

/* Switching between the two ways of saying it keeps the PRICE, not the
   number: "+10%" on Rs 210 becomes "new price Rs 231", and back again — a
   figure typed as a percentage must never be re-read as rupees. */
const switchMode = (u, mode, base) => {
  const b = Number(base);
  if (u.mode === mode) return u;
  const price = applyUplift(base, u);
  if (mode === 'SET') return { mode, value: price != null ? String(Math.round(price)) : '' };
  return {
    mode,
    value: price != null && b > 0 ? String(Math.round((price / b - 1) * 1000) / 10) : '',
  };
};

/* The uplift itself: how much more, and in what — the two-button choice the
   whole feature turns on. Ten per cent and a flat hundred rupees are different
   promises on a Rs 210 buffet and a Rs 1,400 one, so neither is assumed. */
function UpliftCell({ value, base, kids, onChange, t }) {
  const u = value || UPLIFT_ZERO;
  const b = Number(base);
  const next = applyUplift(base, u);
  const isSet = u.mode === 'SET';
  // What the kids pay on the day, in step 1's own adult:kids proportion.
  const kidsNext = next != null && b > 0 && Number(kids) > 0
    ? Math.round(next * Number(kids) / b) : null;
  return (
    <div className="flex flex-col items-end gap-1">
      {/* The ordinary price FIRST: it is what a percentage is a percentage
          of, and what a new price takes the place of. Without it the box
          under it is a number with nothing to be read against. */}
      <div className="text-[11px] text-muted-foreground">
        {b > 0 ? t('buffet.adv.normalIs', { price: rupees(b) }) : t('buffet.adv.normalUnset')}
      </div>
      <div className="flex items-center justify-end gap-2">
        <div className="inline-flex shrink-0 rounded-md border border-border bg-background p-0.5">
          {[['PERCENT', '%'], ['SET', t('buffet.adv.newPrice')]].map(([m, label]) => (
            <button
              key={m}
              type="button"
              aria-pressed={u.mode === m}
              title={m === 'PERCENT' ? t('buffet.adv.modePct') : t('buffet.adv.modeSet')}
              onClick={() => onChange(switchMode(u, m, base))}
              className={`rounded px-1.5 py-0.5 text-xs font-semibold transition ${
                u.mode === m ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="text"
          inputMode="decimal"
          value={u.value}
          placeholder={isSet ? (b > 0 ? String(Math.round(b)) : '') : '0'}
          aria-label={isSet ? t('buffet.adv.newPrice') : t('buffet.adv.upliftLabel')}
          onChange={(e) => onChange({ ...u, value: cleanMoney(e.target.value) })}
          className={`${isSet ? 'w-20' : 'w-14'} rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30`}
        />
        {/* The answer, not the input: what one adult hands over on that day. */}
        <span className={`w-20 shrink-0 text-right text-sm tabular-nums ${
          next != null ? 'font-semibold text-foreground' : 'text-muted-foreground/50'}`}>
          {next != null ? rupees(next) : '—'}
        </span>
        {upliftSet(u) ? (
          <button
            type="button"
            onClick={() => onChange(UPLIFT_ZERO)}
            title={t('buffet.adv.clearOne')}
            className="shrink-0 text-muted-foreground hover:text-red-600"
          >
            <X size={13} />
          </button>
        ) : <span className="w-[13px] shrink-0" aria-hidden="true" />}
      </div>
      {/* Said in full once a figure is in: what it is a change from, and
          what the kids pay alongside it. */}
      {next != null && (b > 0 || kidsNext != null) && (
        <div className="text-[11px] text-muted-foreground">
          {b > 0 ? (isSet
            ? t('buffet.adv.wasPrice', { price: rupees(b) })
            : t('buffet.adv.onNormal', { price: rupees(b) })) : ''}
          {kidsNext != null ? ` · ${t('buffet.slotPrice.kidsShort', { price: rupees(kidsNext) })}` : ''}
        </div>
      )}
    </div>
  );
}

/* One footfall figure, with what it is made of on hover. Zero and "we have no
   record" print differently on purpose: only one of them is an argument
   against charging more. */
function Heads({ n, sub, t }) {
  if (n == null) {
    return <span className="text-xs text-muted-foreground/60">{t('buffet.adv.noFigures')}</span>;
  }
  return (
    <span className="inline-flex flex-col leading-tight">
      <span className="text-sm font-semibold tabular-nums text-foreground">{Math.round(n)}</span>
      {sub ? <span className="text-[11px] text-muted-foreground">{sub}</span> : null}
    </span>
  );
}

/* The control that makes this worth having: tick several rows, name one
   uplift, apply it to all of them. Applying to one row is the same gesture. */
function BulkBar({ n, mode, setMode, value, setValue, onApply, onClear, t }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
      <span className="text-xs text-muted-foreground">
        {mode === 'SET' ? t('buffet.adv.bulkLeadSet') : t('buffet.adv.bulkLead')}
      </span>
      <div className="inline-flex rounded-md border border-border bg-background p-0.5">
        {[['PERCENT', '%'], ['SET', t('buffet.adv.newPrice')]].map(([m, label]) => (
          <button
            key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}
            className={`rounded px-2 py-0.5 text-xs font-semibold transition ${
              mode === m ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <input
        type="text" inputMode="decimal" value={value}
        aria-label={t('buffet.adv.upliftLabel')}
        onChange={(e) => setValue(cleanMoney(e.target.value))}
        className="w-16 rounded-md border border-input bg-background px-2 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring/30"
      />
      <Button type="button" size="sm" disabled={!n || !Number(value)} onClick={onApply}>
        {t('buffet.adv.apply', { n })}
      </Button>
      <button
        type="button" onClick={onClear}
        className="ml-auto text-xs text-muted-foreground underline-offset-2 hover:text-red-600 hover:underline"
      >
        {t('buffet.adv.clearAll')}
      </button>
    </div>
  );
}

/* The head both footfall tables share: a tick-all box, the row's name, one
   column per year, the average, then the uplift. Declared HERE rather than
   inside the card — a component defined in a render body is a new type on
   every keystroke, and React throws its subtree away and rebuilds it. */
function FootfallHead({ label, years, allKeys, picked, setPicked, t }) {
  return (
    <thead className="bg-muted/50 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
      <tr>
        <th className="w-8 px-2 py-2">
          <input
            type="checkbox"
            aria-label={t('buffet.adv.tickAll')}
            checked={allKeys.length > 0 && allKeys.every((k) => picked.has(k))}
            onChange={(e) => setPicked(e.target.checked ? new Set(allKeys) : new Set())}
          />
        </th>
        <th className="px-2 py-2 font-medium">{label}</th>
        {years.map((y) => <th key={y} className="px-2 py-2 font-medium">{y}</th>)}
        <th className="px-2 py-2 font-medium">{t('buffet.adv.colAvg', { n: years.length })}</th>
        <th className="px-2 py-2 text-right font-medium">{t('buffet.adv.colUplift')}</th>
      </tr>
    </thead>
  );
}

/* Advance pricing: what the last few years say a holiday or a weekend is
   worth here, and the uplift on step 1's price that says what to do about it.
   Nothing is invented — the footfall is this branch's own, read on the DATE
   each holiday actually fell on, because most of them move. */
function AdvancePricing({
  restaurantId, locationId, from, to, base,
  holidayUplifts, setHolidayUplifts, weekendUplifts, setWeekendUplifts,
  kids = '',
  pricingMode, onUseDynamic, t,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [pickedHol, setPickedHol] = useState(() => new Set());
  const [pickedWe, setPickedWe] = useState(() => new Set());
  /* Which table is open, or null for the two cards. Both are wide (a column
     per year) and long, and stacking them meant the weekend question sat a
     screen and a half below the holiday one — so neither could be read
     without losing the other. One at a time, entered deliberately. */
  const [open, setOpen] = useState(null);
  const [holMode, setHolMode] = useState('PERCENT');
  const [holValue, setHolValue] = useState('10');
  const [weMode, setWeMode] = useState('PERCENT');
  const [weValue, setWeValue] = useState('10');

  useEffect(() => {
    if (!restaurantId || !from || !to) { setData(null); return undefined; }
    let alive = true;
    setLoading(true);
    setFailed(false);
    apiClient.get(`/users/restaurant/${restaurantId}/buffets/festive-footfall/`, {
      params: { from, to, location_id: locationId || undefined, years: YEARS_BACK },
    })
      .then(({ data: res }) => {
        if (!alive) return;
        if (!res?.success) throw new Error(res?.error || 'failed');
        setData(res.data);
      })
      /* The uplifts still work without the history — this screen just becomes
         a decision made on judgement rather than on figures, which is what it
         was before any of this existed. */
      .catch(() => { if (alive) { setData(null); setFailed(true); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [restaurantId, locationId, from, to]);

  const years = data?.years || [];
  const holidays = data?.holidays || [];
  const weekends = data?.weekends || [];
  const baseline = data?.baseline_heads ?? null;

  const toggleIn = (set, key) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  };

  const applyBulk = (keys, mode, value, setter) => {
    if (!keys.length || !Number(value)) return;
    setter((prev) => {
      const next = { ...prev };
      keys.forEach((k) => { next[k] = { mode, value }; });
      return next;
    });
  };

  const setOne = (setter, key) => (u) => setter((prev) => {
    const next = { ...prev };
    if (upliftSet(u)) next[key] = u; else delete next[key];
    return next;
  });

  const fmtShort = (iso) => {
    const d = parseDay(iso);
    return d ? d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' }) : iso;
  };

  const liftText = (pct) => (pct == null ? null
    : t('buffet.adv.vsOrdinary', { pct: `${pct > 0 ? '+' : ''}${Math.round(pct)}%` }));

  /* The line under a year's figure, read the way the diary is read: the date
     the holiday fell on, how many parties booked, and - where the till was
     used that day - how many covers it rang through. The two are shown side
     by side rather than netted, because the gap between them is the walk-ins,
     and that is worth seeing when deciding what a day is worth. */
  const footSub = (r) => {
    const parts = [fmtShort(r.date)];
    if (r.bookings) parts.push(t('buffet.adv.bookingsN', { n: r.bookings }));
    if (r.covers_sold) parts.push(t('buffet.adv.soldN', { n: r.covers_sold }));
    return parts.join(' · ');
  };

  const holPriced = Object.values(holidayUplifts).filter(upliftSet).length;
  const wePriced = Object.values(weekendUplifts).filter(upliftSet).length;
  const setCount = holPriced + wePriced;
  const weDays = weekends.reduce((n, w) => n + (w.in_range || 0), 0);

  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <h3 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
        <span aria-hidden="true">💰</span>
        {t('buffet.adv.pricing.title')}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">{t('buffet.adv.pricing.blurb')}</p>

      {/* Rules are switched off wholesale under fixed pricing, so an uplift
          set here would be saved and never charged. Said once, at the top,
          with the way out attached. */}
      {pricingMode === 'FIXED' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <AlertTriangle size={15} className="shrink-0" />
          <span>{t('buffet.adv.fixedWarn')}</span>
          <button
            type="button" onClick={onUseDynamic}
            className="font-semibold underline underline-offset-2 hover:opacity-80"
          >
            {t('buffet.adv.useDynamic')}
          </button>
        </div>
      )}

      {!from || !to ? (
        <div className="mt-3 rounded-lg border border-dashed border-border px-4 py-8 text-center">
          <p className="text-xs text-muted-foreground">{t('buffet.adv.needDates')}</p>
        </div>
      ) : loading ? (
        <div className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-4 py-8 text-xs text-muted-foreground">
          <Loader2 size={14} className="animate-spin" />
          {t('buffet.adv.loading', { n: YEARS_BACK })}
        </div>
      ) : (
        <>
          {failed && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              {t('buffet.adv.failed')}
            </p>
          )}

          {/* What "more" is more THAN. Printed before either table, because
              every lift below is measured against it. */}
          <p className="mt-3 text-xs text-muted-foreground">
            {baseline
              ? t('buffet.adv.baseline', { n: Math.round(baseline), days: data?.baseline_days || 0 })
              : t('buffet.adv.baselineNone')}
            {Number(base) > 0
              ? ` ${t('buffet.adv.baseIs', { price: rupees(Number(base)) })}`
              : ` ${t('buffet.adv.noBase')}`}
          </p>

          {/* The two questions, as doors rather than as a scroll. Each says
              how much is behind it and how much has been answered, so the one
              worth opening is obvious without opening either. */}
          {(
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { key: 'hol', emoji: '🎉', n: holidays.length, priced: holPriced,
                  countLabel: t('buffet.adv.hol.inRun', { n: holidays.length }) },
                { key: 'we', emoji: '🗓️', n: weDays, priced: wePriced,
                  countLabel: t('buffet.adv.we.inRun', { n: weDays }) },
              ].map((card) => (
                <button
                  key={card.key}
                  type="button"
                  onClick={() => setOpen(card.key)}
                  className="rounded-xl border border-border bg-background p-4 text-left transition hover:border-primary hover:bg-muted/40"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span aria-hidden="true">{card.emoji}</span>
                    {t(`buffet.adv.${card.key === 'hol' ? 'hol' : 'we'}.title`)}
                    <ArrowRight size={14} className="ml-auto text-muted-foreground" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{card.countLabel}</p>
                  <p className="mt-2 text-xs font-medium text-foreground">
                    {card.priced
                      ? t('buffet.adv.pricedN', { n: card.priced })
                      : t('buffet.adv.nonePriced')}
                  </p>
                </button>
              ))}
            </div>
          )}

          <GlassModal
            open={open === 'hol'}
            title={t('buffet.adv.hol.title')}
            blurb={t('buffet.adv.hol.blurb', { n: YEARS_BACK })}
            closeLabel={t('buffet.adv.closeCard')}
            onClose={() => setOpen(null)}
          >
            <>
            {/* ---- holidays ------------------------------------------------ */}
            <h4 className="mt-5 text-sm font-semibold text-foreground">{t('buffet.adv.hol.title')}</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('buffet.adv.hol.blurb', { n: YEARS_BACK })}
            </p>

            {holidays.length === 0 ? (
              <p className="mt-3 rounded-lg border border-dashed border-border px-4 py-6 text-center text-xs text-muted-foreground">
                {t('buffet.adv.noHolidays')}
              </p>
            ) : (
              <>
                {/* Wide by nature — a column per year. It scrolls inside its own
                    frame rather than pushing the page sideways. */}
                <div className="mt-2 overflow-x-auto rounded-lg border border-border">
                  <table className="w-full min-w-[46rem] text-sm">
                    <FootfallHead
                      label={t('buffet.adv.colHoliday')}
                      years={years}
                      allKeys={holidays.map((h) => h.key)}
                      picked={pickedHol}
                      setPicked={setPickedHol}
                      t={t}
                    />
                    <tbody>
                      {holidays.map((h) => {
                        const u = holidayUplifts[h.key] || UPLIFT_ZERO;
                        return (
                          <tr key={h.key} className="border-t border-border/70 align-middle">
                            <td className="px-2 py-2">
                              <input
                                type="checkbox"
                                aria-label={h.name}
                                checked={pickedHol.has(h.key)}
                                onChange={() => setPickedHol((s) => toggleIn(s, h.key))}
                              />
                            </td>
                            <td className="px-2 py-2">
                              <div className="font-medium text-foreground">
                                {h.icon ? `${h.icon} ` : ''}{h.name}
                              </div>
                              <div className="text-[11px] text-muted-foreground">
                                {fmtShort(h.date)}
                                {' · '}
                                {h.is_public_holiday
                                  ? t('buffet.adv.publicHoliday')
                                  : t('buffet.adv.observance')}
                                {/* Said out loud, because the dates across this
                                    row will not line up and that is correct. */}
                                {h.moves ? ` · ${t('buffet.adv.moves')}` : ''}
                              </div>
                              {/* A holiday landing on a Saturday takes BOTH
                                  uplifts — the engine stacks modifiers — so the
                                  row says so rather than letting the price on
                                  the day come as a surprise. */}
                              {upliftSet(weekendUplifts[String(h.dow)]) && (
                                <div className="mt-0.5 text-[11px] text-amber-700">
                                  {t('buffet.adv.stacks', { day: t(`buffet.adv.dow.${h.dow}`) })}
                                </div>
                              )}
                            </td>
                            {h.history.map((r) => (
                              <td key={r.year} className="px-2 py-2">
                                {r.observed === false ? (
                                  <span className="text-xs text-muted-foreground/60">
                                    {t('buffet.adv.notObserved')}
                                  </span>
                                ) : (
                                  <Heads n={r.heads} sub={footSub(r)} t={t} />
                                )}
                              </td>
                            ))}
                            <td className="px-2 py-2">
                              <Heads n={h.avg_heads} sub={liftText(h.lift_pct)} t={t} />
                            </td>
                            <td className="px-2 py-2">
                              <UpliftCell
                                value={u} base={base} kids={kids} t={t}
                                onChange={setOne(setHolidayUplifts, h.key)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <BulkBar
                  n={pickedHol.size} mode={holMode} setMode={setHolMode}
                  value={holValue} setValue={setHolValue} t={t}
                  onApply={() => {
                    applyBulk([...pickedHol], holMode, holValue, setHolidayUplifts);
                    setPickedHol(new Set());
                  }}
                  onClear={() => { setHolidayUplifts({}); setPickedHol(new Set()); }}
                />
              </>
            )}

          </>
          </GlassModal>

          <GlassModal
            open={open === 'we'}
            title={t('buffet.adv.we.title')}
            blurb={t('buffet.adv.we.blurb', { n: YEARS_BACK })}
            closeLabel={t('buffet.adv.closeCard')}
            onClose={() => setOpen(null)}
          >
            <>
            {/* ---- weekends ------------------------------------------------ */}
            <h4 className="mt-6 text-sm font-semibold text-foreground">{t('buffet.adv.we.title')}</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t('buffet.adv.we.blurb', { n: YEARS_BACK })}
            </p>

            <div className="mt-2 overflow-x-auto rounded-lg border border-border">
              <table className="w-full min-w-[46rem] text-sm">
                <FootfallHead
                  label={t('buffet.adv.colWeekend')}
                  years={years}
                  allKeys={weekends.map((w) => String(w.dow))}
                  picked={pickedWe}
                  setPicked={setPickedWe}
                  t={t}
                />
                <tbody>
                  {weekends.map((w) => {
                    const key = String(w.dow);
                    const u = weekendUplifts[key] || UPLIFT_ZERO;
                    return (
                      <tr key={key} className="border-t border-border/70 align-middle">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            aria-label={w.label}
                            checked={pickedWe.has(key)}
                            onChange={() => setPickedWe((s) => toggleIn(s, key))}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="font-medium text-foreground">
                            {t(`buffet.adv.dow.${w.dow}`)}
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            {t('buffet.adv.inRange', { n: w.in_range })}
                          </div>
                        </td>
                        {w.history.map((r) => (
                          <td key={r.year} className="px-2 py-2">
                            <Heads
                              n={r.avg_heads}
                              sub={r.days_traded
                                ? t('buffet.adv.daysMeasured', { n: r.days_traded })
                                : null}
                              t={t}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-2">
                          <Heads n={w.avg_heads} sub={liftText(w.lift_pct)} t={t} />
                        </td>
                        <td className="px-2 py-2">
                          <UpliftCell
                            value={u} base={base} kids={kids} t={t}
                            onChange={setOne(setWeekendUplifts, key)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <BulkBar
              n={pickedWe.size} mode={weMode} setMode={setWeMode}
              value={weValue} setValue={setWeValue} t={t}
              onApply={() => {
                applyBulk([...pickedWe], weMode, weValue, setWeekendUplifts);
                setPickedWe(new Set());
              }}
              onClear={() => { setWeekendUplifts({}); setPickedWe(new Set()); }}
            />
          </>
          </GlassModal>

          {/* Where these end up, said plainly: they are pricing rules, not a
              note on this screen, and the till reads them on the day. */}
          {setCount > 0 && (
            <p className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <Info size={13} className="mt-0.5 shrink-0" />
              {t('buffet.adv.summary', { n: setCount })}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/* Step 2. Pricing is live; the other two areas are named and reserved, and an
   empty card that says what belongs in it beats a step that silently does
   nothing. */
/* A card that opens OVER the studio: centred, seven tenths of the viewport,
   the page dimmed and blurred behind it. The footfall tables are a column
   per year plus the uplift controls - too wide for the 35% column, and
   wide things that scroll sideways inside a narrow panel are how figures get
   misread. Rendered into <body> so no ancestor's overflow or transform can
   clip it; Escape, the backdrop and the button all close it; the page does
   not scroll underneath while it is up. */
function GlassModal({ open, title, blurb, closeLabel, onClose, children }) {
  const closeRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);
  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={[
          'flex max-h-[85vh] w-[70vw] min-w-[min(100%,52rem)] max-w-[70vw] flex-col overflow-hidden rounded-2xl',
          // The glass: a translucent card with a light edge that catches the
          // blurred page behind it, and a soft shadow so it reads as lifted.
          'border border-white/60 bg-background/80 shadow-[0_24px_80px_-20px_rgba(0,0,0,0.45)] backdrop-blur-2xl',
          'ring-1 ring-black/5 dark:border-white/15 dark:bg-background/75 dark:ring-white/5',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border/60 px-6 py-4">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">{title}</h3>
            {blurb ? <p className="mt-0.5 text-xs text-muted-foreground">{blurb}</p> : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border/60 bg-background/60 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
          >
            <X size={13} /> {closeLabel}
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 pt-2">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* The advance phase's three areas, walked the same way setup's four are:
   a stepper at the top, one area on screen. */
const ADV_STEPS = [
  { key: 'pricing', emoji: '💰' },
  { key: 'menu', emoji: '🍽️' },
  { key: 'capacity', emoji: '🪑' },
];

function AdvanceSettings({ pricing, index, onPick, t }) {
  const steps = ADV_STEPS.map((s) => ({ ...s, title: t(`buffet.adv.${s.key}.title`) }));
  const current = steps[index] || steps[0];
  return (
    <div className="space-y-5">
      {/* Nothing is "done" here: pricing is optional and the other two are not
          built, so every badge stays neutral rather than claiming progress
          through a phase the buffet works perfectly without. */}
      <Stepper steps={steps} index={index} done={() => false} onPick={onPick} />
      <p className="text-sm text-muted-foreground">{t('buffet.adv.intro')}</p>
      {current.key === 'pricing' ? (
        <AdvancePricing {...pricing} t={t} />
      ) : (
        <section className="rounded-xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 text-[15px] font-semibold text-foreground">
            <span aria-hidden="true">{current.emoji}</span>
            {current.title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{t(`buffet.adv.${current.key}.blurb`)}</p>
          <div className="mt-3 rounded-lg border border-dashed border-border px-4 py-8 text-center">
            <p className="text-xs text-muted-foreground">{t('buffet.adv.comingSoon')}</p>
          </div>
        </section>
      )}
    </div>
  );
}
